import React, { useEffect, useState } from "react";

import { endOfDay, format, parse, isValid } from "date-fns";

import pLimit from "p-limit";
import { API, graphqlOperation, Storage } from "aws-amplify";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import * as reportQueries from "../../graphql/report-queries";
import * as reloadYardQueries from "../../graphql/reloadyard/custom-queries";
import * as listIdQueries from "../../graphql/list-id-queries";
import * as adminQueries from "../../graphql/admin-queries";
import * as inboundMutations from "../../graphql/inbound/custom-mutations";
import { getInboundShipment } from "../../graphql/inbound/custom-queries";
import * as outboundMutations from "../../graphql/outbound/custom-mutations";
import { getOutboundShipment } from "../../graphql/outbound/custom-queries";
import * as inventoryMutations from "../../graphql/inventory/custom-mutations";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  useDataProvider,
  useMutation,
  useNotify,
  ArrayField,
  Datagrid,
  TextField,
} from "react-admin";

import { getEnv, logError, safeAPI } from "../../utils/appUtils";
import {
  APP_VERSION,
  AccountType,
  AclType,
  AreaType,
  CarrierType,
  DefaultCarlotLotNumber,
  DefaultFifoLotNumber,
  DefaultPaymentTerms,
  EventType,
  InboundState,
  InboundStatus,
  InventoryStatus,
  LocationType,
  LotType,
  MAX_INVENTORY_QUERY_LIMIT,
  OutboundStatus,
  ReloadYardStatus,
  ResourceType,
  SORTKEY_BUFFER,
  ServiceType,
  ShipmentLineItemStatus,
  SubscriptionStatus,
  Unit,
  UnitOptions,
  UserType,
  workdays,
} from "../../utils/constants";
import { CheckIcon, XIcon } from "../../components/common/iconography";
import {
  getLocationFullName,
  setInventoryAisleList,
} from "../../utils/locationUtils";

import * as utils from "./AdminUtils";
import { backfillCarrierDba } from "./adminCarrierDbaBackfill";
import {
  updateCustomerReloadYardAccountMini,
  updateReloadYardMini,
  updateUserMini,
} from "../../graphql/reloadyard/custom-mutations";
import {
  isWeightUOM,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../utils/loadTypeUtils";
import { normalizeValue, trimDecimal } from "../../utils/utils";
import { computeLowInventoryAlert } from "../../managerApp/resources/products/ProductUtils";
import { calculateSqf } from "../../utils/loadTypeUtils";
import {
  createAccessorialCharges,
  getDefaultTaxRate,
} from "../../managerApp/resources/services/ServiceUtils";
import { ChargeEvent } from "../../managerApp/resources/accounts/chargeRules/chargeRuleConstants";
import { updateGroupName as updateGroupNameJob } from "../updateGroupName";
import {
  backfillTrackCarAisleZoneArea,
  backfillLocationLength,
  cleanupScalabilityLoadTest,
  migrateUnspottedTrackCarsActiveState,
  runScalabilityLoadTest,
} from "./adminTrackCarUtils";
import { backfillOutboundPlacementDateTime } from "./adminPlacementBackfill";
import {
  auditOrphanInventoryItems,
  cleanupOrphanInventoryItems,
  OrphanAction,
} from "./adminOrphanInventoryItems";
import { backfillCarrierCompanies } from "./adminCarrierCompanyUtils";
import { getLatestShippedOutboundId } from "../../managerApp/resources/inventory/InventoryUitls";

const _ = require("lodash");

const getVersion = () => `Version: ${APP_VERSION}`;

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(2),
    overflow: "auto",
  },
}));

const getHelpPageButtonText = (env) => {
  switch (env) {
    case "prod":
      return "Pull Hotfix Help Pages";
    case "hotfix":
      return "Pull Dev Help Pages";
    default:
      return null;
  }
};

const getCommodityButtonText = (env) => {
  switch (env) {
    case "prod":
      return "Pull Hotfix Load Types";
    case "hotfix":
      return "Pull Dev Load Types";
    default:
      return null;
  }
};

const helpPageButtonText = getHelpPageButtonText(getEnv());
const commodityButtonText = getCommodityButtonText(getEnv());

const MismatchIcon = ({ record, ...props }) =>
  record?.quantityOnHand === record?.totalQuantityOnHand &&
  record?.quantityCommitted === record?.totalQuantityCommitted ? (
    <CheckIcon />
  ) : (
    <XIcon />
  );

export const AdminDashboard = ({ permissions }) => {
  const classes = useStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [reloadYards, setReloadYards] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [handler, setHandler] = useState();
  const [handlerName, setHandlerName] = useState();
  const [queryTerms, setQueryTerms] = useState({});
  const [results, setResults] = useState();
  const [loading, setLoading] = useState(false);
  // Last orphan InventoryItem audit. The cleanup only deletes rows it found,
  // and clears it, so every cleanup follows a fresh audit.
  const [orphanAudit, setOrphanAudit] = useState(null);
  const [orphanAuditRunning, setOrphanAuditRunning] = useState(false);

  /**
   * Backfill ProductStats.stats for the past 3 monthly buckets.
   *
   * Why this exists:
   * - The stream-based velocity aggregation only accumulates going forward.
   * - This backfill is an ADMIN-only, one-time (or occasional) job to seed the last 3 months.
   *
   * IMPORTANT LIMITATIONS / ASSUMPTIONS:
   * - We do not have historical daily inventory snapshots in DynamoDB, so avgInventory is
   *   approximated using inventory lifecycles:
   *   - If the inventory is fully shipped: assume linear drawdown from receivedQty → 0,
   *     so average on-hand for that lot ≈ receivedQty / 2 across its lifespan.
   *   - If the inventory is not fully shipped: assume on-hand is roughly constant at the
   *     current quantityOnHand across the overlap window.
   * - Shipped quantities and outbound day counts are derived from shipped OutboundShipment
   *   records in the time window and their ShipmentLineItem records.
   * - Only the 3 target month buckets are overwritten; other months in ProductStats.stats
   *   are untouched.
   *
   * New ProductStats model behavior:
   * - Product.stats is intentionally NOT read or updated.
   * - ProductStats is not @searchable, so stats will not be indexed by OpenSearch.
   * - ProductStats.id === Product.id === ProductStats.productId.
   */
  const backfillProductStats = async () => {
    const pad2 = (n) => String(n).padStart(2, "0");

    const toNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const monthKeyFromDate = (date) => {
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
    };

    const dayKeyFromDate = (date) => {
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(
        d.getUTCDate(),
      )}`;
    };

    const parseStats = (stats) => {
      if (!stats) return {};

      // ProductStats.stats may come back as either an object or an AWSJSON string,
      // depending on resolver/client behavior. Support both.
      if (typeof stats === "object" && !Array.isArray(stats)) return stats;
      if (typeof stats !== "string") return {};

      try {
        const parsed = JSON.parse(stats);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
          ? parsed
          : {};
      } catch {
        return {};
      }
    };

    const serializeStatsInput = (stats) => {
      /**
       * ProductStats.stats is AWSJSON.
       *
       * Most Amplify/AppSync generated clients expect AWSJSON input as a JSON string.
       * Keeping JSON.stringify here is usually safest for GraphQL mutation input.
       *
       * Important:
       * - This is no longer being written to Product.
       * - ProductStats is not @searchable, so this value will not be indexed by OpenSearch.
       */
      return JSON.stringify(stats || {});
    };

    const getProductStatsRecord = async (productId) => {
      if (!productId) return null;

      const resp = await API.graphql(
        graphqlOperation(queries.getProductStats, {
          id: productId,
        }),
      );

      return resp?.data?.getProductStats || null;
    };

    const upsertProductStats = async ({
      product,
      stats,
      existingStatsRecord = null,
    }) => {
      const productId = product?.id;
      if (!productId) return null;

      const statsInput = serializeStatsInput(stats);

      if (existingStatsRecord?.id) {
        return API.graphql(
          graphqlOperation(mutations.updateProductStats, {
            input: {
              id: productId,
              stats: statsInput,
            },
          }),
        );
      }

      return API.graphql(
        graphqlOperation(mutations.createProductStats, {
          input: {
            // Product.id === ProductStats.id === ProductStats.productId
            id: productId,
            productId,

            // Denormalized fields for account-level access patterns.
            accountId: product.accountId || null,
            reloadYardId: product.reloadYardId || null,

            // Preserve auth metadata where available.
            owner: product.owner || undefined,
            groups: product.groups || [],

            stats: statsInput,
          },
        }),
      );
    };

    const emptyBucket = () => ({
      receivedQty: 0,
      shippedQty: 0,
      avgInventory: 0, // derived
      inventoryDays: 0,
      daysTracked: 0,
      totalTurnDays: 0,
      completedLots: 0,
      outboundDays: 0,

      // Internal dedupe structure.
      // Kept in stats so the stream aggregator and backfill can calculate unique outbound days.
      outboundDaySet: {},
    });

    const finalizeBucket = (b) => {
      b.receivedQty = toNumber(b.receivedQty);
      b.shippedQty = toNumber(b.shippedQty);
      b.inventoryDays = toNumber(b.inventoryDays);
      b.daysTracked = Math.trunc(toNumber(b.daysTracked));
      b.totalTurnDays = Math.trunc(toNumber(b.totalTurnDays));
      b.completedLots = Math.trunc(toNumber(b.completedLots));

      // CRITICAL: outboundDays must be recalculated from outboundDaySet for consistency.
      if (b.outboundDaySet && typeof b.outboundDaySet === "object") {
        b.outboundDays = Object.keys(b.outboundDaySet).length;
      } else {
        b.outboundDays = Math.trunc(toNumber(b.outboundDays));
        b.outboundDaySet = {};
      }

      b.avgInventory = b.daysTracked > 0 ? b.inventoryDays / b.daysTracked : 0;
    };

    const isFullyShippedInventory = (inv) => {
      if (!inv) return false;
      if (String(inv.status || "") === "SHIPPED") return true;
      return toNumber(inv.quantityOnHand) === 0;
    };

    const receivedQtyForInv = (inv) => {
      if (!inv) return 0;
      if (inv.quantityReceived != null) return toNumber(inv.quantityReceived);
      if (inv.quantityAdvised != null) return toNumber(inv.quantityAdvised);
      return 0;
    };

    const pickReceivedDate = (inv) => inv?.receivedAt || inv?.createdAt || null;

    const monthStartUtc = (monthKey) => {
      const [y, m] = monthKey.split("-").map((x) => Number(x));
      return new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0));
    };

    const monthEndUtc = (monthKey) => {
      const [y, m] = monthKey.split("-").map((x) => Number(x));

      // day 0 of next month gives last day of month
      return new Date(Date.UTC(y, m, 0, 23, 59, 59, 999));
    };

    const daysOverlapInclusiveUtc = (start, end, monthKey) => {
      const ms = monthStartUtc(monthKey);
      const me = monthEndUtc(monthKey);
      const s = start > ms ? start : ms;
      const e = end < me ? end : me;

      if (s > e) return 0;

      const dayMs = 24 * 60 * 60 * 1000;

      // inclusive count: floor diff + 1
      return Math.floor((e.getTime() - s.getTime()) / dayMs) + 1;
    };

    const lastNMonthKeys = (n) => {
      const now = new Date();
      const keys = [];

      for (let i = n - 1; i >= 0; i -= 1) {
        const d = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1),
        );

        keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`);
      }

      return keys;
    };

    const monthKeys = lastNMonthKeys(3);
    const startMonth = monthKeys[0];
    const endMonth = monthKeys[monthKeys.length - 1];
    const windowStart = monthStartUtc(startMonth);
    const windowEnd = monthEndUtc(endMonth);

    // Keep console output minimal: per-account start/end only.
    setLoading(true);

    notify(
      `Backfill started (months: ${startMonth}..${endMonth}). This can take a while.`,
      "info",
    );

    try {
      // 1) Load ALL accounts (pagination).
      const accounts = [];
      let nextToken = null;

      do {
        const resp = await API.graphql(
          graphqlOperation(queries.listAccounts, {
            limit: 1000,
            nextToken,
          }),
        );

        const page = resp?.data?.listAccounts;

        if (page?.items?.length) {
          accounts.push(...page.items);
        }

        nextToken = page?.nextToken || null;
      } while (nextToken);

      // 2) Process accounts with controlled concurrency.
      const accountLimit = pLimit(2);
      const results = [];

      const processAccount = async (acct) => {
        const tAcct0 = Date.now();
        const accountId = acct?.id;

        if (!accountId) return null;

        const accountName = acct.displayName || acct.businessName || accountId;

        console.log(
          `[ProductStatsBackfill] START account=${accountName} (${accountId})`,
        );

        // 2a) Load products for this account (pagination).
        //
        // Important:
        // - Product.stats is intentionally not required.
        // - ProductStats is populated separately by Product.id.
        // - Make sure the productByAccountByActiveByName query includes:
        //   id, owner, groups, accountId, reloadYardId.
        const products = [];
        let productNext = null;

        do {
          const resp = await API.graphql(
            graphqlOperation(queries.productByAccountByActiveByName, {
              accountId,
              activeName: {
                beginsWith: {
                  active: "true",
                },
              },
              limit: 1000,
              nextToken: productNext,
            }),
          );

          const page = resp?.data?.productByAccountByActiveByName;

          if (page?.items?.length) {
            products.push(...page.items);
          }

          productNext = page?.nextToken || null;
        } while (productNext);

        if (products.length === 0) {
          return {
            id: accountId,
            accountName,
            products: 0,
            updated: 0,
            note: "No products",
          };
        }

        const reloadYardId = products[0]?.reloadYardId || acct?.reloadYardId;

        // ---- NEW MODEL ----
        // 1) Bulk inventories for this account and aggregate by productId.
        // 2) Bulk shipped outbounds for this account and aggregate shippedQty/outboundDays by productId.
        // 3) Write only the last 3 month buckets onto ProductStats.
        //
        // Use a slightly larger fetch window for source events, but only compute buckets for monthKeys.
        const sixMonthStart = monthStartUtc(
          lastNMonthKeys(6)[0], // oldest of last 6 months
        );

        // Safety: validate monthKeys before proceeding.
        if (monthKeys.length === 0) {
          console.warn(
            `backfillProductStats: Invalid month range for account ${accountId}`,
          );

          return {
            id: accountId,
            accountName,
            products: products.length,
            updated: 0,
            note: "Invalid month range",
            inventoryRows: 0,
            shippedRows: 0,
          };
        }

        const bucketsByProductId = {};

        products.forEach((p) => {
          bucketsByProductId[p.id] = {};

          monthKeys.forEach((mk) => {
            bucketsByProductId[p.id][mk] = emptyBucket();
          });
        });

        const productIdSet = new Set(products.map((p) => p.id));

        let inventoryRows = 0;
        let inventoryPages = 0;

        // Bulk inventories for this account via OpenSearch (@searchable).
        // We pull last 6 months of activity by delivery/received/ship dates,
        // but only compute the last 3 month buckets.
        const inventorySearchInput = {
          sort: {
            field: "receivedAt",
            direction: "asc",
          },
          filter: {
            reloadYardId: {
              eq: reloadYardId,
            },
            accountId: {
              eq: accountId,
            },
            and: [
              {
                or: [
                  {
                    status: {
                      eq: InventoryStatus.STORED,
                    },
                  },
                  {
                    status: {
                      eq: InventoryStatus.SHIPPED,
                    },
                  },
                  {
                    status: {
                      eq: InventoryStatus.ARCHIVED,
                    },
                  },
                ],
              },
              {
                or: [
                  {
                    receivedAt: {
                      gte: sixMonthStart.toISOString(),
                    },
                  },
                  {
                    depletedAt: {
                      gte: sixMonthStart.toISOString(),
                    },
                  },
                ],
              },
            ],
          },
          limit: 1000,
        };

        let invNext = true;

        while (invNext) {
          const input =
            invNext === true
              ? inventorySearchInput
              : {
                  ...inventorySearchInput,
                  nextToken: invNext,
                };

          const resp = await API.graphql(
            graphqlOperation(adminQueries.searchInventorysBackfill, input),
          );

          const page = resp?.data?.searchInventorys;
          const items = page?.items || [];

          inventoryPages += 1;
          inventoryRows += items.length;

          for (const inv of items) {
            const productId = inv?.productId;

            if (!productId || !productIdSet.has(productId)) continue;

            const buckets = bucketsByProductId[productId];

            if (!buckets) continue;

            const receivedDateRaw = pickReceivedDate(inv);
            const receivedDate = receivedDateRaw
              ? new Date(receivedDateRaw)
              : null;
            const receivedMonthKey = receivedDate
              ? monthKeyFromDate(receivedDate)
              : null;
            const receivedQty = receivedQtyForInv(inv);

            if (receivedMonthKey && buckets[receivedMonthKey]) {
              buckets[receivedMonthKey].receivedQty += receivedQty;
            }

            const fullyShipped = isFullyShippedInventory(inv);
            const shipDateRaw = inv?.depletedAt;
            const shipDate = shipDateRaw ? new Date(shipDateRaw) : null;
            const shipMonthKey = shipDate ? monthKeyFromDate(shipDate) : null;

            if (fullyShipped && shipMonthKey && buckets[shipMonthKey]) {
              buckets[shipMonthKey].totalTurnDays += Math.trunc(
                toNumber(inv?.turnDays),
              );

              buckets[shipMonthKey].completedLots += 1;
            }

            // Avg inventory approximation:
            // - overlap of [receivedDate..shipDate] with the last-3-month window.
            // - fully shipped inventory contributes receivedQty / 2.
            // - active inventory contributes current quantityOnHand.
            const lifecycleStart =
              receivedDate && isValid(receivedDate) ? receivedDate : null;

            const lifecycleEnd =
              fullyShipped && shipDate && isValid(shipDate) ? shipDate : null;

            const start =
              lifecycleStart && lifecycleStart > windowStart
                ? lifecycleStart
                : windowStart;

            const end =
              lifecycleEnd && lifecycleEnd < windowEnd
                ? lifecycleEnd
                : windowEnd;

            if (!start || !end || start > end) continue;

            const dailyOnHand = fullyShipped
              ? receivedQty / 2
              : toNumber(inv?.quantityOnHand);

            monthKeys.forEach((mk) => {
              const overlapDays = daysOverlapInclusiveUtc(start, end, mk);

              if (overlapDays <= 0) return;

              buckets[mk].inventoryDays += dailyOnHand * overlapDays;
              buckets[mk].daysTracked += overlapDays;
            });
          }

          if (items.length < inventorySearchInput.limit) break;

          invNext = page?.nextToken;

          // Safety guard.
          if (inventoryRows > 300000) break;
        }

        // 2) Bulk shipped outbounds for this account via OpenSearch (@searchable)
        // within the 3-month window.
        //
        // This avoids DynamoDB GSI composite-key edge cases and avoids per-shipment /
        // per-inventory fanout.
        const outboundSearchInput = {
          sort: {
            field: "departureDateTime",
            direction: "asc",
          },
          filter: {
            accountId: {
              eq: accountId,
            },
            status: {
              eq: OutboundStatus.SHIPPED,
            },
            and: [
              {
                departureDateTime: {
                  gte: `${windowStart.toISOString()}`,
                },
              },
              {
                departureDateTime: {
                  lte: `${windowEnd.toISOString()}`,
                },
              },
            ],
          },
          limit: 1000,
        };

        const outbounds = [];
        let outboundNext = true;
        let outboundPages = 0;

        while (outboundNext) {
          const input =
            outboundNext === true
              ? outboundSearchInput
              : {
                  ...outboundSearchInput,
                  nextToken: outboundNext,
                };

          const resp = await API.graphql(
            graphqlOperation(reportQueries.searchOutboundTotal, input),
          );

          const page = resp?.data?.searchOutboundShipments;
          const items = page?.items || [];

          outboundPages += 1;
          outbounds.push(...items);

          if (items.length < outboundSearchInput.limit) break;

          outboundNext = page?.nextToken;
        }

        let shippedRows = 0;

        outbounds.forEach((outbound) => {
          const dtRaw =
            outbound?.departureDateTime ||
            outbound?.endDateTime ||
            outbound?.arrivalDateTime ||
            null;

          const mk = monthKeyFromDate(dtRaw);
          const dk = dayKeyFromDate(dtRaw);

          if (!mk || !dk || !monthKeys.includes(mk)) return;

          const items = outbound?.shipmentLineItems?.items || [];

          items.forEach((li) => {
            if (li?.status !== ShipmentLineItemStatus.SHIPPED) return;

            const qty = toNumber(li?.totalQuantityToPick);
            const productId = li?.inventory?.productId;

            if (!qty || !productId || !bucketsByProductId[productId]) return;

            const b = bucketsByProductId[productId]?.[mk];

            if (!b) {
              // Defensive: bucket should exist, but skip if missing.
              console.warn(
                `backfillProductStats: Missing bucket for product ${productId} month ${mk}`,
              );
              return;
            }

            b.shippedQty = toNumber(b.shippedQty) + qty;

            if (!b.outboundDaySet) {
              b.outboundDaySet = {};
            }

            b.outboundDaySet[dk] = true;
            shippedRows += 1;
          });
        });

        // 3) Persist backfilled buckets onto ProductStats.
        //
        // Important:
        // - Product.stats is intentionally NOT read.
        // - Product.stats is intentionally NOT updated.
        // - Only the target 3 months are overwritten in ProductStats.stats.
        // - Other existing months in ProductStats.stats are preserved.
        const productLimit = pLimit(15);
        let updatedCount = 0;

        await Promise.all(
          products.map((p) =>
            productLimit(async () => {
              const buckets = bucketsByProductId[p.id];

              if (!buckets) return;

              monthKeys.forEach((mk) => {
                buckets[mk].outboundDays = Object.keys(
                  buckets[mk].outboundDaySet || {},
                ).length;

                finalizeBucket(buckets[mk]);
              });

              const existingStatsRecord = await getProductStatsRecord(p.id);
              const existing = parseStats(existingStatsRecord?.stats);

              const merged = { ...existing };

              monthKeys.forEach((mk) => {
                merged[mk] = buckets[mk];
              });

              await upsertProductStats({
                product: p,
                stats: merged,
                existingStatsRecord,
              });

              updatedCount += 1;
            }),
          ),
        );

        console.log(
          `[ProductStatsBackfill] END account=${accountName} (${accountId}) ms=${
            Date.now() - tAcct0
          } products=${
            products.length
          } inventories=${inventoryRows} invPages=${inventoryPages} outbounds=${
            outbounds.length
          } outboundPages=${outboundPages} shipLineItems=${shippedRows} updatedProductStats=${updatedCount}`,
        );

        return {
          id: accountId,
          accountName,
          products: products.length,
          updated: updatedCount,
          reloadYardId: reloadYardId || "unknown",
          shippedRows,
          inventoryRows,
          msTotal: Date.now() - tAcct0,
        };
      };

      const processed = await Promise.all(
        accounts.map((acct) => accountLimit(() => processAccount(acct))),
      );

      processed.filter(Boolean).forEach((r) => {
        results.push(r);
      });

      setResults({
        id: new Date(),
        items: results,
      });

      notify("Backfill complete.", "info");
    } catch (error) {
      logError("AdminDashboard:backfillProductStats", error, notify);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    utils.getYards(setReloadYards, notify);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let appUser = window.appUser;

  // ################################################################
  // ################################################################
  // ################################################################
  const handleDialogOpen = (handler, handlerName) => {
    debugger;
    setHandler(() => handler);
    setHandlerName(handlerName);
    setDialogOpen(true);
  };

  const handleDialogClose = (_, reason) => {
    if (reason !== "backdropClick") {
      setDialogOpen(false);
      setQueryTerms({});
      setResults(null);
    }
  };

  const addDefaultScheduleToYard = async () => {
    const defaultSchedule = {
      workdays,
      holidays: [],
      minNoticeHours: 24,
      maxDaysIntoFuture: 60,
    };

    try {
      const promiseList = [];
      const toUpdateYards = reloadYards.filter((itm) => !itm.schedule);
      toUpdateYards.forEach((itm) => {
        promiseList.push(
          API.graphql(
            graphqlOperation(updateReloadYardMini, {
              input: {
                id: itm.id,
                active: itm.active,
                name: itm.name,
                schedule: defaultSchedule,
              },
            }),
          ),
        );
      });
      const result = Promise.all(promiseList);
      console.log(result);
      console.log("done");
      debugger;
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async (queryTerms) => {
    setLoading(true);
    await handler(queryTerms, notify, dataProvider);
    setDialogOpen(false);
    setQueryTerms({});
    setResults(null);
    setLoading(false);
  };

  const updateGroupNameHandler = async (queryTerms, notify) => {
    return await updateGroupNameJob(
      queryTerms.reloadYardId,
      queryTerms.newGrpupName,
      notify,
    );
  };

  // ################################################################
  // ################################################################
  // ################################################################
  // ################################################################
  // ################################################################
  // ################################################################

  const auditProducts = async (queryTerms) => {
    console.clear();
    try {
      debugger;
      let data = [];
      let nextToken = true;
      while (nextToken) {
        const result = await API.graphql(
          graphqlOperation(queries.inventoryByStatusByLot, {
            reloadYardId: queryTerms.reloadYardId,
            statusLotNumber: {
              beginsWith: { status: InventoryStatus.STORED },
            },
            limit: 1000,
            ...(nextToken === true ? {} : { nextToken }),
          }),
        );
        const page = result.data.inventoryByStatusByLot;
        data = data.concat(page?.items || []);
        nextToken = page?.nextToken || null;
      }
      console.log("reloadYardId:", queryTerms.reloadYardId);
      console.log("inventories:", data);
      debugger;

      data = data.filter((itm) => itm.productCode);

      let reportData = _(data)
        .groupBy("productId")
        .map((objs, key) => ({
          id: objs[0].id,
          accountName: objs[0].accountName,
          productId: key,
          productCode: objs[0].productCode,
          productName: objs[0].productName,
          quantityOnHand: objs.reduce(function (sum, record) {
            return sum + (Number(record.quantityOnHand) || 0);
          }, 0),
          quantityCommitted: objs.reduce(function (sum, record) {
            return sum + (Number(record.quantityCommitted) || 0);
          }, 0),
          quantityAvailable: objs.reduce(function (sum, record) {
            return (
              sum +
              ((Number(record.quantityOnHand) || 0) -
                (Number(record.quantityCommitted) || 0))
            );
          }, 0),
          totalQuantityOnHand: objs[0].product?.totalQuantityOnHand,
          totalQuantityCommitted: objs[0].product?.totalQuantityCommitted,
        }))
        .value();

      if (
        reportData.some(
          (itm) =>
            trimDecimal(itm.quantityOnHand, 3) !==
              trimDecimal(Number(itm.totalQuantityOnHand) || 0, 3) ||
            trimDecimal(itm.quantityCommitted, 3) !==
              trimDecimal(Number(itm.totalQuantityCommitted) || 0, 3),
        )
      )
        console.log("Qty. Mismatch !!!");
      debugger;

      reportData = {
        id: new Date(),
        items: [...reportData],
      };

      setResults(reportData);
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:auditProducts", error, notify);
    }
  };

  const auditInventoryQuantityDiscrepancies = async (queryTerms) => {
    console.clear();
    const reloadYardId = queryTerms?.reloadYardId;
    if (!reloadYardId) {
      notify("reloadYardId is required", "warning");
      return;
    }

    const toNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    let nextToken;
    let scanned = 0;
    const discrepancies = [];
    const maxDiscrepancies = 10;
    let foundDiscrepancies = 0;

    try {
      const searchInput = {
        filter: { reloadYardId: { eq: reloadYardId } },
        limit: 200,
      };

      do {
        const input = nextToken ? { nextToken, ...searchInput } : searchInput;
        const result = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );
        const inventoryList = result.data.searchInventorys.items || [];
        nextToken = result.data.searchInventorys.nextToken;
        scanned += inventoryList.length;
        console.log("scanned", scanned);

        for (const inventory of inventoryList) {
          const inventoryItems = inventory?.inventoryItems?.items || [];
          const shipmentLineItems = inventory?.shipmentLineItems?.items || [];

          const sumInventoryItemsOnHand = inventoryItems.reduce(
            (sum, ii) => sum + toNumber(ii?.quantityOnHand),
            0,
          );
          const sumInventoryItemsCommitted = inventoryItems.reduce(
            (sum, ii) => sum + toNumber(ii?.quantityCommitted),
            0,
          );
          const sumLineItemsCommitted = shipmentLineItems
            .filter((li) => li?.status !== ShipmentLineItemStatus.SHIPPED)
            .reduce((sum, li) => sum + toNumber(li?.totalQuantityToPick), 0);
          const sumLineItemsShipped = shipmentLineItems
            .filter((li) => li?.status === ShipmentLineItemStatus.SHIPPED)
            .reduce((sum, li) => sum + toNumber(li?.totalQuantityToPick), 0);

          const invQuantityOnHand = toNumber(inventory?.quantityOnHand);
          const invQuantityCommitted = toNumber(inventory?.quantityCommitted);

          const expectedOnHandFromDelivered =
            toNumber(inventory?.quantityReceived) - sumLineItemsShipped;
          const mismatchOnHand = invQuantityOnHand !== sumInventoryItemsOnHand;
          const mismatchOnHandFromDelivered =
            invQuantityOnHand !== expectedOnHandFromDelivered;
          const mismatchCommittedFromItems =
            invQuantityCommitted !== sumInventoryItemsCommitted;
          const mismatchCommittedFromLineItems =
            invQuantityCommitted !== sumLineItemsCommitted;

          if (
            mismatchOnHand ||
            mismatchOnHandFromDelivered ||
            mismatchCommittedFromItems ||
            mismatchCommittedFromLineItems
          ) {
            const discrepancy = {
              id: inventory?.id,
              status: inventory?.status,
              invQuantityOnHand,
              sumInventoryItemsOnHand,
              invQuantityCommitted,
              sumInventoryItemsCommitted,
              sumLineItemsCommitted,
              sumLineItemsShipped,
              correctQuantityOnHand: sumInventoryItemsOnHand,
              correctQuantityOnHandFromDelivered: expectedOnHandFromDelivered,
              correctQuantityCommittedFromItems: sumInventoryItemsCommitted,
              correctQuantityCommittedFromLineItems: sumLineItemsCommitted,
              inventoryItemCount: inventoryItems.length,
              shipmentLineItemCount: shipmentLineItems.length,
              mismatchOnHand,
              mismatchOnHandFromDelivered,
              mismatchCommittedFromItems,
              mismatchCommittedFromLineItems,
            };
            discrepancies.push(discrepancy);
            foundDiscrepancies += 1;
            console.log("inventory", inventory);
            console.log("discrepancy", discrepancy);
            if (foundDiscrepancies >= maxDiscrepancies) {
              break;
            }
          }
        }
      } while (nextToken && foundDiscrepancies < maxDiscrepancies);

      setResults({
        id: new Date(),
        items: discrepancies,
      });
      console.log("[auditInventoryQuantityDiscrepancies] done", {
        reloadYardId,
        scanned,
        discrepancies: discrepancies.length,
      });
      notify("All done");
    } catch (error) {
      logError(
        "AdminDashboard:auditInventoryQuantityDiscrepancies",
        error,
        notify,
      );
    }
  };

  // backfill inventoryItem.depletedAt
  const fixShippedInventoryDepletedAt = async (queryTerms) => {
    console.clear();
    const dryRun = false;

    const toDateSafe = (value) => {
      if (!value) return null;
      const d = value instanceof Date ? value : new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const getLatestDateValue = (dateValues) => {
      let latest = null;
      dateValues.forEach((value) => {
        const d = toDateSafe(value);
        if (!d) return;
        if (!latest || d > latest) latest = d;
      });
      return latest ? latest.toISOString() : null;
    };

    const getLatestHistoryDateValue = (history) => {
      const historyDates = (history || [])
        .map((item) => item?.date)
        .filter(Boolean);
      return getLatestDateValue(historyDates);
    };

    const isZeroedOutQuantity = (value) => {
      if (value === null || value === undefined || value === "") return false;

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return false;

      return numericValue <= 0;
    };

    const getInventoryItemDepartureDateMap = (inventory) => {
      const inventoryItemDepartureDateMap = {};
      const lineItems = inventory?.shipmentLineItems?.items || [];

      lineItems.forEach((lineItem) => {
        const departureDateTime = lineItem?.outboundShipment?.departureDateTime;
        if (!departureDateTime) return;

        (lineItem?.inventoryItems?.items || []).forEach(
          (shipmentLineItemInventoryItem) => {
            const inventoryItemId =
              shipmentLineItemInventoryItem?.inventoryItemId;
            if (!inventoryItemId) return;

            if (!inventoryItemDepartureDateMap[inventoryItemId]) {
              inventoryItemDepartureDateMap[inventoryItemId] = [];
            }

            inventoryItemDepartureDateMap[inventoryItemId].push(
              departureDateTime,
            );
          },
        );
      });

      return inventoryItemDepartureDateMap;
    };

    let nextToken;
    let inventoryScanned = 0;
    let inventoryItemScanned = 0;
    let inventoryUpdated = 0;
    let inventoryItemUpdated = 0;
    const updates = [];

    try {
      const inventorySearchInput = {
        limit: 200,
      };

      do {
        const input = nextToken
          ? { nextToken, ...inventorySearchInput }
          : inventorySearchInput;
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInventorysForBackfill, input),
        );
        const inventoryList = result.data.listInventories.items || [];
        nextToken = result.data.listInventories.nextToken;
        inventoryScanned += inventoryList.length;

        if (!dryRun) {
          console.log("[fixShippedInventoryDepletedAt] progress", {
            inventoryScanned,
          });
        }

        for (const inventory of inventoryList) {
          const hasDepletedAt = !!inventory?.depletedAt;
          const shouldUpdateInventory =
            isZeroedOutQuantity(inventory?.quantityOnHand) ||
            inventory.status === InventoryStatus.SHIPPED;
          if (dryRun) {
            console.log("inventory", inventory);
            console.log("hasDepletedAt", hasDepletedAt);
            console.log("shouldUpdateInventory", shouldUpdateInventory);
          }

          const lineItems = inventory?.shipmentLineItems?.items || [];
          const inventoryItemDepartureDateMap =
            getInventoryItemDepartureDateMap(inventory);
          const lineItemDepartureDates = lineItems
            .map((li) => li?.outboundShipment?.departureDateTime)
            .filter(Boolean);
          const latestLineItemDate = getLatestDateValue(lineItemDepartureDates);
          if (dryRun) console.log("latestLineItemDate", latestLineItemDate);

          let newDepletedAt = latestLineItemDate;
          let source = "shipmentLineItems";

          if (!newDepletedAt) {
            newDepletedAt = getLatestHistoryDateValue(inventory?.history);
            if (dryRun) {
              console.log("historyDepletedAt", newDepletedAt);
            }
            source = "history";
          }

          if (shouldUpdateInventory && !hasDepletedAt && newDepletedAt) {
            const updateRecord = {
              recordType: "inventory",
              id: inventory?.id,
              reloadYardId: inventory?.reloadYardId,
              status: inventory?.status,
              accountName: inventory?.accountName,
              productName: inventory?.productName,
              depletedAt: inventory?.depletedAt || null,
              newDepletedAt: hasDepletedAt ? null : newDepletedAt,
              source: hasDepletedAt
                ? "existing"
                : newDepletedAt
                  ? source
                  : "none",
            };
            updates.push(updateRecord);
          }

          if (
            shouldUpdateInventory &&
            !hasDepletedAt &&
            newDepletedAt &&
            !dryRun
          ) {
            await utils.saveInventory({
              id: inventory.id,
              depletedAt: newDepletedAt,
            });
            inventoryUpdated += 1;
          }

          const inventoryItems = inventory?.inventoryItems?.items || [];
          inventoryItemScanned += inventoryItems.length;

          for (const inventoryItem of inventoryItems) {
            const hasDepletedAt = !!inventoryItem?.depletedAt;
            const shouldUpdateInventoryItem =
              isZeroedOutQuantity(inventoryItem?.quantityOnHand) ||
              inventoryItem.status === InventoryStatus.SHIPPED;

            if (hasDepletedAt) continue;
            if (dryRun) {
              console.log("inventoryItem", inventoryItem);
              console.log("hasDepletedAt", hasDepletedAt);
              console.log(
                "shouldUpdateInventoryItem",
                shouldUpdateInventoryItem,
              );
            }

            const latestShipmentLineItemDate = getLatestDateValue(
              inventoryItemDepartureDateMap[inventoryItem.id] || [],
            );
            if (dryRun) {
              console.log(
                "latestShipmentLineItemDate",
                latestShipmentLineItemDate,
              );
            }

            let newDepletedAt = latestShipmentLineItemDate;
            let source = "shipmentLineItemInventoryItems";

            if (!newDepletedAt) {
              newDepletedAt = getLatestHistoryDateValue(inventoryItem?.history);
              if (dryRun) {
                console.log("inventoryItemHistoryDepletedAt", newDepletedAt);
              }
              source = "history";
            }

            if (shouldUpdateInventoryItem && !hasDepletedAt && newDepletedAt) {
              updates.push({
                recordType: "inventoryItem",
                id: inventoryItem?.id,
                inventoryId: inventoryItem?.inventoryId,
                reloadYardId: inventory?.reloadYardId,
                status: inventoryItem?.status,
                quantityOnHand: inventoryItem?.quantityOnHand,
                depletedAt: inventoryItem?.depletedAt || null,
                newDepletedAt,
                source,
              });
            }

            if (
              shouldUpdateInventoryItem &&
              !hasDepletedAt &&
              newDepletedAt &&
              !dryRun
            ) {
              await utils.saveInventoryItem({
                id: inventoryItem.id,
                depletedAt: newDepletedAt,
              });
              inventoryItemUpdated += 1;
            }
          }
        }
      } while (nextToken);

      setResults({ id: new Date(), items: updates });
      console.log("[fixShippedInventoryDepletedAt] done", {
        inventoryScanned,
        inventoryItemScanned,
        updated: inventoryUpdated + inventoryItemUpdated,
        inventoryUpdated,
        inventoryItemUpdated,
        dryRun,
        updates: updates.length,
      });
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:fixShippedInventoryDepletedAt", error, notify);
    }
  };

  // ################################################################
  // ################################################################
  // ################################################################
  // ################################################################
  // ################################################################
  // ################################################################

  const runSortKeyBackfill = async (queryTerms) => {
    console.clear();
    console.log("reload yard:", queryTerms.reloadYardId);

    try {
      let result = await API.graphql(
        graphqlOperation(queries.listInboundShipments, { limit: 1000 }),
      );
      let data = result.data.listInboundShipments.items.filter(
        (itm) => itm.reloadYardId === queryTerms.reloadYardId,
      );
      console.log("Inbound data:", data);
      debugger;

      for (const inbound of data) {
        let index = 10001;

        const resp = await API.graphql(
          graphqlOperation(getInboundShipment, { id: inbound.id }),
        );
        const inventories = resp.data.getInboundShipment.inventories.items;
        console.log("inventories data:", inventories?.length);

        for (const inventory of inventories) {
          const recordToUpdate = {
            ...inventory,
            sortKey: inventory.sortKey ? inventory.sortKey : index,
          };
          index = index + 1;

          debugger;
          delete recordToUpdate.account;
          delete recordToUpdate.area;
          delete recordToUpdate.zone;
          delete recordToUpdate.location;
          delete recordToUpdate.product;
          delete recordToUpdate.reloadYardCommodity;
          delete recordToUpdate.shipmentLineItems;
          delete recordToUpdate.createdAt;
          delete recordToUpdate.updatedAt;

          console.log(
            recordToUpdate.lotNumber,
            recordToUpdate.productName,
            recordToUpdate.sortKey,
          );
          await dataProvider.update("inventories", { data: recordToUpdate });
        }
      }

      result = await API.graphql(
        graphqlOperation(queries.listOutboundShipments, { limit: 1000 }),
      );
      data = result.data.listOutboundShipments.items.filter(
        (itm) => itm.reloadYardId === queryTerms.reloadYardId,
      );
      console.log("Outbound data:", data);
      debugger;

      for (const outbound of data) {
        let index = 10001;

        const resp = await API.graphql(
          graphqlOperation(getOutboundShipment, { id: outbound.id }),
        );
        const lineItems = resp.data.getOutboundShipment.shipmentLineItems.items;
        console.log("Line Items data:", lineItems?.length);

        for (const lineItem of lineItems) {
          const recordToUpdate = {
            ...lineItem,
            sortKey: lineItem.sortKey ? lineItem.sortKey : index,
          };
          index = index + 1;
          debugger;
          delete recordToUpdate.inventory;
          delete recordToUpdate.createdAt;
          delete recordToUpdate.updatedAt;

          console.log(recordToUpdate.lotNumber, recordToUpdate.sortKey);
          await dataProvider.update("shipmentLineItems", {
            data: recordToUpdate,
          });
        }
      }

      console.log("done");
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:runCustomFiledID", error, notify);
    }
  };

  // resetAislesLocations
  const resetAisleSortKey = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);

    try {
      debugger;
      const resp = await API.graphql(
        graphqlOperation(queries.aisleByActiveBySortKey, {
          reloadYardId: queryTerms.reloadYardId,
        }),
      );
      const aisles = resp.data.aisleByActiveBySortKey.items;
      console.log(aisles);

      let index = 0;
      for (const itm of aisles) {
        let input = {
          ...itm,
          sortKey: index + SORTKEY_BUFFER,
        };
        index++;

        if (itm.type === LocationType.UNSTRUCTURED) {
          delete input.bays;
          delete input.levels;
          delete input.positions;
        }
        delete input.area;
        delete input.zone;
        delete input.createdAt;
        delete input.updatedAt;
        delete input.accountId;
        delete input.locations;

        await API.graphql(graphqlOperation(mutations.updateAisle, { input }));
      }
    } catch (err) {
      console.log(err);
    }

    console.log("all done");
    notify("All done");
  };

  const backfillInventoryFields = async (queryTerms) => {
    console.clear();
    const startedAt = performance.now();

    let pageCount = 0;
    let inventoryScanned = 0;
    let inventoryUpdateCount = 0;
    let inventorySkippedCount = 0;
    let fieldDiffCount = 0;
    let totalTasks = 0;
    let failedTasks = 0;
    debugger;

    try {
      let nextToken;
      const concurrency = 50;
      const limit = pLimit(concurrency);
      const searchInput = {
        limit: 200,
      };

      // NOTE: Intentionally scanning ALL inventories (ignore reloadYardId).
      do {
        pageCount++;
        const pageStart = performance.now();
        const input = nextToken ? { nextToken, ...searchInput } : searchInput;
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInventorysForBackfill, input),
        );
        const inventoryList = result.data.listInventories.items || [];
        nextToken = result.data.listInventories.nextToken;
        inventoryScanned += inventoryList.length;

        const promiseList = [];
        let pageSkipped = 0;
        let pageFieldDiffs = 0;
        let pageUpdates = 0;

        for (const inventory of inventoryList) {
          const updateFields = {};

          if (!inventory.lastOutboundId) {
            const latestOutboundShipment =
              getLatestShippedOutboundId(inventory);
            if (latestOutboundShipment) {
              updateFields.lastOutboundId = latestOutboundShipment;
            } else {
              updateFields.lastOutboundId = null;
            }
          }
          if (inventory.status === "ADVICE_PENDING") {
            updateFields.status = InventoryStatus.ADVICE_REVIEW;
          }
          if (inventory.status === "ADVICE_REVIEWED") {
            updateFields.status = InventoryStatus.ADVICE_REVIEW;
          }

          const normalizedPoNumber = normalizeValue(inventory.poNumber);
          if (inventory.normalizedPoNumber !== normalizedPoNumber) {
            updateFields.normalizedPoNumber = normalizedPoNumber;
          }

          const normalizedRailCarNumber = normalizeValue(
            inventory.railCarNumber,
          );
          if (inventory.normalizedRailCarNumber !== normalizedRailCarNumber) {
            updateFields.normalizedRailCarNumber = normalizedRailCarNumber;
          }

          // If there is nothing to backfill, skip the update entirely.
          const fieldsToUpdateCount = Object.keys(updateFields).length;
          if (fieldsToUpdateCount === 0) {
            pageSkipped++;
            continue;
          }
          pageFieldDiffs += fieldsToUpdateCount;

          const toUpdateInventory = {
            ...inventory,
            ...updateFields,
          };

          // console.log("toUpdateInventory", toUpdateInventory)
          inventoryUpdateCount++;
          pageUpdates++;
          promiseList.push(limit(() => utils.saveInventory(toUpdateInventory)));
        }

        inventorySkippedCount += pageSkipped;
        fieldDiffCount += pageFieldDiffs;
        totalTasks += promiseList.length;

        console.log(
          `[inventory_fields_backfill] page ${pageCount} inventories=${
            inventoryList.length
          } updates=${pageUpdates} skipped=${pageSkipped} fieldDiffs=${pageFieldDiffs} tasks=${
            promiseList.length
          } failed=${failedTasks} nextToken=${
            nextToken ? "yes" : "no"
          } concurrency=${concurrency}`,
        );

        const results = await Promise.allSettled(promiseList);
        let pageFailed = 0;
        for (const resp of results) {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp);
            pageFailed++;
            failedTasks++;
          }
        }

        console.log(
          `[inventory_fields_backfill] page ${pageCount} complete in ${Math.round(
            performance.now() - pageStart,
          )}ms rejected=${pageFailed}`,
        );
      } while (nextToken);
    } catch (err) {
      const message = err?.errors?.map((e) => e.message).join(" | ");
      const normalizedError = message
        ? Object.assign(new Error(message), { errors: err.errors })
        : err;
      logError(
        "AdminDashboard:inventory_fields_backfill",
        normalizedError,
        notify,
      );
    } finally {
      console.log("[inventory_fields_backfill] done", {
        pageCount,
        inventoryScanned,
        inventoryUpdateCount,
        inventorySkippedCount,
        fieldDiffCount,
        totalTasks,
        failedTasks,
        durationMs: Math.round(performance.now() - startedAt),
      });
    }

    notify("All done");
  };

  const backfillInventoryItemFields = async () => {
    console.clear();
    const startedAt = performance.now();

    let pageCount = 0;
    let pageFetchErrorCount = 0;
    let missingInventoryCount = 0;
    let inventoryItemsScanned = 0;
    let inventoryItemUpdateCount = 0;
    let inventoryItemSkippedCount = 0;
    let fieldDiffCount = 0;
    let totalTasks = 0;
    let failedTasks = 0;
    debugger;

    try {
      let nextToken;
      const concurrency = 50;
      const limit = pLimit(concurrency);
      const searchInput = {
        limit: 200,
      };

      // NOTE: Intentionally scanning ALL inventories (ignore reloadYardId).
      do {
        pageCount++;
        const pageStart = performance.now();
        const input = nextToken ? { nextToken, ...searchInput } : searchInput;
        const currentPageToken = nextToken;
        let pageResult;

        try {
          pageResult = await API.graphql(
            graphqlOperation(adminQueries.listInventoryItemsForBackfill, input),
          );
        } catch (err) {
          const message = err?.errors?.map((e) => e.message).join(" | ");
          const normalizedError = message
            ? Object.assign(new Error(message), { errors: err.errors })
            : err;
          const partialPage = err?.data?.listInventoryItems;

          pageFetchErrorCount++;
          logError(
            "AdminDashboard:inventory_item_fields_backfill:page_fetch",
            normalizedError,
            false,
          );

          if (!partialPage) {
            throw normalizedError;
          }

          pageResult = {
            data: {
              listInventoryItems: partialPage,
            },
          };

          console.log(
            `[inventory_item_fields_backfill] page ${pageCount} fetch had GraphQL errors; continuing with partial page data`,
            {
              currentPageToken,
              nextToken: partialPage.nextToken,
              inventoryItems: partialPage.items?.length || 0,
            },
          );
        }

        const inventoryItemsPage = pageResult?.data?.listInventoryItems;
        if (!inventoryItemsPage) {
          throw new Error("Missing listInventoryItems page data");
        }

        const inventoryItemsList = inventoryItemsPage.items || [];
        nextToken = inventoryItemsPage.nextToken;

        if (
          currentPageToken === nextToken &&
          inventoryItemsList.length === 0 &&
          currentPageToken
        ) {
          throw new Error(
            `Inventory item backfill pagination did not advance for page ${pageCount}`,
          );
        }

        inventoryItemsScanned += inventoryItemsList.length;

        const promiseList = [];
        let pageSkipped = 0;
        let pageFieldDiffs = 0;
        let pageUpdates = 0;

        for (const inventoryItem of inventoryItemsList) {
          if (!inventoryItem?.inventory) {
            pageSkipped++;
            missingInventoryCount++;
            console.log(
              "[inventory_item_fields_backfill] inventoryItem missing inventory; skipping",
              {
                inventoryItemId: inventoryItem?.id,
                inventoryId: inventoryItem?.inventoryId,
                pageCount,
              },
            );
            continue;
          }

          const updateFields = {};

          // If there is nothing to backfill, skip the update entirely.
          const fieldsToUpdateCount = Object.keys(updateFields).length;
          if (fieldsToUpdateCount === 0) {
            pageSkipped++;
            continue;
          }
          pageFieldDiffs += fieldsToUpdateCount;

          const toUpdateInventoryItem = {
            id: inventoryItem.id,
            ...updateFields,
          };

          inventoryItemUpdateCount++;
          pageUpdates++;
          promiseList.push(
            limit(() => utils.saveInventoryItem(toUpdateInventoryItem)),
          );
        }

        inventoryItemSkippedCount += pageSkipped;
        fieldDiffCount += pageFieldDiffs;
        totalTasks += promiseList.length;

        console.log(
          `[inventory_item_fields_backfill] page ${pageCount} inventoryItems=${
            inventoryItemsList.length
          } updates=${pageUpdates} skipped=${pageSkipped} fieldDiffs=${pageFieldDiffs} tasks=${
            promiseList.length
          } failed=${failedTasks} nextToken=${
            nextToken ? "yes" : "no"
          } concurrency=${concurrency}`,
        );

        const results = await Promise.allSettled(promiseList);
        let pageFailed = 0;
        for (const resp of results) {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp);
            pageFailed++;
            failedTasks++;
          }
        }

        console.log(
          `[inventory_item_fields_backfill] page ${pageCount} complete in ${Math.round(
            performance.now() - pageStart,
          )}ms rejected=${pageFailed}`,
        );
      } while (nextToken);
    } catch (err) {
      const message = err?.errors?.map((e) => e.message).join(" | ");
      const normalizedError = message
        ? Object.assign(new Error(message), { errors: err.errors })
        : err;
      logError(
        "AdminDashboard:inventory_item_fields_backfill",
        normalizedError,
        notify,
      );
    } finally {
      console.log("[inventory_item_fields_backfill] done", {
        pageCount,
        pageFetchErrorCount,
        missingInventoryCount,
        inventoryItemsScanned,
        inventoryItemUpdateCount,
        inventoryItemSkippedCount,
        fieldDiffCount,
        totalTasks,
        failedTasks,
        durationMs: Math.round(performance.now() - startedAt),
      });
    }

    notify("All done");
  };

  const backfillInboundFields = async () => {
    console.clear();
    const startedAt = performance.now();

    let pageCount = 0;
    let inboundsScanned = 0;
    let inboundUpdateCount = 0;
    let inboundSkippedCount = 0;
    let fieldDiffCount = 0;
    let totalTasks = 0;
    let failedTasks = 0;
    debugger;

    try {
      let nextToken;
      const concurrency = 50;
      const limit = pLimit(concurrency);
      const searchInput = {
        limit: 200,
      };

      // NOTE: Intentionally scanning ALL inbound shipments (ignore reloadYardId).
      do {
        pageCount++;
        const pageStart = performance.now();
        const input = nextToken ? { nextToken, ...searchInput } : searchInput;
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInboundShipmentsForBackfill, input),
        );
        const inboundList = result.data.listInboundShipments.items || [];
        nextToken = result.data.listInboundShipments.nextToken;
        inboundsScanned += inboundList.length;

        const promiseList = [];
        let pageSkipped = 0;
        let pageFieldDiffs = 0;
        let pageUpdates = 0;

        for (const inbound of inboundList) {
          const updateFields = {};

          // Simple rule: if old && old !== new, write new = old.
          if (
            inbound.originCarrier &&
            inbound.originCarrier !== inbound.carrierName
          ) {
            updateFields.carrierName = inbound.originCarrier;
            updateFields.originCarrier = null;
          }
          if (inbound.braRawData) updateFields.braRawData = null;

          // If there is nothing to backfill, skip the update entirely.
          const fieldsToUpdateCount = Object.keys(updateFields).length;
          if (fieldsToUpdateCount === 0) {
            pageSkipped++;
            continue;
          }
          pageFieldDiffs += fieldsToUpdateCount;

          const toUpdateInbound = {
            id: inbound.id,
            ...updateFields,
          };

          inboundUpdateCount++;
          pageUpdates++;
          promiseList.push(
            limit(async () => {
              return API.graphql(
                graphqlOperation(inboundMutations.updateInboundShipmentMini, {
                  input: toUpdateInbound,
                }),
              );
            }),
          );
        }

        inboundSkippedCount += pageSkipped;
        fieldDiffCount += pageFieldDiffs;
        totalTasks += promiseList.length;

        console.log(
          `[inbound_fields_backfill] page ${pageCount} inbounds=${
            inboundList.length
          } updates=${pageUpdates} skipped=${pageSkipped} fieldDiffs=${pageFieldDiffs} tasks=${
            promiseList.length
          } failed=${failedTasks} nextToken=${
            nextToken ? "yes" : "no"
          } concurrency=${concurrency}`,
        );

        const results = await Promise.allSettled(promiseList);
        let pageFailed = 0;
        for (const resp of results) {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp);
            pageFailed++;
            failedTasks++;
          }
        }

        console.log(
          `[inbound_fields_backfill] page ${pageCount} complete in ${Math.round(
            performance.now() - pageStart,
          )}ms rejected=${pageFailed}`,
        );
      } while (nextToken);
    } catch (err) {
      const message = err?.errors?.map((e) => e.message).join(" | ");
      const normalizedError = message
        ? Object.assign(new Error(message), { errors: err.errors })
        : err;
      logError(
        "AdminDashboard:inbound_fields_backfill",
        normalizedError,
        notify,
      );
    } finally {
      console.log("[inbound_fields_backfill] done", {
        pageCount,
        inboundsScanned,
        inboundUpdateCount,
        inboundSkippedCount,
        fieldDiffCount,
        totalTasks,
        failedTasks,
        durationMs: Math.round(performance.now() - startedAt),
      });
    }

    notify("All done");
  };

  const backfillOutboundFields = async () => {
    console.clear();
    const startedAt = performance.now();

    let pageCount = 0;
    let outboundsScanned = 0;
    let outboundUpdateCount = 0;
    let outboundSkippedCount = 0;
    let fieldDiffCount = 0;
    let totalTasks = 0;
    let failedTasks = 0;
    debugger;

    try {
      let nextToken;
      const concurrency = 50;
      const limit = pLimit(concurrency);
      const searchInput = {
        limit: 200,
      };

      // NOTE: Intentionally scanning ALL inbound shipments (ignore reloadYardId).
      do {
        pageCount++;
        const pageStart = performance.now();
        const input = nextToken ? { nextToken, ...searchInput } : searchInput;
        const result = await API.graphql(
          graphqlOperation(
            adminQueries.listOutboundShipmentsForBackfill,
            input,
          ),
        );
        const outboundList = result.data.listOutboundShipments.items || [];
        nextToken = result.data.listOutboundShipments.nextToken;
        outboundsScanned += outboundList.length;

        const promiseList = [];
        let pageSkipped = 0;
        let pageFieldDiffs = 0;
        let pageUpdates = 0;

        for (const outbound of outboundList) {
          const updateFields = {};

          // Simple rule: if old && old !== new, write new = old.
          if (outbound.carrier && outbound.carrier !== outbound.carrierName) {
            updateFields.carrierName = outbound.carrier;
            updateFields.carrier = null;
          }

          // If there is nothing to backfill, skip the update entirely.
          const fieldsToUpdateCount = Object.keys(updateFields).length;
          if (fieldsToUpdateCount === 0) {
            pageSkipped++;
            continue;
          }
          pageFieldDiffs += fieldsToUpdateCount;

          const toUpdateOutbound = {
            id: outbound.id,
            ...updateFields,
          };

          outboundUpdateCount++;
          pageUpdates++;
          promiseList.push(
            limit(() =>
              API.graphql(
                graphqlOperation(outboundMutations.updateOutboundShipmentMini, {
                  input: toUpdateOutbound,
                }),
              ),
            ),
          );
        }

        outboundSkippedCount += pageSkipped;
        fieldDiffCount += pageFieldDiffs;
        totalTasks += promiseList.length;

        console.log(
          `[outbound_fields_backfill] page ${pageCount} inbounds=${
            outboundList.length
          } updates=${pageUpdates} skipped=${pageSkipped} fieldDiffs=${pageFieldDiffs} tasks=${
            promiseList.length
          } failed=${failedTasks} nextToken=${
            nextToken ? "yes" : "no"
          } concurrency=${concurrency}`,
        );

        const results = await Promise.allSettled(promiseList);
        let pageFailed = 0;
        for (const resp of results) {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp);
            pageFailed++;
            failedTasks++;
          }
        }

        console.log(
          `[outbound_fields_backfill] page ${pageCount} complete in ${Math.round(
            performance.now() - pageStart,
          )}ms rejected=${pageFailed}`,
        );
      } while (nextToken);
    } catch (err) {
      const message = err?.errors?.map((e) => e.message).join(" | ");
      const normalizedError = message
        ? Object.assign(new Error(message), { errors: err.errors })
        : err;
      logError(
        "AdminDashboard:outbound_fields_backfill",
        normalizedError,
        notify,
      );
    } finally {
      console.log("[outbound_fields_backfill] done", {
        pageCount,
        outboundsScanned,
        outboundUpdateCount,
        outboundSkippedCount,
        fieldDiffCount,
        totalTasks,
        failedTasks,
        durationMs: Math.round(performance.now() - startedAt),
      });
    }

    notify("All done");
  };

  const doCreateInventoryItems = (currentInventory) => {
    const promiseList = [];
    const toCreateInvItem = {
      groups: currentInventory.groups,
      owner: currentInventory.owner,
      status: currentInventory.status,
      inventoryId: currentInventory.id,
      reloadYardId: currentInventory.reloadYardId,
      areaId: currentInventory.areaId,
      zoneId: currentInventory.zoneId,
      aisleId: currentInventory.aisleId,
      locationId: currentInventory.locationId,
      inboundShipmentId: currentInventory.inboundShipmentId,
      outboundShipmentId: currentInventory.outboundShipmentId || "",
      locationFullName: currentInventory.location
        ? getLocationFullName(currentInventory.location)
        : currentInventory.locationFullName || "",
    };
    // if (currentInventory.id === "5ad3378b-7964-4517-ac37-d0d798ca52c1") debugger;
    if (
      !toCreateInvItem.locationFullName ||
      toCreateInvItem.locationFullName === ""
    ) {
      debugger;
      console.log(
        "inventory missing location",
        currentInventory,
        currentInventory.id,
        currentInventory.locationId,
      );
    }

    let remainingCount = currentInventory.quantityReceived; // quantityReceived - qty shipped outbound
    if (
      currentInventory.inventoryUOM === "UNIT" &&
      currentInventory.location.type === LocationType.STRUCTURED &&
      !currentInventory.location.locationLoad?.other.selected
    ) {
      if (currentInventory.shipmentLineItems?.items?.length > 0) {
        for (const lineItem of currentInventory.shipmentLineItems.items) {
          const qtyShipped = lineItem.totalQuantityToPick;
          if (lineItem.status === ShipmentLineItemStatus.SHIPPED) {
            for (let i = 1; i <= qtyShipped; i++) {
              toCreateInvItem.status = InventoryStatus.SHIPPED;
              toCreateInvItem.quantityOnHand = 0;
              toCreateInvItem.quantityCommitted = 0;
              toCreateInvItem.history = currentInventory.history;
              promiseList.push(
                API.graphql(
                  graphqlOperation(mutations.createInventoryItem, {
                    input: { ...toCreateInvItem },
                  }),
                ),
              );
              remainingCount--;
            }
          } else {
            for (let i = 1; i <= qtyShipped; i++) {
              toCreateInvItem.status = currentInventory.status;
              toCreateInvItem.quantityOnHand = 1;
              toCreateInvItem.quantityCommitted = 1;
              toCreateInvItem.history = currentInventory.history;
              promiseList.push(
                API.graphql(
                  graphqlOperation(mutations.createInventoryItem, {
                    input: { ...toCreateInvItem },
                  }),
                ),
              );
              remainingCount--;
            }
          }
        }
      }

      for (let i = 1; i <= remainingCount; i++) {
        toCreateInvItem.status = currentInventory.status;
        toCreateInvItem.quantityOnHand = 1;
        toCreateInvItem.quantityCommitted = 0;
        toCreateInvItem.history = currentInventory.history;
        promiseList.push(
          API.graphql(
            graphqlOperation(mutations.createInventoryItem, {
              input: { ...toCreateInvItem },
            }),
          ),
        );
      }
    } else {
      // inventoryUOM !== "UNIT" || loadType === OTHER
      if (
        currentInventory.quantityOnHand === null ||
        currentInventory.quantityCommitted === null
      ) {
        if (currentInventory.shipmentLineItems?.items?.length > 0) {
          let totalShipped = 0;
          let totalCommitted = 0;
          for (const lineItem of currentInventory.shipmentLineItems.items) {
            if (lineItem.status === ShipmentLineItemStatus.SHIPPED) {
              toCreateInvItem.status = InventoryStatus.SHIPPED;
              totalShipped += lineItem.totalQuantityToPick;
            } else {
              toCreateInvItem.status = currentInventory.status;
              totalCommitted += lineItem.totalQuantityToPick;
            }
          }
          toCreateInvItem.quantityOnHand =
            currentInventory.quantityReceived - totalShipped;
          toCreateInvItem.quantityCommitted = totalCommitted;
        } else {
          toCreateInvItem.status = currentInventory.status;
          toCreateInvItem.quantityOnHand = currentInventory.quantityReceived;
          toCreateInvItem.quantityCommitted =
            currentInventory.quantityCommitted;
        }
      } else {
        toCreateInvItem.status = currentInventory.status;
        toCreateInvItem.quantityOnHand = currentInventory.quantityOnHand;
        toCreateInvItem.quantityCommitted = currentInventory.quantityCommitted;
      }

      toCreateInvItem.history = currentInventory.history;
      promiseList.push(
        API.graphql(
          graphqlOperation(mutations.createInventoryItem, {
            input: { ...toCreateInvItem },
          }),
        ),
      );
    }
    return promiseList;
  };

  const createInventoryItems = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    console.log("env", process.env.NODE_ENV);
    debugger;
    // create inventory items from inventory for inventoryItem release conversion

    const startTime = performance.now();
    let inventoryCount = 0;

    try {
      const searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 20,
      };

      let nextToken = true;
      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(adminQueries.inventoryByStatusByLot, input),
        );
        const inventoryList = result.data.inventoryByStatusByLot.items.filter(
          (itm) => {
            // keep if has no aisleList, and has aisleId and locationId
            return (
              (!itm.aisleList || itm.aisleList?.length === 0) &&
              itm.locationId &&
              itm.aisleId
            );
          },
        );
        console.log("inventoryList", inventoryList);
        nextToken = result.data.inventoryByStatusByLot.nextToken;

        for (const inventory of inventoryList) {
          // if (inventory.id === "5ad3378b-7964-4517-ac37-d0d798ca52c1") debugger;
          if (
            !inventory.aisleList ||
            !inventory.inventoryItems ||
            inventory.inventoryItems.items.length === 0
          ) {
            const promises = doCreateInventoryItems(inventory);
            promiseList = promiseList.concat(promises);

            inventory.aisleList = [inventory.aisleId];
            promiseList.push(utils.saveInventory(inventory));
            inventoryCount += 1;
          } else {
            console.log("manually created", inventory, inventory.id);
          }
        }

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);

        response.some((resp) => {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp.reason);
            // return true; // Stops the iteration
          }
          return false;
        });
      }
    } catch (err) {
      logError("AdminDashboard:createInventoryItems", err, notify);
    }

    const endTime = performance.now();
    utils.elapsedTime(startTime, endTime, inventoryCount, 16300);

    notify("All done");
  };

  const hdmCreateInventoryItem = (inventory) => {
    const promiseList = [];
    const location = inventory.location;
    const locationFullName = location ? getLocationFullName(location) : "TTR-1";
    const toCreateInvItem = {
      groups: inventory.groups,
      owner: inventory.owner,
      status: inventory.status,
      inventoryId: inventory.id,
      reloadYardId: inventory.reloadYardId,
      areaId: location?.areaId ?? inventory.areaId,
      zoneId: location?.zoneId ?? inventory.zoneId,
      aisleId: location?.aisleId ?? "67aa47c5-68c5-4546-ba7f-dcf806db64d6",
      locationId: location?.id ?? "9f9efa58-e1c7-4396-a70a-4808ef263690",
      inboundShipmentId: inventory.inboundShipmentId,
      outboundShipmentId: inventory.outboundShipmentId || "",
      locationFullName,
    };

    toCreateInvItem.quantityOnHand = inventory.quantityOnHand;
    toCreateInvItem.quantityCommitted = inventory.quantityCommitted;

    toCreateInvItem.history = [
      {
        by: "Jonathan MacGregor",
        date: inventory.updatedAt,
        text:
          "Allocated " +
          inventory.quantityOnHand +
          " " +
          inventory.inventoryUOM +
          " to " +
          locationFullName,
        type: "INVENTORY",
      },
    ];
    console.log("toCreateInvItem", toCreateInvItem);

    promiseList.push(
      API.graphql(
        graphqlOperation(mutations.createInventoryItem, {
          input: { ...toCreateInvItem },
        }),
      ),
    );
    return promiseList;
  };

  const hdmFixInventoryItems = async () => {
    console.clear();
    // create missing inventory items from inventory and fix status
    debugger;

    try {
      let searchInput = {
        filter: {
          reloadYardId: { eq: "3404db3b-775d-44a9-a445-2551f04a5eb6" },
          status: { eq: "STORED" },
        },
        limit: 500,
      };

      let nextToken = true;
      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );
        const inventoryList = result.data.searchInventorys.items;
        console.log("inventoryList", inventoryList);
        nextToken = result.data.searchInventorys.nextToken;

        for (const inventory of inventoryList) {
          if (
            !inventory.inventoryItems ||
            inventory.inventoryItems.items.length === 0
          ) {
            const promises = hdmCreateInventoryItem(inventory);
            promiseList = promiseList.concat(promises);
            inventory.aisleList = ["67aa47c5-68c5-4546-ba7f-dcf806db64d6"];
            promiseList.push(utils.saveInventory(inventory));
          } else {
            for (const inventoryItem of inventory.inventoryItems.items) {
              if (
                inventoryItem.status !== inventory.status ||
                inventoryItem.inboundShipmentId !== inventory.inboundShipmentId
              ) {
                if (inventoryItem.status !== inventory.status) {
                  console.log("status", inventoryItem.status, inventory.status);
                }
                if (
                  inventoryItem.inboundShipmentId !==
                  inventory.inboundShipmentId
                ) {
                  console.log(
                    "inboundShipmentId",
                    inventoryItem.inboundShipmentId,
                    inventory.inboundShipmentId,
                  );
                }
                inventoryItem.status = inventory.status;
                inventoryItem.inboundShipmentId = inventory.inboundShipmentId;
                const promise = utils.saveInventoryItem(inventoryItem);
                promiseList = promiseList.concat(promise);
              }
            }
          }
        }

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);

        response.some((resp) => {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp.reason);
          }
          return false;
        });
      }
    } catch (err) {
      logError("AdminDashboard:hdmFixInventoryItems", err, notify);
    }

    notify("All done");
  };

  function processInventoryMerge(inventories) {
    // Grouping inventories by their equality criteria
    let groupedInventories = {};
    let keptInventories = []; // List to keep track of inventories that are retained
    let updatePromises = [];
    let deletePromises = [];

    inventories.forEach((inv) => {
      let key = `${inv.productName}||${inv.inboundShipmentId}||${
        inv.productId || "productId"
      }||${
        JSON.stringify(inv.customFields) || "customFields"
      }||${inv.pcs || "pcs"}|`;
      if (!groupedInventories[key]) {
        groupedInventories[key] = [];
      }
      groupedInventories[key].push(inv);
    });
    console.log("groupedInventories", groupedInventories);

    // Process each inventyory group array
    Object.values(groupedInventories).forEach((invGroup) => {
      debugger;
      // Find the first 'STORED' inventory if there's a mix of statuses, otherwise keep the first one
      // only dealing with STORED and SHIPPED inventories.
      let keepInventory =
        invGroup.find((inv) => inv.status === "STORED") || invGroup[0];
      const aisleIdMap = {};
      let mergedHistory = [];
      let mergedNotes = [];
      let oldestInventoryCreatedEntry = null;

      // Merge logic
      invGroup.forEach((inv) => {
        // Merge histories
        inv.history.forEach((hist) => {
          debugger;
          if (
            hist.text.startsWith("Inventory Created") ||
            hist.text.startsWith("Created Inventory")
          ) {
            if (
              !oldestInventoryCreatedEntry ||
              new Date(hist.date) < new Date(oldestInventoryCreatedEntry.date)
            ) {
              oldestInventoryCreatedEntry = hist; // Keep the oldest 'Inventory Created' entry
            }
            // Skip the addition of 'Inventory Created' entry here as it will be added later if it's the oldest
          } else if (hist.text === "Location re-named from  to ") {
            // skip all blank "location re-named" history
          } else if (hist.text === "Location changed  to ") {
            // skip all blank "Location changed" history
          } else if (
            inv.id !== keepInventory.id &&
            !hist.text.startsWith("Location changed")
          ) {
            // Add the history entry from a deleted inventory if it's not
            //	'Inventory Created' || "Location changed"
            mergedHistory.push(hist);
          } else if (inv.id === keepInventory.id) {
            // keep all other history from keepInventory
            mergedHistory.push(hist);
          }
        });

        // merge notes
        inv.notes?.forEach((note) => {
          mergedNotes.push(note);
        });

        if (inv.id === keepInventory.id) {
          // keep ailes from kept inventoryItems
          inv.inventoryItems.items.forEach((item) => {
            aisleIdMap[item.aisleId] = true;
          });
        } else {
          // Update inventoryItems of the duplicate inventories and sum their quantities
          inv.inventoryItems.items.forEach((item) => {
            item.inventoryId = keepInventory.id;
            /////////////////////////////////////////////////////
            updatePromises.push(utils.saveInventoryItem(item));

            // if SHIPPED qtyOnHand = 0 ...
            keepInventory.quantityOnHand += item.quantityOnHand;
            keepInventory.quantityCommitted += item.quantityCommitted;
            aisleIdMap[item.aisleId] = true;
          });

          // Sum quantities from deleted inventories
          // if SHIPPED qtyOnHand = 0 ...
          keepInventory.quantityAdvised += inv.quantityAdvised;
          keepInventory.quantityReceived += inv.quantityReceived;
          keepInventory.depletedAt =
            keepInventory.depletedAt > inv.depletedAt
              ? keepInventory.depletedAt
              : inv.depletedAt;
          keepInventory.turnDays =
            keepInventory.turnDays > inv.turnDays
              ? keepInventory.turnDays
              : inv.turnDays;

          // Update shipmentLineItems for each inventory
          inv.shipmentLineItems.items.forEach((shipmentLineItem) => {
            shipmentLineItem.inventoryId = keepInventory.id;
            /////////////////////////////////////////////////////
            updatePromises.push(utils.saveShipmentLineItem(shipmentLineItem));

            // Update shipmentLineItemInventoryItems for each shipment line item
            shipmentLineItem.inventoryItems.items.forEach((inventoryItem) => {
              inventoryItem.inventoryId = keepInventory.id;
              /////////////////////////////////////////////////////
              updatePromises.push(
                utils.saveShipmentLineItemInventoryItem(inventoryItem),
              );
            });
          });

          /////////////////////////////////////////////////////
          deletePromises.push(utils.removeInventory(inv));
        }
      });

      // update aisle list for kept inventory
      keepInventory.aisleList = Object.keys(aisleIdMap);

      // Sort the merged history by date
      mergedHistory.sort((a, b) => new Date(a.date) - new Date(b.date));

      // If the oldest 'Inventory Created' entry exists, ensure it's the first entry
      if (oldestInventoryCreatedEntry) {
        mergedHistory.unshift(oldestInventoryCreatedEntry);
      }

      keepInventory.history = mergedHistory;

      if (mergedNotes.length > 0) {
        mergedNotes.sort((a, b) => new Date(a.date) - new Date(b.date));
        keepInventory.notes = mergedNotes;
      }

      // Keep track of the retained inventory
      keptInventories.push(keepInventory);
    });

    // Update sortKey of all kept inventories
    let sortKeyIndex = 10001;
    keptInventories.forEach((keepInventory) => {
      keepInventory.sortKey = sortKeyIndex++;
      /////////////////////////////////////////////////////
      updatePromises.push(utils.saveInventory(keepInventory));
    });

    return [...updatePromises, ...deletePromises];
  }

  const mergeInventories = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    debugger;
    // merge inventories with same inbound, product name, custom fields, units. Move inventoryItems

    try {
      let nextToken = true;
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 10,
      };

      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(
            adminQueries.inboundShipmentByReloadYardByAccount,
            input,
          ),
        );
        const inboundList =
          result.data.inboundShipmentByReloadYardByAccount.items.filter(
            (itm) => itm.status === "RELEASED",
          );
        console.log("inboundList", inboundList);
        nextToken = result.data.inboundShipmentByReloadYardByAccount.nextToken;

        for (const inbbund of inboundList) {
          const inventoryList = inbbund.inventories.items.sort(
            (a, b) => a.sortKey - b.sortKey,
          );
          console.log("inventoryList", inventoryList);
          promiseList = processInventoryMerge(inventoryList);
        }

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);

        response.some((resp) => {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp.reason);
            return true; // Stops the iteration
          }
          return false;
        });
      }
    } catch (err) {
      logError("AdminDashboard:mergeInventories", err, notify);
    }

    notify("All done");
  };

  function processShipmentLineItemMerge(lineItems) {
    // Grouping lineItems by their equality criteria
    let groupedLineItems = {};
    let keptLineItems = []; // List to keep track of LineItems that are retained
    let updatePromises = [];
    let deletePromises = [];

    lineItems.forEach((item) => {
      let key = `${item.inventoryId}||${item.outboundShipmentId}||${item.status}`;
      if (!groupedLineItems[key]) {
        groupedLineItems[key] = [];
      }
      groupedLineItems[key].push(item);
    });
    console.log("groupedLineItems", groupedLineItems);

    // Process each inventyory group array
    Object.values(groupedLineItems).forEach((lineItemGroup) => {
      debugger;
      // keep the first one
      let keepLineItem = lineItemGroup[0];
      let mergedHistory = [];
      let mergedNotes = [];
      let oldestLineItemCreatedEntry = null;
      console.log("keepLineItem", keepLineItem);

      // Merge logic
      lineItemGroup.forEach((lineItem) => {
        // Merge histories
        lineItem.history.forEach((hist) => {
          debugger;
          if (
            hist.text.startsWith("Pick List item created") ||
            hist.text.startsWith("Committed Qty. of")
          ) {
            if (
              !oldestLineItemCreatedEntry ||
              new Date(hist.date) < new Date(oldestLineItemCreatedEntry.date)
            ) {
              oldestLineItemCreatedEntry = hist; // Keep the oldest 'Pick List item Created' entry
            }
            // Skip the addition of 'Inventory Created' entry here as it will be added later if it's the oldest
          } else {
            // keep all other history from keepLineItem && deleted lineItems
            mergedHistory.push(hist);
          }
        });
        console.log("mergedHistory", mergedHistory);

        // merge notes
        lineItem.notes?.forEach((note) => {
          mergedNotes.push(note);
        });

        if (lineItem.id !== keepLineItem.id) {
          // Update shipmentLineItemInventoryItems of the duplicate lineItems
          lineItem.inventoryItems.items.forEach((inventoryItem) => {
            inventoryItem.shipmentLineItemId = keepLineItem.id;
            inventoryItem.inventoryId = keepLineItem.inventoryId;
            /////////////////////////////////////////////////////
            updatePromises.push(
              utils.saveShipmentLineItemInventoryItem(inventoryItem),
            );
            console.log(
              "saveShipmentLineItemInventoryItem(inventoryItem)",
              inventoryItem,
            );
          });

          // Sum quantities from deleted lineItems
          keepLineItem.totalQuantityToPick += lineItem.totalQuantityToPick;

          /////////////////////////////////////////////////////
          deletePromises.push(utils.removeShipmentLineItem(lineItem));
          console.log("removeInventory(inv)", lineItem);
        }
      });

      // Sort the merged history by date
      mergedHistory.sort((a, b) => new Date(a.date) - new Date(b.date));

      // If the oldest 'PlicList Item Created' entry exists, ensure it's the first entry
      if (oldestLineItemCreatedEntry) {
        mergedHistory.unshift(oldestLineItemCreatedEntry);
      }

      keepLineItem.history = mergedHistory;

      if (mergedNotes.length > 0) {
        mergedNotes.sort((a, b) => new Date(a.date) - new Date(b.date));
        keepLineItem.notes = mergedNotes;
      }

      // Keep track of the retained inventory
      keptLineItems.push(keepLineItem);
    });

    // Update sortKey of all kept inventories
    let sortKeyIndex = 10001;
    keptLineItems.forEach((keepLineItem) => {
      keepLineItem.sortKey = sortKeyIndex++;
      /////////////////////////////////////////////////////
      updatePromises.push(utils.saveShipmentLineItem(keepLineItem));
      console.log("saveShipmentLineItem(shipmentLineItem)", keepLineItem);
    });

    return [...updatePromises, ...deletePromises];
  }

  const mergeShipmentLineItems = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    debugger;
    // merge inventories with same inbound, product name, custom fields, units. Move inventoryItems

    try {
      let nextToken = true;
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 10,
      };

      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(adminQueries.inventoryByStatusByLot, input),
        );
        const inventoryList = result.data.inventoryByStatusByLot.items;
        console.log("inventoryList", inventoryList);
        nextToken = result.data.inventoryByStatusByLot.nextToken;
        // nextToken = null;

        for (const inventory of inventoryList) {
          const lineItemList = inventory.shipmentLineItems.items.sort(
            (a, b) => a.sortKey - b.sortKey,
          );
          // if (lineItemList.length > 5) {
          console.log("lineItemList", lineItemList);
          promiseList = processShipmentLineItemMerge(lineItemList);
          // }
        }

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);

        response.some((resp) => {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp.reason);
            return true; // Stops the iteration
          }
          return false;
        });
      }
    } catch (err) {
      logError("AdminDashboard:mergeShipmentLineItems", err, notify);
    }

    notify("All done");
  };

  const devInventoryItemCleanout = async () => {
    console.clear();
    debugger;

    try {
      let missingInventory = 0;
      let missingLineItems = 0;
      let missingInvItemss = 0;
      const promiseList = [];
      let inventoryItemMap = {};
      let nextToken = true;

      let result = await API.graphql(
        graphqlOperation(listIdQueries.listInventoryIds, { limit: 1000 }),
      );
      const inventoryList = result.data.listInventories.items; //.filter(itm => itm !== null);
      console.log("inventoryList", inventoryList);
      const inventoryMap = inventoryList.reduce((acc, obj) => {
        acc[obj.id] = obj;
        return acc;
      }, {});

      // ##############################################################################################################
      result = await API.graphql(
        graphqlOperation(listIdQueries.listShipmentLineItemIds, {
          limit: 1000,
        }),
      );
      const lineItemList = result.data.listShipmentLineItems.items; //.filter(itm => itm !== null);
      console.log("lineItemList", lineItemList);
      const lineItemMap = lineItemList.reduce((acc, obj) => {
        acc[obj.id] = obj;
        return acc;
      }, {});

      result = await API.graphql(
        graphqlOperation(
          adminQueries.listShipmentLineItemInventoryItemsDeleteMe,
          { limit: 1000 },
        ),
      );
      const lineItemInvItemList =
        result.data.listShipmentLineItemInventoryItems.items; //.filter(itm => itm !== null);
      console.log("lineItemInvItemList", lineItemInvItemList);
      for (const lineItemInvItem of lineItemInvItemList) {
        let lineItem = lineItemMap[lineItemInvItem.shipmentLineItemId];
        let inventoryItem = lineItemInvItem.inventoryItem;
        let inventory =
          inventoryMap[lineItemInvItem.inventoryId] ||
          lineItemInvItem?.inventoryItem?.inventory;

        if (!lineItem) {
          console.log(
            "lineItemInventoryItem missing lineItem",
            lineItemInvItem,
          );
          missingLineItems++;
          const input = { id: lineItemInvItem.id };
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.deleteShipmentLineItemInventoryItem, {
                input,
              }),
            ),
          );
        } else if (!inventoryItem) {
          console.log(
            "lineItemInventoryItem missing inventoryItem",
            lineItemInvItem,
          );
          missingInvItemss++;
          const input = { id: lineItemInvItem.id };
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.deleteShipmentLineItemInventoryItem, {
                input,
              }),
            ),
          );
        } else if (!inventory) {
          console.log(
            "lineItemInventoryItem missing inventory",
            lineItemInvItem,
          );
          missingInventory++;
          const input = { id: lineItemInvItem.id };
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.deleteShipmentLineItemInventoryItem, {
                input,
              }),
            ),
          );
        } else {
          // } if (location) {
          // delete ShipmentLineItemInventoryItem for old, pre-existing inventories
          // all ShipmentLineItemInventoryItem will be re-created with CreateLineItemInventoryItem
          const input = { id: lineItemInvItem.id };
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.deleteShipmentLineItemInventoryItem, {
                input,
              }),
            ),
          );
        }
      }

      // ##############################################################################################################
      nextToken = true;
      const searchInput = { limit: 500 };
      while (nextToken) {
        try {
          const input =
            nextToken === true ? searchInput : { nextToken, ...searchInput };
          let result = await API.graphql(
            graphqlOperation(adminQueries.listInventoryItemsDeleteMe, input),
          );
          const inventoryItemList = result.data.listInventoryItems.items; //.filter(itm => itm !== null);
          nextToken = result.data.listInventoryItems.nextToken;
          console.log("inventoryItemList", inventoryItemList);

          for (const inventoryItem of inventoryItemList) {
            let inventory =
              inventoryMap[inventoryItem.inventoryId] ||
              inventoryItem.inventory;
            inventoryItemMap[inventoryItem.id] = inventoryItem;

            if (!inventory) {
              // delete inventoryItems if they are missing inventory for some reason
              console.log(
                "inventoryItem missing inventory",
                inventoryItem,
                inventoryItem.id,
                inventoryItem.inventoryId,
              );
              missingInventory++;
              const input = { id: inventoryItem.id };
              promiseList.push(
                API.graphql(
                  graphqlOperation(inventoryMutations.deleteInventoryItemMini, {
                    input,
                  }),
                ),
              );
            } else if (inventory.locationId) {
              // delete inventoryItems for old, pre-existing Inventories with location
              const input = { id: inventoryItem.id };
              promiseList.push(
                API.graphql(
                  graphqlOperation(inventoryMutations.deleteInventoryItemMini, {
                    input,
                  }),
                ),
              );
            }
          }
        } catch (err) {
          logError("AdminDashboard:devInventoryCleanup:loop", err, notify);
        }
      }

      console.log("total inventory missing:", missingInventory);
      console.log("total lineItem missing:", missingLineItems);
      console.log("total invItem missing:", missingInvItemss);

      debugger;
      const response = await Promise.allSettled(promiseList);
      response.forEach((resp) => {
        if (resp.status === "rejected") {
          console.log("REJECTED: ", resp.reason);
        }
      });
    } catch (err) {
      logError("AdminDashboard:devInventoryCleanup", err, notify);
    }
    notify("All done");
  };

  const backfillExtIdPaymentTerm = async () => {
    console.clear();
    debugger;
    // change accountNumber = externalId
    // set externalId = " " for index
    // set paymentTerms

    try {
      // Step 1: List all payment terms
      const ptResult = await API.graphql(
        graphqlOperation(queries.listPaymentTerms, { limit: 1000 }),
      );
      const allPaymentTerms = ptResult.data.listPaymentTerms.items;

      // Step 2: List all accounts
      let result = await API.graphql(
        graphqlOperation(queries.listAccounts, { limit: 1000 }),
      );
      const accountList = result.data.listAccounts.items;
      console.log("accountList", accountList);

      const promiseList = [];
      for (const account of accountList) {
        const reloadYardId = account.reloadYardId;
        const legacyCode = account.paymentTerms;
        const normalizedCode = legacyCode === "NET_14" ? "NET_15" : legacyCode;

        const matched = allPaymentTerms.find(
          (pt) =>
            pt.reloadYardId === reloadYardId && pt.code === normalizedCode,
        );

        let paymentTermId = "";
        if (paymentTermId) {
          paymentTermId = account.paymentTermId;
        } else if (matched) {
          paymentTermId = matched.id;
        } else {
          console.warn("No match for", legacyCode, "in account", account.id);
        }

        const input = {
          ...account,
          externalId: account.externalId
            ? account.externalId
            : account.accountNumber
              ? account.accountNumber
              : " ",
          accountNumber: null,
          paymentTermId,
        };

        delete input.checklists;
        delete input.products;
        delete input.services;
        delete input.savedAddresses;
        delete input.updatedAt;
        delete input.createdAt;
        delete input.billingAddress;
        delete input.history;

        promiseList.push(
          API.graphql(graphqlOperation(mutations.updateAccount, { input })),
        );
      }

      debugger;
      const response = await Promise.allSettled(promiseList);
      const reasonMap = {};
      response.forEach((resp) => {
        if (resp.status === "rejected") {
          if (!reasonMap[resp.reason.errors[0].message]) {
            reasonMap[resp.reason.errors[0].message] =
              resp.reason.errors[0].message;
            console.log("REJECTED: ", resp);
          }
        }
      });
    } catch (err) {
      logError("AdminDashboard:backfillAccountNameNumber", err, notify);
    }
    notify("All done");
  };

  const inventoryItemAddAccount = async () => {
    console.clear();
    debugger;
    // set accountId if missing on all inventoryItems

    const getUnloadedBy = (inventory, inventoryItem) => {
      if (inventoryItem.unloadedBy) return inventoryItem.unloadedBy;
      const unloadedHistory = inventory.history.find((itm) =>
        itm.text.includes("status: UNLOADED"),
      );
      if (unloadedHistory) return unloadedHistory.by;
      return undefined;
    };

    const getUnloadedAt = (inventory, inventoryItem) => {
      if (inventoryItem.unloadedAt) return inventoryItem.unloadedAt;
      const unloadedHistory = inventory.history.find((itm) =>
        itm.text.includes("status: UNLOADED"),
      );
      if (unloadedHistory) return unloadedHistory.date;
      if (inventory.receivedAt) return inventory.receivedAt;
      return undefined;
    };

    try {
      let nextToken = true;
      const searchInput = { limit: 500 };
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(adminQueries.listInventoryItemsDeleteMe, input),
        );
        const inventoryItemList = result.data.listInventoryItems.items;
        nextToken = result.data.listInventoryItems.nextToken;
        console.log("inventoryItemList", inventoryItemList);

        const promiseList = [];
        for (const inventoryItem of inventoryItemList) {
          const inventory = inventoryItem.inventory;

          const input = {
            ...inventoryItem,
            accountId: inventoryItem.accountId
              ? inventoryItem.accountId
              : inventory.accountId,
            unloadedBy: getUnloadedBy(inventory, inventoryItem),
            unloadedAt: getUnloadedAt(inventory, inventoryItem),
          };

          promiseList.push(utils.saveInventoryItem(input));
        }

        debugger;
        const response = await Promise.allSettled(promiseList);
        const reasonMap = {};
        response.forEach((resp) => {
          if (resp.status === "rejected") {
            if (!reasonMap[resp.reason.errors[0].message]) {
              reasonMap[resp.reason.errors[0].message] =
                resp.reason.errors[0].message;
              console.log("REJECTED: ", resp);
            }
          }
        });
      }
    } catch (err) {
      logError("AdminDashboard:inventoryItemAddAccount", err, notify);
    }
    notify("All done");
  };

  const backfillLineItems = (outbound) => {
    debugger;
    const promiseList = [];
    if (outbound.shipmentLineItems?.items?.length > 0) {
      for (const lineItem of outbound.shipmentLineItems.items) {
        const toUpdateLineItem = {
          ...lineItem,
          poNumber: outbound.poNumber,
          status:
            outbound.status === OutboundStatus.RELEASED
              ? ShipmentLineItemStatus.SHIPPED
              : lineItem.status,
        };
        delete toUpdateLineItem.inventory;
        delete toUpdateLineItem.inventoryItems;
        delete toUpdateLineItem.createdAt;
        delete toUpdateLineItem.updatedAt;
        // console.log("toUpdateLineItem", toUpdateLineItem)
        promiseList.push(
          API.graphql(
            graphqlOperation(mutations.updateShipmentLineItem, {
              input: toUpdateLineItem,
            }),
          ),
        );
      }
    }

    return promiseList;
  };

  const doBackfilOutbound = (outbound) => {
    debugger;
    const toUpdateOutbound = {
      ...outbound,
      areaId: outbound.shipmentLineItems?.items[0]?.inventory.areaId,
      zoneId: outbound.shipmentLineItems?.items[0]?.inventory.zoneId,
      reloadYardCommodityId:
        outbound.shipmentLineItems?.items[0]?.inventory.reloadYardCommodityId,
      scheduledDateTime: outbound.scheduledDateTime
        ? outbound.scheduledDateTime
        : null,
    };

    delete toUpdateOutbound.account;
    delete toUpdateOutbound.area;
    delete toUpdateOutbound.createdAt;
    delete toUpdateOutbound.reloadYardCommodity;
    delete toUpdateOutbound.seals;
    delete toUpdateOutbound.shipmentLineItems;
    delete toUpdateOutbound.updatedAt;

    // console.log("toUpdateOutbound", toUpdateOutbound);
    return API.graphql(
      graphqlOperation(mutations.updateOutboundShipment, {
        input: toUpdateOutbound,
      }),
    );
  };

  const backfillOutboundShipment = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    debugger;

    try {
      let nextToken = true;
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 50,
      };

      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(
            adminQueries.outboundShipmentByStatusDeleteMe,
            input,
          ),
        );
        const outboundList = result.data.outboundShipmentByStatus.items;
        console.log("outboundList", outboundList);
        nextToken = result.data.outboundShipmentByStatus.nextToken;

        for (const outbound of outboundList) {
          const promises = backfillLineItems(outbound);
          promiseList = promiseList.concat(promises);

          if (
            !outbound.areaId ||
            !outbound.zoneId ||
            !outbound.reloadYardCommodityId ||
            outbound.scheduledDateTime
          ) {
            promiseList.push(doBackfilOutbound(outbound));
          }
        }

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);
        debugger;
        response.forEach((resp) => {
          if (resp.status === "rejected") console.log("REJECTED: ", resp);
        });
      }
    } catch (err) {
      logError("AdminDashboard:backfillOutboundShipment", err, notify);
    }

    notify("All done");
  };

  const backfillProductFields = async () => {
    console.clear();

    try {
      let nextToken = true;
      let searchInput = {
        limit: 250,
      };

      let totalProductsUpdated = 0;
      let failedProducts = 0;
      let totalProductsSkipped = 0;

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(adminQueries.listProductsForBackfill, input),
        );

        const productList = result.data.listProducts.items;
        nextToken = result.data.listProducts.nextToken;

        let promiseList = [];
        let productIds = [];

        for (const product of productList) {
          console.log("product", product);
          const updateFields = {};

          if (product.defaultWeightUOM === Unit.UNIT) {
            updateFields.defaultWeightUOM = null;
          }
          // Clear the deprecated field once its data has been migrated.
          if (product.totalInventoryQuantity != null) {
            updateFields.totalInventoryQuantity = null;
          }

          // If there is nothing to backfill, skip the update entirely.
          const fieldsToUpdateCount = Object.keys(updateFields).length;
          if (fieldsToUpdateCount === 0) {
            totalProductsSkipped++;
            continue;
          }

          const updatedProduct = {
            id: product.id,
            ...updateFields,
            // status is part of the inventories connection key; keep it set.
            status: InventoryStatus.STORED,
          };

          console.log("updatedProduct", updatedProduct);

          const promise = API.graphql(
            graphqlOperation(mutations.updateProduct, {
              input: updatedProduct,
            }),
          );
          promiseList.push(promise);
          productIds.push(product.id);
        }

        const responses = await Promise.allSettled(promiseList);

        // **Use a regular for loop instead of responses.forEach**
        let successfulUpdates = 0;
        let failedUpdates = 0;

        for (let index = 0; index < responses.length; index++) {
          const resp = responses[index];
          if (resp.status === "fulfilled") {
            successfulUpdates++;
          } else {
            console.error(
              `Failed to update product ${productIds[index]}: `,
              resp.reason,
            );
            failedUpdates++;
          }
        }

        totalProductsUpdated += successfulUpdates;
        failedProducts += failedUpdates;
      }

      console.log("Total Products Updated:", totalProductsUpdated);
      console.log("Failed Products:", failedProducts);
      console.log("Products Skipped (no change):", totalProductsSkipped);
    } catch (err) {
      logError("AdminDashboard:backfillProductFields", err, notify);
    }

    notify("All done");
  };

  const backfilPanelDims = async () => {
    console.clear();
    console.log("Starting panel dims audit…");

    const limit = 250;
    let nextToken = null;
    let scanned = 0;
    let mismatches = 0;
    let updated = 0;

    try {
      // 1) Load all reloadYardCommoditys into a map
      const rycMap = {};
      let rycNext = null;
      do {
        const rycResp = await API.graphql(
          graphqlOperation(queries.listReloadYardCommoditys, {
            limit,
            nextToken: rycNext,
          }),
        );
        const rycItems = rycResp?.data?.listReloadYardCommoditys?.items || [];
        rycItems.forEach((ryc) => (rycMap[ryc.id] = ryc));
        rycNext = rycResp?.data?.listReloadYardCommoditys?.nextToken || null;
      } while (rycNext);

      // 2) Page through products and audit/update Panels
      do {
        const prodResp = await API.graphql(
          graphqlOperation(queries.listProducts, { limit, nextToken }),
        );

        const products = prodResp?.data?.listProducts?.items || [];
        nextToken = prodResp?.data?.listProducts?.nextToken || null;

        const promiseList = [];
        for (const product of products) {
          scanned++;
          const ryc = rycMap[product.reloadYardCommodityId];
          const isPanels = ryc?.commodity?.name === "Panels";
          if (!isPanels) {
            if (product.sf != null) {
              console.log("Clearing SF for non-Panels:", {
                id: product.id,
                productCode: product.productCode,
                sf: product.sf,
              });
              promiseList.push(
                API.graphql(
                  graphqlOperation(mutations.updateProduct, {
                    input: { id: product.id, sf: null },
                  }),
                ),
              );
            }
            continue;
          }

          const actualSf = Number(product.sf);
          const pcsHeight = Number(product.pcsHeight);
          const pcsLengthFeet = Math.ceil(pcsHeight / 12);
          const testRecord = { ...product, pcsLength: pcsLengthFeet };
          const newSf = Number(calculateSqf(testRecord));

          if (
            isFinite(actualSf) &&
            isFinite(newSf) &&
            Math.abs(actualSf - newSf) >= 0.001
          ) {
            mismatches++;
            // If original pcsHeight is greater than 20, compute new pcsLength and update
            if (isFinite(pcsHeight) && pcsHeight > 20) {
              if (pcsLengthFeet > 0) {
                if (isFinite(newSf)) {
                  console.log("SF updated:", {
                    id: product.id,
                    pcsW: product.pcsWidth,
                    // pcsL: product.pcsLength,
                    pcsH: product.pcsHeight,
                    actualSf,
                    newSf,
                  });
                  const input = {
                    id: product.id,
                    pcsLength: pcsLengthFeet,
                    sf: newSf,
                  };
                  // promiseList.push(
                  //   API.graphql(
                  //     graphqlOperation(mutations.updateProduct, { input })
                  //   )
                  // );
                }
              }
            } else {
              console.warn("SF mismatch:", {
                id: product.id,
                pcsWidth: product.pcsWidth,
                pcsLength: product.pcsLength,
                pcsHeight: product.pcsHeight,
                pcs: product.pcs,
                actualSf,
                newSf,
              });
            }
          }
        }

        if (promiseList.length > 0) {
          const responses = await Promise.allSettled(promiseList);
          const succeeded = responses.filter(
            (r) => r.status === "fulfilled",
          ).length;
          updated += succeeded;
          const failed = responses.length - succeeded;
          if (failed > 0) console.warn("Panels dims updates failed:", failed);
        }
      } while (nextToken);

      console.log(
        `Panels audit complete. scanned=${scanned}, mismatches=${mismatches}, updated=${updated}`,
      );
      notify(
        `Panels audit complete: scanned=${scanned}, mismatches=${mismatches}, updated=${updated}`,
      );
    } catch (err) {
      logError("AdminDashboard:backfilPanelDims", err, notify);
    }
  };

  /**
   * Wipes the gate check-in surface so enrollment and check-in can be retested
   * from scratch: gate check-ins, driver sessions, the one-check-in-per-shipment
   * locks those held, driver accounts with their carrier memberships, and every
   * driver's biometrics — face templates in Rekognition and the images in S3.
   *
   * The work belongs to the driverAdmin lambda: Rekognition and S3 are not
   * reachable from AppSync, and the lambda already holds the credentials for
   * both. It refuses to run in prod and demands an active ADMIN row of its own,
   * so the button being disabled here is the convenience, not the control.
   */
  const resetGateCheckInData = async (_queryTerms, notifyInput) => {
    const notifyFn = notifyInput || notify;
    try {
      const response = await safeAPI.post(
        "transREST",
        "/driverAdmin/devResetGate",
        { body: {} },
      );
      const counts = response?.counts || {};
      console.log("Gate check-in reset:", counts);
      notifyFn(
        `Gate reset: ${counts.gateCheckIns || 0} check-ins, ` +
          `${counts.driverUsers || 0} drivers, ${counts.identities || 0} identities, ` +
          `${counts.faces || 0} faces, ${counts.s3Objects || 0} files`,
      );
    } catch (err) {
      logError("AdminDashboard:resetGateCheckInData", err, notifyFn);
    }
  };

  /**
   * Backfill for the gate status rename (CHECKED_IN -> ARRIVED, AT_DOCK ->
   * DOCKED, COMPLETED -> DEPARTED, checkedInAt -> arrivedAt). Rows written
   * before the rename carry none of the renamed attributes, so the two
   * arrivedAt-keyed GSIs leave them out and the Gate Queue never lists them;
   * COMPLETED rows are indexed but under a status nothing reads any more. The driverAdmin lambda rewrites
   * every such row directly in DynamoDB: AppSync can no longer read the legacy
   * attributes, and the resolver cannot remove them. Idempotent, so it is safe
   * to run again after a partial failure. Enabled in every environment - prod
   * is where the pre-rename rows are.
   *
   * One press is as many calls as the table needs: the endpoint runs against
   * its own clock and hands back the scan position (nextKey) when API
   * Gateway's 29-second cap is the next thing it would hit, so this resumes
   * from there and adds the counts up. The pass cap is a runaway guard - a
   * table that needs more presses than that says so rather than looping.
   */
  const backfillGateQueue = async (_queryTerms, notifyInput) => {
    const notifyFn = notifyInput || notify;
    const MAX_PASSES = 50;
    const total = {
      scanned: 0,
      needFix: 0,
      fixed: 0,
      failed: 0,
      skippedNoStamp: 0,
    };
    try {
      let startKey = null;
      let passes = 0;
      let done = false;
      while (!done && passes < MAX_PASSES) {
        // eslint-disable-next-line no-await-in-loop -- each pass resumes where the last stopped
        const response = await safeAPI.post(
          "transREST",
          "/driverAdmin/backfillGateArrived",
          { body: startKey ? { startKey } : {} },
        );
        const counts = response?.counts || {};
        Object.keys(total).forEach((key) => {
          total[key] += counts[key] || 0;
        });
        startKey = response?.nextKey || null;
        // An endpoint that reported neither done nor a cursor has nowhere to
        // resume from, so stop rather than re-scan the table from row zero.
        done = Boolean(response?.done) || !startKey;
        passes += 1;
      }
      console.log("Gate queue backfill:", { ...total, passes, done });
      notifyFn(
        `Gate queue backfill: ${total.scanned} rows scanned, ` +
          `${total.fixed} rewritten, ${total.failed} failed, ` +
          `${total.skippedNoStamp} skipped with no arrival stamp` +
          (done
            ? ""
            : ` - stopped after ${passes} passes, run it again to carry on`),
        total.failed || !done ? "warning" : "info",
      );
    } catch (err) {
      // Say how far it got before the failure: the run is resumable and
      // idempotent, so what matters is whether pressing again is worth it.
      console.log("Gate queue backfill (incomplete):", total);
      logError("AdminDashboard:backfillGateQueue", err, notifyFn);
    }
  };

  /**
   * Fills Carrier.dbaName from the FMCSA register for every carrier that has
   * a USDOT number and no trading name yet (see adminCarrierDbaBackfill). Runs
   * in the browser: the admin session already lists every carrier, calls the
   * IAM-private /carrierLookup/verify path, and updates carriers, so no lambda
   * or secret changes are needed. Idempotent; never overwrites a name already
   * set, so it is safe to run again after FMCSA was unavailable. Enabled in
   * every environment.
   */
  const backfillDba = async (_queryTerms, notifyInput) => {
    const notifyFn = notifyInput || notify;
    try {
      const dba = await backfillCarrierDba();
      console.log("Carrier dbaName backfill:", dba);
      notifyFn(
        `Carrier trading names: ${dba.candidates} of ${dba.scanned} carriers asked FMCSA, ` +
          `${dba.filled} filled, ${dba.noDba} with no trading name, ${dba.sameAsName} same as business name, ` +
          `${dba.notFound} not in the register, ${dba.unavailable} unavailable, ${dba.failed} failed`,
        dba.failed || dba.unavailable ? "warning" : "info",
      );
    } catch (err) {
      logError("AdminDashboard:backfillDba", err, notifyFn);
    }
  };

  const devDeleteEmptyOutbounds = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    debugger;

    try {
      let nextToken = true;
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 500,
      };
      const outboundMap = {};

      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(
            adminQueries.outboundShipmentByStatusDeleteMe,
            input,
          ),
        );
        const outboundList = result.data.outboundShipmentByStatus.items;
        console.log("outboundList", outboundList);
        nextToken = result.data.outboundShipmentByStatus.nextToken;
        let count = 0;

        for (const outbound of outboundList) {
          outboundMap[outbound.id] = outbound;
          // if (outbound.id === "05bd3ef9-6363-48fa-bd60-8b940147b906") debugger;
          if (
            (!outbound.shipmentLineItems ||
              outbound.shipmentLineItems?.items?.length === 0) &&
            outbound.status === "RELEASED"
          ) {
            console.log("empty outbound", outbound.id);
            const input = { id: outbound.id };
            count++;
            promiseList.push(
              API.graphql(
                graphqlOperation(mutations.deleteOutboundShipment, { input }),
              ),
            );
          }
        }

        console.log(
          "empty outbounds",
          count,
          "good outbounds",
          outboundList.length - count,
        );
        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);
        debugger;
        response.forEach((resp) => {
          if (resp.status === "rejected") console.log("REJECTED: ", resp);
        });
      }

      let promiseList = [];
      let result = await API.graphql(
        graphqlOperation(listIdQueries.listShipmentLineItemIds, {
          limit: 1000,
        }),
      );
      const lineItemList = result.data.listShipmentLineItems.items.filter(
        (itm) => itm.reloadYardId === queryTerms.reloadYardId,
      );
      console.log("lineItemList", lineItemList);
      let count = 0;

      result = await API.graphql(
        graphqlOperation(listIdQueries.listInventoryIds, { limit: 1000 }),
      );
      const inventoryList = result.data.listInventories.items.filter(
        (itm) => itm.reloadYardId === queryTerms.reloadYardId,
      );
      console.log("inventoryList", inventoryList);
      const inventoryMap = inventoryList.reduce((acc, obj) => {
        acc[obj.id] = obj;
        return acc;
      }, {});

      for (const lineItem of lineItemList) {
        if (!outboundMap[lineItem.outboundShipmentId]) {
          console.log(
            "zombie line item",
            lineItem.id,
            inventoryMap[lineItem.inventoryId],
          );
          count++;
          if (!inventoryMap[lineItem.inventoryId]) {
            const input = { id: lineItem.id };
            promiseList.push(
              API.graphql(
                graphqlOperation(mutations.deleteShipmentLineItem, { input }),
              ),
            );
          }
        }
      }

      console.log(
        "zombie line items",
        count,
        "good line items",
        lineItemList.length - count,
      );
      console.log("promiseList", promiseList.length);
      const response = await Promise.allSettled(promiseList);

      response.forEach((resp) => {
        if (resp.status === "rejected") console.log("REJECTED: ", resp);
      });
    } catch (err) {
      logError("AdminDashboard:deleteEmptyOutbounds", err, notify);
    }
    notify("All done");
  };

  const devDeleteEmptyInbounds = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    debugger;

    try {
      let nextToken = true;
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 500,
      };
      const inboundMap = {};

      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(
            adminQueries.inboundShipmentByReloadYardByAccount,
            input,
          ),
        );
        const inboundList =
          result.data.inboundShipmentByReloadYardByAccount.items;
        console.log("inboundList", inboundList);
        nextToken = result.data.inboundShipmentByReloadYardByAccount.nextToken;
        let count = 0;

        for (const inbound of inboundList) {
          inboundMap[inbound.id] = inbound;
          if (inbound.id === "f561a451-f3ab-4357-a6cf-f61947a03194") debugger;
          if (
            (!inbound.inventories ||
              inbound.inventories?.items?.length === 0) &&
            inbound.status === InboundStatus.RELEASED
          ) {
            console.log("empty inbound", inbound.id);
            const input = { id: inbound.id };
            count++;
            promiseList.push(
              API.graphql(
                graphqlOperation(mutations.deleteInboundShipment, { input }),
              ),
            );
          }
        }

        console.log(
          "empty inbounds",
          count,
          "good inbounds",
          inboundList.length - count,
        );
        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);
        debugger;
        response.forEach((resp) => {
          if (resp.status === "rejected") console.log("REJECTED: ", resp);
        });
      }
    } catch (err) {
      logError("AdminDashboard:deleteEmptyOutbounds", err, notify);
    }
    notify("All done");
  };

  const deleteLogs = async () => {
    console.log("Starting log deletion...");

    try {
      let nextToken = true;
      const limit = 250;

      while (nextToken) {
        const input = nextToken === true ? { limit } : { limit, nextToken };
        const result = await API.graphql(
          graphqlOperation(listIdQueries.listLogIds, input),
        );

        const logs = result?.data?.listLogs?.items || [];
        nextToken = result?.data?.listLogs?.nextToken;

        console.log(`Fetched ${logs.length} logs`);

        for (const log of logs) {
          if (!log?.id) continue;

          try {
            await API.graphql(
              graphqlOperation(mutations.deleteLog, { input: { id: log.id } }),
            );
          } catch (err) {
            console.error("Failed to delete log:", log.id, err);
          }
        }
      }

      console.log("All done");
      notify("All logs deleted.");
    } catch (err) {
      logError("AdminDashboard:deleteLogs", err, notify);
    }
  };

  const resetLineItems = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    // no longer needed - reset quanttiy from totalQtyToPcik
    debugger;

    try {
      const searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 1000,
      };
      let nextToken = true;
      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(
            adminQueries.outboundShipmentByStatusDeleteMe,
            input,
          ),
        );
        const outboundList = result.data.outboundShipmentByStatus.items; //.filter(itm => itm !== null);
        console.log("outboundList", outboundList);
        nextToken = result.data.outboundShipmentByStatus.nextToken;

        for (const outbound of outboundList) {
          const promiseList = [];
          if (outbound.shipmentLineItems?.items?.length > 0) {
            for (const lineItem of outbound.shipmentLineItems.items) {
              if (
                lineItem.totalQuantityToPick !== null &&
                lineItem.totalQuantityToPick !== undefined
              ) {
                const toUpdateLineItem = {
                  ...lineItem,
                  // quantity: lineItem.totalQuantityToPick,
                };
                delete toUpdateLineItem.inventory;
                delete toUpdateLineItem.inventoryItems;
                delete toUpdateLineItem.createdAt;
                delete toUpdateLineItem.updatedAt;

                promiseList.push(
                  API.graphql(
                    graphqlOperation(mutations.updateShipmentLineItem, {
                      input: toUpdateLineItem,
                    }),
                  ),
                );
              }
            }
          }
        }

        const response = await Promise.allSettled(promiseList);
        debugger;
        response.forEach((resp) => {
          if (resp.status === "rejected") console.log("REJECTED: ", resp);
        });
      }
    } catch (err) {
      logError("AdminDashboard:resetLineItems", err, notify);
    }
    notify("All done");
  };

  const fixAisleList = async (queryTerms) => {
    console.clear();
    console.log(queryTerms.reloadYardId);
    debugger;
    // reset inventory.aisleList from inventoryItems

    try {
      let searchInput = {
        filter: {
          reloadYardId: { eq: queryTerms.reloadYardId },
        },
        limit: 250,
      };

      let nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );
        const inventoryList = result.data.searchInventorys.items;
        nextToken = result.data.searchInventorys.nextToken;
        console.log("inventoryList", inventoryList);

        let promiseList = inventoryList.map((inventory) => {
          const toUpdateInventory = {
            id: inventory.id,
            aisleList: setInventoryAisleList(inventory),
          };
          // console.log("toUpdateInventory", toUpdateInventory);
          return API.graphql(
            graphqlOperation(inventoryMutations.updateAuditInventorie, {
              input: toUpdateInventory,
            }),
          );
        });

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);
        debugger;
        response.forEach((resp) => {
          if (resp.status === "rejected") console.log("REJECTED: ", resp);
        });
      }
    } catch (err) {
      logError("AdminDashboard:fixAisleList", err, notify);
    }
    notify("All done");
  };

  const devFixLocations = async (queryTerms) => {
    console.clear();
    try {
      let promiseList = [];

      let results = await API.graphql(
        graphqlOperation(queries.listAreas, { limit: 1000 }),
      );
      const areas = results.data.listAreas.items;
      console.log("areas:", areas);

      results = await API.graphql(
        graphqlOperation(queries.listZones, { limit: 1000 }),
      );
      const zones = results.data.listZones.items;
      console.log("zones:", zones);

      results = await API.graphql(
        graphqlOperation(queries.listAisles, { limit: 1000 }),
      );
      const aisles = results.data.listAisles.items;
      console.log("aisles:", aisles);

      results = await API.graphql(
        graphqlOperation(listIdQueries.listLocationIds, { limit: 1000 }),
      );
      let locations = results.data.listLocations.items;
      console.log("Locations:", locations);

      // for (const location of locations) {
      // 	let area = areas.find(itm => itm.id === location.areaId);
      // 	let zone = zones.find(itm => itm.id === location.zoneId);
      // 	let aisle = aisles.find(itm => itm.id === location.aisleId);

      // 	if (!area) {
      // 		const recordToDelete = {
      // 			...location
      // 		};
      // 		console.log("missing area:", recordToDelete, recordToDelete.id);
      // 		// promiseList.push(dataProvider.delete("locations", { data: recordToDelete }));
      // 	}
      // 	if (!zone) {
      // 		const recordToDelete = {
      // 			...location
      // 		};
      // 		console.log("missing zone:", recordToDelete, recordToDelete.id);
      // 		// promiseList.push(dataProvider.delete("locations", { data: recordToDelete }));
      // 	}
      // 	if (!aisle) {
      // 		const recordToDelete = {
      // 			...location
      // 		};
      // 		console.log("missing aisle:", recordToDelete, recordToDelete.id);
      // 		// promiseList.push(dataProvider.delete("locations", { data: recordToDelete }));
      // 	}
      // }

      results = await API.graphql(
        graphqlOperation(listIdQueries.listInventoryIds, { limit: 1000 }),
      );
      const inventories = results.data.listInventories.items.filter(
        (itm) => itm.locationId,
      );
      console.log("inventories:", inventories);
      const inventoryMap = inventories.reduce((acc, obj) => {
        acc[obj.id] = obj;
        return acc;
      }, {});

      results = await API.graphql(
        graphqlOperation(queries.listLocations, { limit: 1000 }),
      );
      locations = results.data.listLocations.items;
      console.log("Locations:", locations);

      for (const inv of inventories) {
        let inventory = inventoryMap[inv.id];

        let area = areas.find(
          (itm) =>
            itm.id === inventory.areaId &&
            itm.reloadYardId === inventory.reloadYardId,
        );
        let location = locations.find(
          (itm) =>
            itm.id === inventory.locationId &&
            itm.reloadYardId === inventory.reloadYardId,
        );

        if (!area) {
          console.log("missing area:", inventory, inventory.id);
          const matchingArea = areas.find(
            (area) => area.reloadYardId === inventory.reloadYardId,
          );

          if (matchingArea) {
            try {
              const record = await dataProvider.getOne("inventories", {
                id: inventory.id,
              });

              const input = {
                ...record.data,
                areaId: matchingArea.id,
              };

              await utils.saveInventory(input);
              inventoryMap[inv.id] = input;
              inventory = input;
            } catch (error) {
              console.error(
                "There was an error fetching the inventory:",
                error.errors,
              );
              if (error.errors.length > 1) {
                console.log("deleting inventory:", inventory, inventory.id);
                const input = { id: inventory.id };
                await API.graphql(
                  graphqlOperation(inventoryMutations.deleteInventorieMini, {
                    input,
                  }),
                );
              }
            }
          }
        }

        let zone = zones.find(
          (itm) =>
            itm.id === inventory.zoneId &&
            itm.reloadYardId === inventory.reloadYardId,
        );
        let matchingZone = null;

        if (!zone || zone.areaId !== inventory.areaId) {
          console.log("missing zone:", inventory, inventory.id);
          if (!location)
            matchingZone = zones.find(
              (itm) =>
                itm.reloadYardId === inventory.reloadYardId &&
                itm.areaId === inventory.areaId,
            );
          else matchingZone = location.zone;
        }
        //  else if () {
        // 	console.log("zone area mismatch:", inventory, inventory.id);
        // 	matchingZone = zones.find(zone => zone.reloadYardId === inventory.reloadYardId && zone.areaId === inventory.areaId);
        // }

        if (matchingZone !== null && matchingZone?.id) {
          try {
            const record = await dataProvider.getOne("inventories", {
              id: inventory.id,
            });

            const input = {
              ...record.data,
              areaId: matchingZone.areaId,
              zoneId: matchingZone.id,
            };

            console.log("updateing zone", input);
            await utils.saveInventory(input);
            inventoryMap[inv.id] = input;
            inventory = input;
          } catch (error) {
            console.error(
              "There was an error fetching the inventory:",
              error.errors,
            );
            if (error.errors.length > 1) {
              console.log("deleting inventory:", inventory, inventory.id);
              const input = { id: inventory.id };
              // await API.graphql(graphqlOperation(inventoryMutations.deleteInventorieMini, { input }));
            }
          }
        } else if (matchingZone !== null) console.log("no zone match found.");

        if (inventory.aisleId || inventory.locationId) {
          let aisle = aisles.find(
            (itm) =>
              itm.id === inventory.aisleId &&
              itm.reloadYardId === inventory.reloadYardId,
          );
          let matchingAisle = null;

          if (
            !aisle ||
            aisle.areaId !== inventory.areaId ||
            aisle.zoneId !== inventory.zoneId ||
            aisle.areaId !== inventory.areaId
          ) {
            debugger;
            console.log("missing aisle:", inventory, inventory.id);
            if (!location) {
              matchingAisle = aisles.find(
                (itm) =>
                  itm.reloadYardId === inventory.reloadYardId &&
                  itm.areaId === inventory.areaId &&
                  itm.zoneId === inventory.zoneId,
              );
            } else matchingAisle = location.aisle;
          }
          //  else if (aisle.areaId !== inventory.areaId || aisle.zoneId !== inventory.zoneId || aisle.areaId !== inventory.areaId) {
          // 	console.log("aisle zone area mismatch:", inventory, inventory.id);
          // 	matchingAisle = aisles.find(aisle => aisle.reloadYardId === inventory.reloadYardId && aisle.areaId === inventory.areaId && aisle.zoneId === inventory.zoneId);
          // }

          if (matchingAisle !== null && matchingAisle?.id) {
            try {
              const record = await dataProvider.getOne("inventories", {
                id: inventory.id,
              });

              const input = {
                ...record.data,
                areaId: matchingAisle.areaId,
                zoneId: matchingAisle.zoneId,
                aisleId: matchingAisle.id,
              };

              console.log("updateing aisle", input);
              await utils.saveInventory(input);
              inventoryMap[inv.id] = input;
              inventory = input;
            } catch (error) {
              console.error(
                "There was an error fetching the inventory:",
                error.errors,
              );
              if (error.errors.length > 1) {
                console.log("deleting inventory:", inventory, inventory.id);
                const input = { id: inventory.id };
                // await API.graphql(graphqlOperation(inventoryMutations.deleteInventorieMini, { input }));
              }
            }
          } else if (matchingZone !== null) {
            console.log("no aisle match found - delete inventory");
            const input = { id: inventory.id };
            // await API.graphql(graphqlOperation(inventoryMutations.deleteInventorieMini, { input }));
          }

          let matchingLocation = null;

          if (
            !location ||
            location.areaId !== inventory.areaId ||
            location.zoneId !== inventory.zoneId ||
            location.aisleId !== inventory.aisleId
          ) {
            debugger;
            console.log("missing location:", inventory, inventory.id);
            matchingLocation = locations.find(
              (itm) =>
                itm.reloadYardId === inventory.reloadYardId &&
                itm.areaId !== inventory.areaId &&
                itm.zoneId !== inventory.zoneId &&
                itm.aisleId !== inventory.aisleId,
            );
          }
          //  else if (location.areaId !== inventory.areaId || location.zoneId !== inventory.zoneId || location.aisleId !== inventory.aisleId) {
          // 	console.log("location aisle zone area mismatch:", inventory, inventory.id);
          // 	matchingLocation = locations.find(itm => itm.reloadYardId === inventory.reloadYardId && itm.areaId === inventory.areaId && itm.zoneId === inventory.zoneId && itm.aisleId === inventory.aisleId);
          // }

          if (matchingLocation !== null && matchingLocation?.id) {
            try {
              const record = await dataProvider.getOne("inventories", {
                id: inventory.id,
              });

              const input = {
                ...record.data,
                areaId: matchingLocation.areaId,
                zoneId: matchingLocation.zoneId,
                aisleId: matchingLocation.aisleId,
                locationId: matchingLocation.id,
                locationFullName: getLocationFullName(matchingLocation),
              };

              console.log("updateing location", input);
              await utils.saveInventory(input);
              inventoryMap[inv.id] = input;
              inventory = input;
            } catch (error) {
              console.error(
                "There was an error fetching the inventory:",
                error.errors,
              );
              if (error.errors.length > 1) {
                console.log("deleting inventory:", inventory, inventory.id);
                const input = { id: inventory.id };
                // await API.graphql(graphqlOperation(inventoryMutations.deleteInventorieMini, { input }));
              }
            }
          } else if (matchingLocation !== null) {
            console.log("no location match found - delete inventory");
            const input = { id: inventory.id };
            // await API.graphql(graphqlOperation(inventoryMutations.deleteInventorieMini, { input }));
          }
        }
      }

      results = await Promise.allSettled(promiseList);
      results.forEach((resp) => {
        if (resp.status === "rejected") console.log(resp.reason);
      });
      debugger;

      console.log("done");
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:devDeleteInvalidLocations", error, notify);
    }
  };

  const invoices_Backfill_Numbers = async (queryTerms) => {
    console.clear();
    try {
      let response = await dataProvider.getList("invoices", {
        filter: {},
        // filter: {reloadYardId: {eq: queryTerms.reloadYardId}},
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
      });
      const invoices = response.data;
      console.log("invoices:", invoices);

      let promiseList = [];
      invoices.forEach((record, idx) => {
        const invoiceToUpdate = {
          ...record,
          railCarNumber:
            record.inboundShipment?.railCarNumber ||
            record.outboundShipment?.railCarNumber,
          pickupNumber: record.outboundShipment?.pickupNumber,
        };
        delete invoiceToUpdate.account;
        delete invoiceToUpdate.lineItems;
        delete invoiceToUpdate.reloadYard;
        delete invoiceToUpdate.inboundShipment;
        delete invoiceToUpdate.outboundShipment;

        promiseList.push(
          dataProvider.update("invoices", { data: invoiceToUpdate }),
        );
      });

      const results = await Promise.allSettled(promiseList);
      debugger;
      results.forEach((resp) => {
        if (resp.status === "rejected") console.log("ERROR:", resp);
      });

      console.log("done");
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:invoices_Backfill_Numbers", error, notify);
    }
  };

  const run_Aisle_Location_Backfill = async (queryTerms) => {
    // convert UNSTRUCTURED to STRUCUTED.other + calc aisle.capacityUsed
    // UNSTRUCTURED Aisle add locationLoad.other, set Bays, Levels, positions, totalLocations = 1
    // UNSTRUCTURED Location add locationLoad.other, set Bay, Level, position = 1
    // add dryBulk, liquidBulk loadType
    console.clear();
    try {
      let locationList = [];
      const aisleMap = {};
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        limit: 1000,
      };
      let nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(adminQueries.locationBySortKey, input),
        );
        const locations = result.data.locationBySortKey.items;
        locationList = locationList.concat(locations);
        nextToken = result.data.locationBySortKey.nextToken;
      }
      console.log("locationList", locationList);

      debugger;
      let promiseList = [];
      locationList.forEach((location) => {
        if (
          location.type === LocationType.UNSTRUCTURED &&
          !location.locationLoad
        ) {
          const aisle = aisleMap[location.aisleId];
          if (aisle) {
            aisle.count = aisle.count + 1;
            aisle.totalCapacityUsed =
              aisle.totalCapacityUsed + location.capacityUsed;
          } else {
            aisleMap[location.aisleId] = location.aisle;
          }

          const locationToUpdate = {
            ...location,
            name: location.aisle.name,
            bay: 1,
            level: 1,
            position: 1,
            locationLoad: {
              other: { selected: true },
              pallet: { selected: false },
              box: { selected: false },
              container: { selected: false },
              superSack: { selected: false },
              dryBulk: { selected: false },
              liquidBulk: { selected: false },
            },
            // productId: ???
          };
          console.log("locationToUpdate", locationToUpdate);
          promiseList.push(
            utils.saveLocation(locationToUpdate),
            // dataProvider.update("locations", { data: locationToUpdate })
          );
        }
      });

      const aisleList = Object.values(aisleMap);
      aisleList.forEach((aisle) => {
        const totalLocations = aisle.count;
        const totalCapacityUsed = aisle.totalCapacityUsed;
        const capacityUsed = totalCapacityUsed / totalLocations;
        delete aisle.count;

        const aisleToUpdate = {
          ...aisle,
          totalLocations: totalLocations,
          capacityUsed: capacityUsed,
          bays: 1,
          levels: 1,
          positions: 1,
          locationLoad: {
            other: { selected: true },
            pallet: { selected: false },
            box: { selected: false },
            container: { selected: false },
            superSack: { selected: false },
            dryBulk: { selected: false },
            liquidBulk: { selected: false },
          },
        };
        console.log("aisleToUpdate", aisleToUpdate);
        promiseList.push(utils.saveAisle(aisleToUpdate));
      });
      const results = await Promise.allSettled(promiseList);
      results.forEach((resp) => {
        if (resp.status === "rejected") console.log(resp);
      });
      debugger;
      console.log("done");
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:run_Aisle_Location_Backfill", error, notify);
    }
  };

  const backfillLoadTypes = async () => {
    console.clear();
    console.log("Starting backfill for load types (liquidBulk/dryBulk)...");

    try {
      let nextLocationToken = null;
      const locationLimit = 200;
      const aisleUpdates = new Map();
      let totalLocationsUpdated = 0;
      let totalAislesUpdated = 0;
      let totalLocationErrors = 0;
      let totalAisleErrors = 0;

      do {
        const locResp = await API.graphql(
          graphqlOperation(adminQueries.listLocations, {
            limit: locationLimit,
            nextToken: nextLocationToken,
          }),
        );

        const locations = locResp?.data?.listLocations?.items || [];
        nextLocationToken = locResp?.data?.listLocations?.nextToken || null;

        const locationPromises = [];

        for (const location of locations) {
          if (
            location?.type !== LocationType.BULK &&
            location?.type !== LocationType.BULK_MOBILE &&
            !location?.locationLoad?.liquidBulk &&
            !location?.locationLoad?.dryBulk
          ) {
            const existingLoad = location.locationLoad || {};
            const updatedLoad = {
              ...existingLoad,
              liquidBulk: { selected: false, capacity: 0 },
              dryBulk: { selected: false, capacity: 0 },
            };

            const locationToUpdate = {
              ...location,
              locationLoad: updatedLoad,
            };
            locationPromises.push(utils.saveLocation(locationToUpdate));
          }

          // Stage parent aisle update once per aisle
          const aisle = location?.aisle;
          if (
            aisle?.id &&
            aisle?.type !== LocationType.BULK &&
            aisle?.type !== LocationType.BULK_MOBILE &&
            !aisle?.locationLoad?.liquidBulk &&
            !aisle?.locationLoad?.dryBulk &&
            !aisleUpdates.has(aisle.id)
          ) {
            const existingAisleLoad = aisle.locationLoad || {};
            const updatedAisleLoad = {
              ...existingAisleLoad,
              liquidBulk: { selected: false, capacity: 0 },
              dryBulk: { selected: false, capacity: 0 },
            };
            aisleUpdates.set(aisle.id, {
              ...aisle,
              locationLoad: updatedAisleLoad,
            });
          }
        }

        // ✅ Handle location updates safely
        if (locationPromises.length > 0) {
          const results = await Promise.allSettled(locationPromises);
          const failed = results.filter((r) => r.status === "rejected");
          const succeeded = results.length - failed.length;
          totalLocationsUpdated += succeeded;
          totalLocationErrors += failed.length;

          if (failed.length > 0) {
            console.warn(
              `⚠️ ${failed.length} location updates failed in this batch:`,
              failed.map((f) => f.reason),
            );
          }
        }
      } while (nextLocationToken);

      // ✅ Handle aisle updates safely
      if (aisleUpdates.size > 0) {
        const aislePromises = Array.from(aisleUpdates.values()).map((a) =>
          utils.saveAisle(a),
        );

        const results = await Promise.allSettled(aislePromises);
        const failed = results.filter((r) => r.status === "rejected");
        const succeeded = results.length - failed.length;
        totalAislesUpdated += succeeded;
        totalAisleErrors += failed.length;

        if (failed.length > 0) {
          console.warn(
            `⚠️ ${failed.length} aisle updates failed:`,
            failed.map((f) => f.reason),
          );
        }
      }

      const summary = `
  ✅ Backfill complete:
    • Locations updated: ${totalLocationsUpdated}
    • Aisles updated:    ${totalAislesUpdated}
    ⚠️ Location errors:  ${totalLocationErrors}
    ⚠️ Aisle errors:     ${totalAisleErrors}
      `.trim();

      console.log(summary);
      notify(summary);
    } catch (error) {
      logError("AdminDashboard:backfillLoadTypes", error, notify);
    }
  };

  const impoertHelpPages = async () => {
    try {
      console.clear();
      const res = await API.post("transREST", "/importHelpPages");
      notify(
        `Help Pages Imported, created ${res.numberRowsCreate} new help pages and updated ${res.numberRowsUpdate} help pages`,
      );
      console.log(`Created ${res.numberRowsCreate} new help pages`);
      console.log(`Updated ${res.numberRowsUpdate} help pages`);
    } catch (err) {
      logError("AdminDashboard:helpPagesMigration", err, notify);
    }
  };

  const importCommodities = async () => {
    try {
      console.clear();
      const res = await API.post("transREST", "/importCommodities");
      notify(
        `Commodities Imported, created ${res.numberRowsCreate} new commodities and updated ${res.numberRowsUpdate} commodities`,
      );
      console.log(`Created ${res.numberRowsCreate} commodities`);
      console.log(`Updated ${res.numberRowsUpdate} commodities`);
    } catch (err) {
      logError("AdminDashboard:commodityMigration", err, notify);
    }
  };

  const fixInventoryDateTime = async (queryTerms) => {
    console.clear();
    // fix receivedAt = ""
    // update query : comment out receivedAt
    debugger;

    try {
      // update query : comment out receivedAt
      let searchInput = {
        reloadYardId: queryTerms.reloadYardId,
        statusLotNumber: {
          beginsWith: { status: InventoryStatus.IN_TRANSIT },
        },
        limit: 250,
      };

      let nextToken = true;
      while (nextToken) {
        let promiseList = [];
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };

        // update query : comment out receivedAt
        const result = await API.graphql(
          graphqlOperation(queries.inventoryByStatusByLot, input),
        );
        let inventoryList = result.data.inventoryByStatusByLot.items;
        console.log("inventoryList", inventoryList);
        nextToken = result.data.inventoryByStatusByLot.nextToken;

        for (const inventory of inventoryList) {
          if (!inventory.receivedAt) {
            inventory.receivedAt = null;
            promiseList.push(utils.saveInventory(inventory));
          }
        }

        console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);

        response.some((resp) => {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp.reason);
          }
          return false;
        });
      }
    } catch (err) {
      logError("AdminDashboard:fixInventoryDatTime", err, notify);
    }

    notify("All done");
  };

  const fixProductLink = async (queryTerms) => {
    console.clear();
    debugger;

    const dryRun = true; // <-- set to false to actually write
    const limit = pLimit(25);

    const reloadYardId = queryTerms?.reloadYardId;
    if (!reloadYardId) {
      notify("reloadYardId is required", "warning");
      return;
    }

    let missingProductCount = 0;
    let updatedCount = 0;
    let failedCount = 0;
    let wouldUpdateCount = 0;

    // Track unique errors (by message + optional code/type)
    const uniqueErrorKeys = new Set();
    const logUniqueError = (err) => {
      // Try to extract something stable
      const code =
        err?.errors?.[0]?.errorType ||
        err?.errors?.[0]?.errorInfo?.errorCode ||
        err?.name ||
        err?.code ||
        "";
      const msg =
        err?.errors?.[0]?.message ||
        err?.message ||
        (typeof err === "string" ? err : JSON.stringify(err));

      const key = `${code}::${msg}`;
      if (uniqueErrorKeys.has(key)) return;
      uniqueErrorKeys.add(key);

      console.log("REJECTED:", { code, message: msg, raw: err });
    };

    try {
      // ----------------------------------------------------------
      // 1) Build productCode -> productId map
      // ----------------------------------------------------------
      const productMap = new Map();

      let nextToken = true;
      const productSearchInput = {
        limit: 100,
        filter: {
          reloadYardId: { eq: reloadYardId },
        },
      };

      while (nextToken) {
        const input =
          nextToken === true
            ? productSearchInput
            : { ...productSearchInput, nextToken };

        const productData = await API.graphql(
          graphqlOperation(listIdQueries.listProductIds, input),
        );

        const products = productData?.data?.listProducts?.items || [];
        nextToken = productData?.data?.listProducts?.nextToken || null;

        for (const p of products) {
          if (p?.productCode && p?.id) productMap.set(p.productCode, p.id);
        }
      }

      console.log("Loaded products:", productMap.size);

      // ----------------------------------------------------------
      // 2) Backfill Inventory.productId using Inventory.productCode
      // ----------------------------------------------------------
      nextToken = true;

      const searchInput = {
        filter: {
          reloadYardId: { eq: reloadYardId },
          productCode: { exists: true }, // keep consistent with your schema
          or: [
            { productId: { exists: false } },
            { productId: { eq: "" } },
            { productId: { lte: "0" } },
          ],
        },
        limit: 100,
      };

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { ...searchInput, nextToken };

        const inventoryData = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );

        nextToken = inventoryData?.data?.searchInventorys?.nextToken || null;
        const inventories = inventoryData?.data?.searchInventorys?.items || [];

        // Build updates for this page
        const updates = [];
        for (const inv of inventories) {
          const productCode = inv?.productCode;
          if (!productCode) continue;
          if (inv?.productId) continue;

          const productId = productMap.get(productCode);
          if (!productId) {
            missingProductCount++;
            continue;
          }

          updates.push({ ...inv, productId });
        }

        if (!updates.length) continue;

        if (dryRun) {
          wouldUpdateCount += updates.length;
          continue;
        }

        const results = await Promise.allSettled(
          updates.map((inv) =>
            limit(async () => {
              await utils.saveInventory(inv);
              updatedCount++;
            }),
          ),
        );

        results.forEach((r) => {
          if (r.status === "rejected") {
            failedCount++;
            logUniqueError(r.reason);
          }
        });
      }

      if (dryRun) {
        notify(
          `DRY RUN: Would update ${wouldUpdateCount}. Missing products: ${missingProductCount}.`,
        );
        console.log("DRY RUN summary:", {
          wouldUpdate: wouldUpdateCount,
          missingProducts: missingProductCount,
          uniqueErrors: uniqueErrorKeys.size,
        });
      } else {
        notify(
          `Done. Updated: ${updatedCount}. Missing products: ${missingProductCount}. Failed: ${failedCount}.`,
        );
        console.log("Run summary:", {
          updated: updatedCount,
          missingProducts: missingProductCount,
          failed: failedCount,
          uniqueErrors: uniqueErrorKeys.size,
        });
      }
    } catch (error) {
      notify("Error fixing product links", "error");
      logUniqueError(error);
    }

    notify("All done");
  };

  const fixNegativeInvQty = async () => {
    console.clear();
    // fix incorrect inv + invItm qtys
    debugger;
    let totalInv = 0;
    let totalPromises = 0;

    try {
      const searchInput = { limit: 100 };
      let nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(adminQueries.listInventories, input),
        );
        const inventoryList = result.data.listInventories.items;
        nextToken = result.data.listInventories.nextToken;
        // console.log("inventoryList", inventoryList.length);
        totalInv += inventoryList.length;

        // Initialize the promise list inside the loop to handle batches
        let promiseList = [];

        for (const inventory of inventoryList) {
          if (
            !inventory.hasException &&
            (inventory.status === "STORED" ||
              inventory.inboundShipment.status === "RELEASED") &&
            inventory.status !== "AGGREGATED" &&
            inventory.reloadYardId !== "1229f609-e391-4641-9c5c-53e73f4d0c6d" && // mdyard
            inventory.reloadYardId !== "3404db3b-775d-44a9-a445-2551f04a5eb6" // HDM
          ) {
            let totalShipped = 0;
            let totalCommitted = 0;

            // Flag to check if inventory needs updating
            let inventoryNeedsUpdate = false;

            // **Iterate over inventory.history to check for specific pattern**
            const historyNotes = inventory.history || [];
            for (const note of historyNotes) {
              const text = note.text || "";
              // Regular expression to match "Qty. OnHand Changed from * to *"
              const regex = /Qty\. OnHand Changed from\s*(.+)\s*to\s*(.+)/;
              const match = regex.exec(text);
              if (match && !inventory.hasException) {
                console.log("Add Exception", inventory.id, text);
                inventory.hasException = true;
                inventory.otherException = true;
                inventory.otherExceptionNote = text;
                inventoryNeedsUpdate = true;
              }
            }

            // Process ShipmentLineItems for the current inventory
            const shipmentLineItems = inventory.shipmentLineItems?.items || [];

            if (!inventory.hasException) {
              for (const shipmentLineItem of shipmentLineItems) {
                if (shipmentLineItem.status === "SHIPPED") {
                  totalShipped += shipmentLineItem.totalQuantityToPick || 0;
                } else {
                  totalCommitted += shipmentLineItem.totalQuantityToPick || 0;
                }
              }

              // Calculate expected quantities
              const expectedQuantityOnHand = trimDecimal(
                (inventory.quantityReceived || 0) - totalShipped,
              );
              const expectedQuantityCommitted = totalCommitted;

              // Update inventory.quantityOnHand if there's a discrepancy
              if (
                inventory.quantityOnHand !== expectedQuantityOnHand &&
                expectedQuantityOnHand >= 0
              ) {
                console.log(
                  "saveInventory onHand",
                  inventory.id,
                  inventory.quantityOnHand,
                  expectedQuantityOnHand,
                );
                inventory.quantityOnHand = expectedQuantityOnHand;
                if (inventory.quantityOnHand === 0) {
                  inventory.status = "SHIPPED";
                }
                inventoryNeedsUpdate = true;
              }

              // Update inventory.quantityCommitted if there's a discrepancy
              if (
                inventory.quantityCommitted !== expectedQuantityCommitted &&
                expectedQuantityCommitted >= 0
              ) {
                console.log(
                  "saveInventory committed",
                  inventory.id,
                  inventory.quantityCommitted,
                  expectedQuantityCommitted,
                );
                inventory.quantityCommitted = trimDecimal(
                  expectedQuantityCommitted,
                );
                inventoryNeedsUpdate = true;
              }
            }

            // If updates are needed, save the inventory
            if (inventoryNeedsUpdate) {
              totalPromises++;
              promiseList.push(utils.saveInventory(inventory));
            }

            // Process InventoryItems for the current inventory
            const inventoryItems = inventory.inventoryItems?.items || [];
            for (const inventoryItem of inventoryItems) {
              if (
                !inventoryItem.hasException &&
                inventoryItem.quantityCommitted !== null
              ) {
                let itemQuantityCommitted = 0;

                // For each ShipmentLineItem, find matching ShipmentLineItemInventoryItems
                for (const shipmentLineItem of shipmentLineItems) {
                  const sliInventoryItems =
                    shipmentLineItem.inventoryItems?.items || [];
                  for (const sliInvItem of sliInventoryItems) {
                    if (
                      sliInvItem.inventoryItemId === inventoryItem.id &&
                      sliInvItem.status !== "SHIPPED"
                    ) {
                      itemQuantityCommitted += sliInvItem.quantityToPick || 0;
                    }
                  }
                }

                // Update inventoryItem.quantityCommitted if there's a discrepancy
                if (
                  inventoryItem.quantityCommitted !== itemQuantityCommitted &&
                  itemQuantityCommitted >= 0
                ) {
                  console.log(
                    "saveInventoryItem",
                    inventoryItem.id,
                    inventoryItem.quantityCommitted,
                    itemQuantityCommitted,
                  );
                  inventoryItem.quantityCommitted = trimDecimal(
                    itemQuantityCommitted,
                  );
                  totalPromises++;
                  promiseList.push(utils.saveInventoryItem(inventoryItem));
                }
              }
            }
          }
        }

        // console.log("promiseList", promiseList.length);
        const response = await Promise.allSettled(promiseList);

        response.forEach((resp) => {
          if (resp.status === "rejected") {
            console.log("REJECTED: ", resp.reason);
          }
        });
      }

      console.log("totalInv", totalInv);
      console.log("totalPromises", totalPromises);
    } catch (err) {
      logError("AdminDashboard:fixInventoryDatTime", err, notify);
    }

    notify("All done");
  };

  const addBillingBusinessName = async () => {
    console.clear();
    debugger;

    try {
      const searchInput = { limit: 100 };
      let nextToken = true;

      while (nextToken) {
        // Prepare input for the GraphQL query
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };

        // Fetch a batch of acceounts
        let result = await API.graphql(
          graphqlOperation(queries.listAccounts, input),
        );
        const accountList = result.data.listAccounts.items;
        nextToken = result.data.listAccounts.nextToken;
        console.log("Fetched acceounts:", accountList.length);

        // Initialize the promise list and acceounts to update list for the current batch
        let promiseList = [];
        let accountsToUpdate = [];

        for (const account of accountList) {
          const updatedAccount = {
            id: account.id,
            billingAddress: {
              ...account.billingAddress,
              name: account.businessName,
            },
          };
          delete updatedAccount.createdAt;
          delete updatedAccount.updatedAt;

          // Push the mutation promise to the list
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateAccount, {
                input: updatedAccount,
              }),
            ),
          );
          accountsToUpdate.push(account); // Keep track of which acceounts corresponds to which promise
        }

        console.log("Accounts to Update:", promiseList.length);

        if (promiseList.length > 0) {
          // Execute all update mutations for the current batch
          const responses = await Promise.allSettled(promiseList);

          const failedUpdates = [];

          responses.forEach((resp, index) => {
            const account = accountsToUpdate[index];
            if (resp.status === "rejected") {
              console.error(
                `Failed to update account ${account.id}:`,
                resp.reason,
              );
              failedUpdates.push({
                accountId: account.id,
                reason: resp.reason,
              });
            } else {
              // Assuming the mutation returns the updated acceount in resp.value.data.updateAccount
              console.log(
                `Successfully updated account ${resp.value.data.updateAccount}`,
              );
            }
          });

          if (failedUpdates.length > 0) {
            console.warn("Some account updates failed:", failedUpdates);
            // Optionally, implement retry logic or notify administrators
            // Example: await retryFailedUpdates(failedUpdates);
          }
        }
      }

      console.log("All account processed.");
    } catch (err) {
      logError("AdminDashboard:addBillingBusinessName", err, notify);
    }

    notify("All done");
  };

  const addUserACL = async () => {
    console.clear();
    // Add OUTBOUND_SIGN ACL to eligible users
    debugger;

    try {
      const searchInput = { limit: 100 };
      let nextToken = true;

      while (nextToken) {
        // Prepare input for the GraphQL query
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };

        // Fetch a batch of users
        let result = await API.graphql(
          graphqlOperation(queries.listUsers, input),
        );
        const userList = result.data.listUsers.items;
        nextToken = result.data.listUsers.nextToken;
        console.log("Fetched Users:", userList.length);

        // Initialize the promise list and users to update list for the current batch
        let promiseList = [];
        let usersToUpdate = [];

        for (const user of userList) {
          const isEligibleType =
            user.type === UserType.SUPERVISOR || user.type === UserType.CREW;
          const hasNoOutboundSignACL =
            !Array.isArray(user.acl) ||
            !user.acl.includes(AclType.OUTBOUND_SIGN);
          const isActive = user.active === "true";

          if (isEligibleType && hasNoOutboundSignACL && isActive) {
            // Create a new user object with updated ACL
            const updatedUser = {
              ...user,
              acl: Array.isArray(user.acl)
                ? [...user.acl, AclType.OUTBOUND_SIGN]
                : [AclType.OUTBOUND_SIGN],
              // Include necessary fields for the mutation
              id: user.id,
              // If using optimistic locking with versions, include the 'version' field
              // version: user.version,
            };
            delete updatedUser.createdAt;
            delete updatedUser.updatedAt;

            // Push the mutation promise to the list
            promiseList.push(
              API.graphql(
                graphqlOperation(updateUserMini, { input: updatedUser }),
              ),
            );
            usersToUpdate.push(user); // Keep track of which user corresponds to which promise
          }
        }

        console.log("Users to Update:", promiseList.length);

        if (promiseList.length > 0) {
          // Execute all update mutations for the current batch
          const responses = await Promise.allSettled(promiseList);

          const failedUpdates = [];

          responses.forEach((resp, index) => {
            const user = usersToUpdate[index];
            if (resp.status === "rejected") {
              console.error(`Failed to update user ${user.id}:`, resp.reason);
              failedUpdates.push({
                userId: user.id,
                reason: resp.reason,
              });
            } else {
              // Assuming the mutation returns the updated user in resp.value.data.updateUser
              console.log(
                `Successfully updated user ${resp.value.data.updateUser}`,
              );
            }
          });

          if (failedUpdates.length > 0) {
            console.warn("Some user updates failed:", failedUpdates);
            // Optionally, implement retry logic or notify administrators
            // Example: await retryFailedUpdates(failedUpdates);
          }
        }
      }

      console.log("All users processed.");
    } catch (err) {
      logError("AdminDashboard:addUserACL", err, notify);
    }

    notify("All done");
  };

  const addTrackManagementACL = async () => {
    console.clear();
    console.log("Starting Track Management ACL backfill...");
    const limit = pLimit(10); // Senior practice: throttle concurrency to prevent throttling

    try {
      // 1. Update Users
      const userSearchInput = { limit: 100 };
      let userNextToken = true;
      let usersUpdated = 0;

      while (userNextToken) {
        const input =
          userNextToken === true
            ? userSearchInput
            : { nextToken: userNextToken, ...userSearchInput };

        let result = await API.graphql(
          graphqlOperation(queries.listUsers, input),
        );
        const userItems = result.data.listUsers.items;
        userNextToken = result.data.listUsers.nextToken;

        const userPromiseList = userItems
          .filter((user) => {
            const isEligibleType =
              user.type === UserType.SUPERVISOR ||
              user.type === UserType.CREW ||
              user.type === UserType.CUSTOMER;
            const hasNoTrackManagementACL =
              !Array.isArray(user.acl) ||
              !user.acl.includes(AclType.TRACK_MANAGEMENT);
            const isActive = user.active === "true";
            return isEligibleType && hasNoTrackManagementACL && isActive;
          })
          .map((user) =>
            limit(async () => {
              const updatedUser = {
                id: user.id,
                acl: Array.isArray(user.acl)
                  ? [...user.acl, AclType.TRACK_MANAGEMENT]
                  : [AclType.TRACK_MANAGEMENT],
              };
              // Only send what's necessary to avoid mutation errors
              await API.graphql(
                graphqlOperation(updateUserMini, { input: updatedUser }),
              );
              usersUpdated++;
            }),
          );

        if (userPromiseList.length > 0) {
          await Promise.allSettled(userPromiseList);
          console.log(`Updated ${usersUpdated} users so far...`);
        }
      }

      // 2. Update CustomerReloadYardAccounts
      const cryaSearchInput = { limit: 100 };
      let cryaNextToken = true;
      let cryaUpdated = 0;

      while (cryaNextToken) {
        const input =
          cryaNextToken === true
            ? cryaSearchInput
            : { nextToken: cryaNextToken, ...cryaSearchInput };

        let result = await API.graphql(
          graphqlOperation(queries.listCustomerReloadYardAccounts, input),
        );
        const cryaItems = result.data.listCustomerReloadYardAccounts.items;
        cryaNextToken = result.data.listCustomerReloadYardAccounts.nextToken;

        const cryaPromiseList = cryaItems
          .filter((crya) => {
            const hasNoTrackManagementACL =
              !Array.isArray(crya.acl) ||
              !crya.acl.includes(AclType.TRACK_MANAGEMENT);
            const isActive = crya.active === "true";
            return hasNoTrackManagementACL && isActive;
          })
          .map((crya) =>
            limit(async () => {
              const updatedCrya = {
                id: crya.id,
                acl: Array.isArray(crya.acl)
                  ? [...crya.acl, AclType.TRACK_MANAGEMENT]
                  : [AclType.TRACK_MANAGEMENT],
              };
              await API.graphql(
                graphqlOperation(updateCustomerReloadYardAccountMini, {
                  input: updatedCrya,
                }),
              );
              cryaUpdated++;
            }),
          );

        if (cryaPromiseList.length > 0) {
          await Promise.allSettled(cryaPromiseList);
          console.log(`Updated ${cryaUpdated} accounts so far...`);
        }
      }

      console.log("Track Management ACL backfill completed.");
      notify(
        `Successfully updated ${usersUpdated} users and ${cryaUpdated} accounts.`,
      );
    } catch (err) {
      logError("AdminDashboard:addTrackManagementACL", err, notify);
    }
  };

  /**
   * Grant the new GATE_MANAGEMENT permission to existing yard staff.
   *
   * The gate queue used to be reachable by every crew and supervisor — its
   * route and menu entry carried no permission check. Gating it behind
   * GATE_MANAGEMENT means anyone whose stored `acl` predates the permission
   * loses the page on deploy, because permissionList is built from that list
   * alone (App.js) and only MANAGER/ADMIN bypass it. Run this once per
   * environment BEFORE the gated build ships: the attribute is inert until
   * then, so an early run costs nothing while a late one is a lockout window.
   *
   * Users only — GATE_MANAGEMENT is not offered to customers, so
   * CustomerReloadYardAccount rows are left alone. Safe to re-run: users who
   * already carry the permission are skipped.
   */
  const addGateManagementACL = async () => {
    console.clear();
    console.log("Starting Gate Management ACL backfill...");
    const limit = pLimit(10);

    try {
      const userSearchInput = { limit: 100 };
      let userNextToken = true;
      let usersUpdated = 0;

      while (userNextToken) {
        const input =
          userNextToken === true
            ? userSearchInput
            : { nextToken: userNextToken, ...userSearchInput };

        const result = await API.graphql(
          graphqlOperation(queries.listUsers, input),
        );
        const userItems = result.data.listUsers.items;
        userNextToken = result.data.listUsers.nextToken;

        const userPromiseList = userItems
          .filter((user) => {
            // MANAGER and ADMIN are deliberately absent: isAuthorized
            // short-circuits for them, so writing the permission would only add
            // noise to their ACL.
            const isEligibleType =
              user.type === UserType.SUPERVISOR || user.type === UserType.CREW;
            const hasNoGateManagementACL =
              !Array.isArray(user.acl) ||
              !user.acl.includes(AclType.GATE_MANAGEMENT);
            const isActive = user.active === "true";
            return isEligibleType && hasNoGateManagementACL && isActive;
          })
          .map((user) =>
            limit(async () => {
              const updatedUser = {
                id: user.id,
                acl: Array.isArray(user.acl)
                  ? [...user.acl, AclType.GATE_MANAGEMENT]
                  : [AclType.GATE_MANAGEMENT],
              };
              // Only send what's necessary to avoid mutation errors
              await API.graphql(
                graphqlOperation(updateUserMini, { input: updatedUser }),
              );
              usersUpdated++;
            }),
          );

        if (userPromiseList.length > 0) {
          await Promise.allSettled(userPromiseList);
          console.log(`Updated ${usersUpdated} users so far...`);
        }
      }

      console.log("Gate Management ACL backfill completed.");
      notify(`Successfully updated ${usersUpdated} users.`);
    } catch (err) {
      logError("AdminDashboard:addGateManagementACL", err, notify);
    }
  };

  const runOrphanInventoryItemAudit = async () => {
    setOrphanAuditRunning(true);
    try {
      setOrphanAudit(await auditOrphanInventoryItems(reloadYards, notify));
    } finally {
      setOrphanAuditRunning(false);
    }
  };

  const runOrphanInventoryItemCleanup = async (queryTerms, notify) => {
    await cleanupOrphanInventoryItems(
      orphanAudit,
      queryTerms.reloadYardId,
      notify,
    );
    setOrphanAudit(null);
  };

  const orphansToDelete =
    orphanAudit?.orphans?.filter(
      (orphan) => orphan.action === OrphanAction.DELETE,
    ).length || 0;

  const listAllItems = async (query, rootKey, limit = 500) => {
    let nextToken = true;
    const items = [];

    while (nextToken) {
      const input = nextToken === true ? { limit } : { limit, nextToken };
      const result = await API.graphql(graphqlOperation(query, input));
      const pageItems = result.data?.[rootKey]?.items || [];
      items.push(...pageItems);
      nextToken = result.data?.[rootKey]?.nextToken;
    }

    return items;
  };

  const auditInventoriesMissingInbound = async () => {
    console.clear();
    debugger;

    try {
      const inboundList = await listAllItems(
        listIdQueries.listInboundShipmentIds,
        "listInboundShipments",
      );
      const inboundIds = new Set(inboundList.map((inbound) => inbound.id));

      const inventoryList = await listAllItems(
        listIdQueries.listInventoryIds,
        "listInventories",
      );
      let missingCount = 0;

      inventoryList.forEach((inventory) => {
        const inboundId = inventory.inboundShipmentId;
        if (!inboundId || !inboundIds.has(inboundId)) {
          missingCount++;
          console.log(
            "inventory:",
            inventory.id,
            "missing inbound:",
            inboundId,
          );
        }
      });

      console.log("missing inbound inventories:", missingCount);
    } catch (err) {
      logError("AdminDashboard:auditInventoriesMissingInbound", err, notify);
    }
    notify("All done");
  };

  const auditInventoryItemsMissingInventory = async () => {
    console.clear();
    debugger;

    try {
      const inventoryList = await listAllItems(
        listIdQueries.listInventoryIds,
        "listInventories",
      );
      const inventoryIds = new Set(inventoryList.map((inv) => inv.id));

      const inventoryItemList = await listAllItems(
        listIdQueries.listInventoryItemIds,
        "listInventoryItems",
      );
      let missingCount = 0;

      inventoryItemList.forEach((inventoryItem) => {
        const inventoryId = inventoryItem.inventoryId;
        if (!inventoryId || !inventoryIds.has(inventoryId)) {
          missingCount++;
          console.log(
            "inventoryItem:",
            inventoryItem.id,
            "missing inventory:",
            inventoryId,
          );
        }
      });

      console.log("inventoryItems missing inventory:", missingCount);
    } catch (err) {
      logError(
        "AdminDashboard:auditInventoryItemsMissingInventory",
        err,
        notify,
      );
    }
    notify("All done");
  };

  const auditShipmentsWithInvoiceIdNoServices = async () => {
    console.clear();
    debugger;

    let inboundScanned = 0;
    let outboundScanned = 0;
    let inboundMissingCount = 0;
    let outboundMissingCount = 0;
    let nextToken;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInboundShipmentsContainerDrop, {
            filter: {
              invoiceId: { attributeExists: true },
            },
            limit: 200,
            nextToken,
          }),
        );
        const page = result?.data?.listInboundShipments;
        const items = page?.items || [];
        inboundScanned += items.length;

        for (const inbound of items) {
          const hasInvoiceId = Boolean(inbound?.invoiceId);
          const hasServices =
            (inbound?.invoicedServices?.items?.length ?? 0) > 0;
          if (hasInvoiceId && !hasServices) {
            inboundMissingCount++;
            console.log(
              "inbound missing invoicedServices:",
              inbound.id,
              inbound.invoiceId,
              {
                accountId: inbound.accountId,
                accountName: inbound.accountName,
                reloadYardId: inbound.reloadYardId,
              },
            );
          }
        }

        nextToken = page?.nextToken;
      } while (nextToken);

      nextToken = null;
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listOutboundShipmentsSkipInvoice, {
            filter: {
              invoiceId: { attributeExists: true },
            },
            limit: 200,
            nextToken,
          }),
        );
        const page = result?.data?.listOutboundShipments;
        const items = page?.items || [];
        outboundScanned += items.length;

        for (const outbound of items) {
          const hasInvoiceId = Boolean(outbound?.invoiceId);
          const hasServices =
            (outbound?.invoicedServices?.items?.length ?? 0) > 0;
          if (hasInvoiceId && !hasServices) {
            outboundMissingCount++;
            console.log(
              "outbound missing invoicedServices:",
              outbound.id,
              outbound.invoiceId,
              {
                accountId: outbound.accountId,
                accountName: outbound.accountName,
                reloadYardId: outbound.reloadYardId,
              },
            );
          }
        }

        nextToken = page?.nextToken;
      } while (nextToken);

      console.log("Audit complete:", {
        inboundScanned,
        outboundScanned,
        inboundMissingCount,
        outboundMissingCount,
      });
    } catch (err) {
      logError(
        "AdminDashboard:auditShipmentsWithInvoiceIdNoServices",
        err,
        notify,
      );
    }

    notify("All done");
  };

  const isInvoiceIdMissing = (invoiceId) =>
    invoiceId == null ||
    invoiceId === undefined ||
    (typeof invoiceId === "string" && invoiceId.trim() === "");

  const unlinkInboundsWithInvoiceIdNoServices = async () => {
    console.clear();
    debugger;

    const dryRun = false;
    let inboundScanned = 0;
    let inboundUpdated = 0;
    let nextToken;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInboundShipmentsContainerDrop, {
            limit: 200,
            nextToken,
          }),
        );
        const page = result?.data?.listInboundShipments;
        const items = page?.items || [];
        inboundScanned += items.length;
        console.log("inboundScanned", inboundScanned);

        const promiseList = [];
        for (const inbound of items) {
          if (!isInvoiceIdMissing(inbound?.invoiceId)) continue;

          if (dryRun) {
            inboundUpdated++;
            if (inboundUpdated < 11)
              console.log(
                "dryRun: would set invoiceId to space:",
                inbound.id,
                inbound.invoiceId,
                {
                  accountId: inbound.accountId,
                  accountName: inbound.accountName,
                  reloadYardId: inbound.reloadYardId,
                },
              );
          } else {
            promiseList.push(
              API.graphql(
                graphqlOperation(inboundMutations.updateInboundShipmentMini, {
                  input: {
                    id: inbound.id,
                    invoiceId: " ",
                  },
                }),
              ),
            );
          }
        }
        if (promiseList.length) {
          await Promise.all(promiseList);
          inboundUpdated += promiseList.length;
        }

        nextToken = page?.nextToken;
      } while (nextToken);

      console.log("Unlink complete:", {
        dryRun,
        inboundScanned,
        inboundUpdated,
      });
    } catch (err) {
      logError(
        "AdminDashboard:unlinkInboundsWithInvoiceIdNoServices",
        err,
        notify,
      );
    }

    notify("All done");
  };

  const unlinkOutboundsWithInvoiceIdNoServices = async () => {
    console.clear();
    debugger;

    const dryRun = false;
    let outboundScanned = 0;
    let outboundUpdated = 0;
    let nextToken;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listOutboundShipmentsSkipInvoice, {
            limit: 200,
            nextToken,
          }),
        );
        const page = result?.data?.listOutboundShipments;
        const items = page?.items || [];
        outboundScanned += items.length;
        console.log("outboundScanned", outboundScanned);

        const promiseList = [];
        for (const outbound of items) {
          if (!isInvoiceIdMissing(outbound?.invoiceId)) continue;

          if (dryRun) {
            outboundUpdated++;
            if (outboundUpdated < 11)
              console.log(
                "dryRun: would set invoiceId to space:",
                outbound.id,
                outbound.invoiceId,
                {
                  accountId: outbound.accountId,
                  accountName: outbound.accountName,
                  reloadYardId: outbound.reloadYardId,
                },
              );
          } else {
            promiseList.push(
              API.graphql(
                graphqlOperation(outboundMutations.updateOutboundShipmentMini, {
                  input: {
                    id: outbound.id,
                    invoiceId: " ",
                  },
                }),
              ),
            );
          }
        }
        if (promiseList.length) {
          await Promise.all(promiseList);
          outboundUpdated += promiseList.length;
        }

        nextToken = page?.nextToken;
      } while (nextToken);

      console.log("Unlink complete:", {
        dryRun,
        outboundScanned,
        outboundUpdated,
      });
    } catch (err) {
      logError(
        "AdminDashboard:unlinkOutboundsWithInvoiceIdNoServices",
        err,
        notify,
      );
    }

    notify("All done");
  };

  const auditShipmentLineItemsMissingInventory = async () => {
    console.clear();
    debugger;

    try {
      const inventoryList = await listAllItems(
        listIdQueries.listInventoryIds,
        "listInventories",
      );
      const inventoryIds = new Set(inventoryList.map((inv) => inv.id));

      const lineItemList = await listAllItems(
        listIdQueries.listShipmentLineItemIds,
        "listShipmentLineItems",
      );
      let missingCount = 0;

      lineItemList.forEach((lineItem) => {
        const inventoryId = lineItem.inventoryId;
        if (!inventoryId || !inventoryIds.has(inventoryId)) {
          missingCount++;
          console.log(
            "shipmentLineItem:",
            lineItem.id,
            "missing inventory:",
            inventoryId,
          );
        }
      });

      console.log("shipmentLineItems missing inventory:", missingCount);
    } catch (err) {
      logError(
        "AdminDashboard:auditShipmentLineItemsMissingInventory",
        err,
        notify,
      );
    }
    notify("All done");
  };

  const auditShipmentLineItemsMissingOutbound = async () => {
    console.clear();
    debugger;

    try {
      const outboundList = await listAllItems(
        listIdQueries.listOutboundShipmentIds,
        "listOutboundShipments",
      );
      const outboundIds = new Set(outboundList.map((outbound) => outbound.id));

      const lineItemList = await listAllItems(
        listIdQueries.listShipmentLineItemIds,
        "listShipmentLineItems",
      );
      let missingCount = 0;

      lineItemList.forEach((lineItem) => {
        const outboundId = lineItem.outboundShipmentId;
        if (!outboundId || !outboundIds.has(outboundId)) {
          missingCount++;
          console.log(
            "shipmentLineItem:",
            lineItem.id,
            "missing outbound:",
            outboundId,
          );
        }
      });

      console.log("shipmentLineItems missing outbound:", missingCount);
    } catch (err) {
      logError(
        "AdminDashboard:auditShipmentLineItemsMissingOutbound",
        err,
        notify,
      );
    }
    notify("All done");
  };

  const auditShipmentLineItemInventoryItemsMissingRefs = async () => {
    console.clear();
    debugger;

    try {
      const lineItemList = await listAllItems(
        listIdQueries.listShipmentLineItemIds,
        "listShipmentLineItems",
      );
      const lineItemIds = new Set(lineItemList.map((lineItem) => lineItem.id));

      const inventoryItemList = await listAllItems(
        listIdQueries.listInventoryItemIds,
        "listInventoryItems",
      );
      const inventoryItemIds = new Set(
        inventoryItemList.map((inventoryItem) => inventoryItem.id),
      );

      const linkList = await listAllItems(
        listIdQueries.listShipmentLineItemInventoryItemIds,
        "listShipmentLineItemInventoryItems",
      );
      let missingLineItemCount = 0;
      let missingInventoryItemCount = 0;

      linkList.forEach((link) => {
        if (
          !link.shipmentLineItemId ||
          !lineItemIds.has(link.shipmentLineItemId)
        ) {
          missingLineItemCount++;
          console.log(
            "shipmentLineItemInventoryItem:",
            link.id,
            "missing shipmentLineItem:",
            link.shipmentLineItemId,
          );
        }
        if (
          !link.inventoryItemId ||
          !inventoryItemIds.has(link.inventoryItemId)
        ) {
          missingInventoryItemCount++;
          console.log(
            "shipmentLineItemInventoryItem:",
            link.id,
            "missing inventoryItem:",
            link.inventoryItemId,
          );
        }
      });

      console.log(
        "lineItemInventoryItems missing lineItem:",
        missingLineItemCount,
      );
      console.log(
        "lineItemInventoryItems missing inventoryItem:",
        missingInventoryItemCount,
      );
    } catch (err) {
      logError(
        "AdminDashboard:auditShipmentLineItemInventoryItemsMissingRefs",
        err,
        notify,
      );
    }
    notify("All done");
  };

  const auditChargeRulesMissingRefs = async () => {
    console.clear();
    debugger;

    try {
      const accountList = await listAllItems(
        listIdQueries.listAccountIds,
        "listAccounts",
      );
      const accountIds = new Set(accountList.map((account) => account.id));

      const serviceList = await listAllItems(
        queries.listServices,
        "listServices",
      );
      const serviceIds = new Set(serviceList.map((service) => service.id));

      const chargeRules = await listAllItems(
        queries.listChargeRules,
        "listChargeRules",
      );
      let missingAccountCount = 0;
      let missingServiceCount = 0;

      chargeRules.forEach((rule) => {
        if (!rule.accountId || !accountIds.has(rule.accountId)) {
          missingAccountCount++;
          console.log(
            "chargeRule:",
            rule.id,
            "missing account:",
            rule.accountId,
          );
        }
        if (!rule.serviceId || !serviceIds.has(rule.serviceId)) {
          missingServiceCount++;
          console.log(
            "chargeRule:",
            rule.id,
            "missing service:",
            rule.serviceId,
          );
        }
      });

      console.log("chargeRules missing account:", missingAccountCount);
      console.log("chargeRules missing service:", missingServiceCount);
    } catch (err) {
      logError("AdminDashboard:auditChargeRulesMissingRefs", err, notify);
    }
    notify("All done");
  };

  const auditInvoicesMissingAccount = async () => {
    console.clear();
    debugger;

    try {
      const accountList = await listAllItems(
        listIdQueries.listAccountIds,
        "listAccounts",
      );
      const accountIds = new Set(accountList.map((account) => account.id));

      const invoiceList = await listAllItems(
        queries.listInvoices,
        "listInvoices",
      );
      let missingCount = 0;

      invoiceList.forEach((invoice) => {
        if (!invoice.accountId || !accountIds.has(invoice.accountId)) {
          missingCount++;
          console.log(
            "invoice:",
            invoice.id,
            "missing account:",
            invoice.accountId,
          );
        }
      });

      console.log("invoices missing account:", missingCount);
    } catch (err) {
      logError("AdminDashboard:auditInvoicesMissingAccount", err, notify);
    }
    notify("All done");
  };

  const auditInvoiceServicesMissingInvoice = async () => {
    console.clear();
    debugger;

    try {
      const invoiceList = await listAllItems(
        listIdQueries.listInvoiceIds,
        "listInvoices",
      );
      const invoiceIds = new Set(invoiceList.map((invoice) => invoice.id));

      const invoiceServiceList = await listAllItems(
        queries.listInvoiceServices,
        "listInvoiceServices",
      );
      let missingCount = 0;

      invoiceServiceList.forEach((invoiceService) => {
        if (
          !invoiceService.invoiceId ||
          !invoiceIds.has(invoiceService.invoiceId)
        ) {
          missingCount++;
          console.log(
            "invoiceService:",
            invoiceService.id,
            "missing invoice:",
            invoiceService.invoiceId,
          );
        }
      });

      console.log("invoiceServices missing invoice:", missingCount);
    } catch (err) {
      logError(
        "AdminDashboard:auditInvoiceServicesMissingInvoice",
        err,
        notify,
      );
    }
    notify("All done");
  };

  const auditServicesMissingAccount = async () => {
    console.clear();
    debugger;

    try {
      const accountList = await listAllItems(
        listIdQueries.listAccountIds,
        "listAccounts",
      );
      const accountIds = new Set(accountList.map((account) => account.id));

      const serviceList = await listAllItems(
        queries.listServices,
        "listServices",
      );
      let missingCount = 0;

      serviceList.forEach((service) => {
        const isYardService = service.accountId === service.reloadYardId;
        if (isYardService) return;

        if (!service.accountId || !accountIds.has(service.accountId)) {
          missingCount++;
          console.log(
            "service:",
            service.id,
            "missing account:",
            service.accountId,
          );
        }
      });

      console.log("services missing account:", missingCount);
    } catch (err) {
      logError("AdminDashboard:auditServicesMissingAccount", err, notify);
    }
    notify("All done");
  };

  const fixProductCode = async () => {
    console.clear();

    try {
      const performUpdates = false; // Flag to control whether updates should be performed
      const accountList = [];
      const productList = [];
      const productsToUpdate = []; // Products needing product code updates only
      const productsToMerge = []; // Products requiring merging
      const alteredCodes = new Map(); // Map to track base product codes by accountId
      const atsYardId = "18567be7-5e38-4a33-b947-f380620326c1";

      // Stage 1: Fetch Accounts
      console.log("Fetching accounts...");
      let searchInput = { limit: 500 };
      let nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(queries.listAccounts, input),
        );

        const filteredAccounts = result.data.listAccounts.items.filter(
          (itm) => itm.reloadYardId === atsYardId,
        );
        accountList.push(...filteredAccounts);
        nextToken = result.data.listAccounts.nextToken;
      }
      console.log("Accounts fetched:", accountList.length);

      // Stage 2: Fetch and Evaluate Products
      console.log("Fetching and evaluating products...");
      searchInput = { limit: 500 };
      nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(adminQueries.listProducts, input),
        );
        console.log("products fetched:", result.data.listProducts.items.length);

        const filteredProducts = result.data.listProducts.items.filter(
          (itm) => itm.reloadYardId === atsYardId && itm.active === "true",
        );
        productList.push(...filteredProducts);
        nextToken = result.data.listProducts.nextToken;
        console.log("filtered products", filteredProducts.length);

        for (const product of filteredProducts) {
          const { id, active, productCode, name, accountId, customFields } =
            product;
          const accountName = accountList.find(
            (itm) => itm.id === accountId,
          )?.businessName;

          const match = productCode.match(/^(.*?)(?=-[a-zA-Z]+$)/);
          const baseProductCode = match ? match[1] : null;

          // Ensure alteredCodes map is account-specific
          if (!alteredCodes.has(accountId)) {
            alteredCodes.set(accountId, new Map());
          }
          const accountAlteredCodes = alteredCodes.get(accountId);

          if (baseProductCode) {
            if (accountAlteredCodes.has(baseProductCode)) {
              // A duplicate would be created within the same account
              const baseProductId = accountAlteredCodes.get(baseProductCode);
              // console.log(
              //   `Merge detected: ${id} ${productCode} to ${baseProductId} ${baseProductCode} in ${accountName}`
              // );
              // console.log("customFields", customFields);
              productsToMerge.push({
                accountName,
                accountId,
                id,
                active,
                productCode,
                baseProductId, // new product
                baseProductCode, // new product
                name,
                customFields: customFields?.length > 0 ? customFields[0] : null,
              });
            } else {
              // Track the base product code and add to update list
              accountAlteredCodes.set(baseProductCode, id);
              productsToUpdate.push({
                accountName,
                accountId,
                id,
                active,
                productCode,
                baseProductId: id,
                baseProductCode,
                name,
                customFields: customFields?.length > 0 ? customFields[0] : null,
              });
            }
          } else {
            // No suffix, no updates needed
            accountAlteredCodes.set(productCode, id);
          }
        }
      }

      console.log("Products evaluated:");

      // Display alteredCodes in a legible format
      // console.log("Altered Codes Map:");
      // alteredCodes.forEach((codesMap, accountId) => {
      //   console.log(`Account ID: ${accountId}`);
      //   console.table(
      //     Array.from(codesMap.entries()).map(
      //       ([baseProductCode, productId]) => ({
      //         baseProductCode,
      //         productId,
      //       })
      //     )
      //   );
      // });

      console.log(
        "Products to update (no merge required):",
        productsToUpdate.length,
      );
      console.log("Products to merge:", productsToMerge.length);

      // Stage 3: Display Duplicates and Prepare Updates
      if (productsToMerge.length > 0) {
        console.log("Products requiring merging:");
        console.table(productsToMerge);
      }
      if (productsToUpdate.length > 0) {
        console.log("Products requiring code updates:");
        console.table(productsToUpdate);
      }

      if (!performUpdates) {
        console.log(
          "Updates skipped. Set `performUpdates = true` to enable updates.",
        );
        return;
      }

      // Stage 4: Execute Updates
      console.log("Executing updates...");

      // Step 1: Update product codes for products without merging
      for (const product of productsToUpdate) {
        await updateInventories(
          product.id, // old product
          product.productCode,
          product.id, // new product
          product.baseProductCode,
        );

        // update the old product
        await updateProductCode(product);
      }

      console.log("# # # # # # # # # # # # # # # # # # # #");
      console.log("...");
      console.log("Executing merges...");
      console.log("...");
      console.log("# # # # # # # # # # # # # # # # # # # #");

      // Step 2: Handle merges for duplicate products
      for (const product of productsToMerge) {
        const { id, productCode, baseProductId, baseProductCode } = product;

        // Link inventories to the new product
        await updateInventories(
          id, // old product
          productCode,
          baseProductId, // new product
          baseProductCode,
        );

        // update the new product
        await updateProductCode(product);

        // Delete the duplicate old product
        await deleteProduct(
          id, // old product
          productCode,
        );
      }

      console.log("Products and inventories updated successfully.");
      notify("All done");
    } catch (error) {
      logError("AdminDashboard:dataIntegrityAudit", error, notify);
    }
  };

  // Helper function to update inventories linked to a product
  const updateInventories = async (
    oldProductId,
    oldProductCode,
    newProductId,
    newProductCode,
  ) => {
    try {
      // console.log(
      //   "updateInventories:",
      //   oldProductId,
      //   oldProductCode,
      //   newProductId,
      //   newProductCode
      // );

      const searchInput = {
        filter: { productId: { eq: oldProductId } },
        limit: 250,
      };
      let nextToken = true;

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );
        const inventoryList = result.data.searchInventorys.items;
        nextToken = result.data.searchInventorys.nextToken;

        console.log("inventoryList:", inventoryList);

        let updatePromises = [];
        updatePromises = inventoryList.map((inventory) => {
          console.log(
            `Update Inventory from old product ID ${inventory.productId}, ${inventory.productCode} to new ${newProductId} ${newProductCode}`,
          );

          inventory.productId = newProductId;
          inventory.productCode = newProductCode;
          return utils.saveInventory(inventory); // Save updated inventory
        });

        // inventoryList.forEach((inventory) => {
        //   console.log(
        //     `Update Inventory from old product ID ${inventory.productId}, ${inventory.productCode} to new ${newProductId} ${newProductCode}`
        //   );
        //   inventory.productId = newProductId;
        //   inventory.productCode = newProductCode;
        // });

        await Promise.allSettled(updatePromises);
      }
    } catch (error) {
      console.error(
        `Error updating inventories for product ID ${oldProductId}`,
        error,
      );
    }
  };

  // Helper function to delete a product
  const deleteProduct = async (productId, oldProductCode) => {
    try {
      await API.graphql(
        graphqlOperation(mutations.deleteProduct, {
          input: { id: productId },
        }),
      );
      console.log(
        `Deleted old product with ID ${productId} ${oldProductCode}.`,
      );
    } catch (error) {
      console.error(`Error deleting product with ID ${productId}`, error);
    }
  };

  // Helper function to update a product code
  const updateProductCode = async (product) => {
    try {
      const input = {
        id: product.baseProductId, // new product
        active: product.active,
        accountId: product.accountId,
        productCode: product.baseProductCode, // new product
        name: product.name,
      };
      if (product.customFields?.length > 0) {
        input.customFields = product.customFields;
      }
      await API.graphql(
        graphqlOperation(mutations.updateProduct, {
          input,
        }),
      );
      console.log(
        `Updated product ${input.id} ${product.productCode} to new code: ${input.productCode}`,
        input,
      );
    } catch (error) {
      console.error(
        `Error updating product code for product ID ${product.id}`,
        error,
      );
    }
  };

  const backfillTimeslotIds = async () => {
    console.clear();
    console.log("Starting backfill for missing timeslotIds...");

    const startTime = performance.now();
    let updatedCount = 0;

    try {
      const searchInput = {
        limit: 100, // Adjust limit as needed
      };

      let nextToken = true;
      while (nextToken) {
        let updatePromises = [];

        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(queries.listTimeslots, input),
        );

        const timeslotList = result.data.listTimeslots.items;
        console.log("Fetched timeslots:", timeslotList.length);

        nextToken = result.data.listTimeslots.nextToken;

        for (const timeslot of timeslotList) {
          console.log("timeslot:", timeslot);
          if (!timeslot.outboundId) continue; // Skip if no outboundId

          console.log(
            `Updating outboundShipment ${timeslot.outboundId} with timeslotId ${timeslot.id}`,
          );

          const updatePromise = API.graphql(
            graphqlOperation(mutations.updateOutboundShipment, {
              input: { id: timeslot.outboundId, timeslotId: timeslot.id },
            }),
          );
          updatePromises.push(updatePromise);
          updatedCount += 1;
        }

        console.log("Update promises:", updatePromises.length);
        await Promise.allSettled(updatePromises);
      }
    } catch (err) {
      logError("backfillTimeslotIds", err, notify);
    }

    const endTime = performance.now();
    console.log(
      `Backfill completed. Updated ${updatedCount} outbound shipments.`,
    );
    utils.elapsedTime(startTime, endTime, updatedCount, 2000);
    notify(`Backfill complete: ${updatedCount} outbound shipments updated.`);
  };

  const fixProductCounts = async () => {
    // Reconcile one product's stored totals. Returns "updated" | "skipped".
    const reconcileProduct = async (product, reloadYardId) => {
      let allInventories = [];
      const inventoryQuery = {
        reloadYardId,
        productIdStatus: {
          beginsWith: {
            productId: product.id,
            status: InventoryStatus.STORED,
          },
        },
        limit: MAX_INVENTORY_QUERY_LIMIT,
      };

      let nextInvToken = true;
      while (nextInvToken) {
        const inventoryInput =
          nextInvToken === true
            ? inventoryQuery
            : { nextToken: nextInvToken, ...inventoryQuery };

        const result = await API.graphql(
          graphqlOperation(queries.inventoryByProductByStatus, inventoryInput),
        );

        const items = result?.data?.inventoryByProductByStatus?.items || [];
        if (items.length) allInventories = allInventories.concat(items);
        nextInvToken =
          result?.data?.inventoryByProductByStatus?.nextToken || null;
      }

      // Always compute totals, even when there are 0 STORED lines
      // Trim to 3 dp to match handleInventoryChange Lambda's stored totals
      // so reconciliation doesn't churn on float summation differences.
      const totalQuantityOnHand = trimDecimal(
        allInventories.reduce(
          (sum, inv) => sum + (Number(inv.quantityOnHand) || 0),
          0,
        ),
        3,
      );
      const totalQuantityCommitted = trimDecimal(
        allInventories.reduce(
          (sum, inv) => sum + (Number(inv.quantityCommitted) || 0),
          0,
        ),
        3,
      );
      // Match handleInventoryChange Lambda: compare trimmed available qty.
      const lowInventoryAlert = computeLowInventoryAlert(
        { totalQuantityOnHand, totalQuantityCommitted },
        product.lowInventoryThreshold,
      );

      // Only update when something actually differs.
      if (
        // Strict compare with no `|| 0` fallback so unset (null/undefined)
        // totals on existing products are treated as differing from a
        // computed 0 and get backfilled to a numeric value.
        product.totalQuantityOnHand !== totalQuantityOnHand ||
        product.totalQuantityCommitted !== totalQuantityCommitted ||
        Boolean(product.lowInventoryAlert) !== lowInventoryAlert
      ) {
        await API.graphql(
          graphqlOperation(mutations.updateProduct, {
            input: {
              id: product.id,
              totalQuantityOnHand, // will be 0 if no STORED lines
              totalQuantityCommitted, // will be 0 if no STORED lines
              lowInventoryAlert,
              status: InventoryStatus.STORED,
            },
          }),
        );
        return "updated";
      }

      return "skipped";
    };

    try {
      if (!reloadYards.length) {
        notify("No reload yards loaded yet", "warning");
        return;
      }

      // Fetch every product once (paginated), then reconcile yard by yard.
      // Avoids re-scanning the full product list for each reload yard.
      let allProducts = [];
      let nextToken = true;
      const searchInput = { limit: 1000 };

      do {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const productsData = await API.graphql(
          graphqlOperation(queries.listProducts, input),
        );

        nextToken = productsData.data.listProducts.nextToken;
        allProducts = allProducts.concat(productsData.data.listProducts.items);
      } while (nextToken);

      // Fan out per-product work in parallel batches (like backfillProductFields'
      // page-at-a-time Promise.allSettled) instead of awaiting one product at a
      // time. Bounded because each task is heavier: an inventory query + an
      // optional update mutation.
      const PRODUCT_BATCH_SIZE = 50;
      let totalUpdated = 0;
      let totalSkipped = 0;
      let totalFailed = 0;

      for (const yard of reloadYards) {
        const reloadYardId = yard.id;
        const products = allProducts.filter(
          (itm) => itm.reloadYardId === reloadYardId,
        );
        console.log(
          `Reload yard ${yard.name || reloadYardId}: ${products.length} products`,
        );

        for (let i = 0; i < products.length; i += PRODUCT_BATCH_SIZE) {
          const batch = products.slice(i, i + PRODUCT_BATCH_SIZE);
          const responses = await Promise.allSettled(
            batch.map((product) => reconcileProduct(product, reloadYardId)),
          );

          for (let j = 0; j < responses.length; j++) {
            const resp = responses[j];
            if (resp.status === "fulfilled") {
              if (resp.value === "updated") totalUpdated++;
              else totalSkipped++;
            } else {
              totalFailed++;
              console.error(
                `Failed to reconcile product ${batch[j].id}:`,
                resp.reason,
              );
            }
          }
        }
      }

      console.log("Total Products Updated:", totalUpdated);
      console.log("Products Skipped (no change):", totalSkipped);
      console.log("Failed Products:", totalFailed);
      notify("All products updated successfully!");
    } catch (error) {
      notify("Error fixing product counts", "error");
      console.error("Error fixing product counts:", error);
    }
  };

  const fixProductStatus = async () => {
    console.clear();
    try {
      let nextToken = true;
      let searchInput = { limit: 1000 };

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const productsData = await API.graphql(
          graphqlOperation(queries.listProducts, input),
        );

        nextToken = productsData.data.listProducts.nextToken;
        const products = productsData.data.listProducts.items;
        console.log("Products page:", products.length);

        const promiseList = products.map((product) =>
          API.graphql(
            graphqlOperation(mutations.updateProduct, {
              input: { id: product.id, status: InventoryStatus.STORED },
            }),
          ),
        );

        const results = await Promise.allSettled(promiseList);
        results.forEach((resp) => {
          if (resp.status === "rejected") console.log("ERROR:", resp.reason);
        });
      }

      notify("All products set to STORED");
    } catch (error) {
      logError("AdminDashboard:fixProductStatus", error, notify);
    }
  };

  const addPcsQuantity = async () => {
    console.clear();

    try {
      let nextToken = true;
      const searchInput = {
        filter: {
          inventoryUOM: { eq: "UNIT" },
          or: [
            { status: { eq: "STORED" } },
            { status: { eq: "SHIPPED" } },
            { status: { eq: "UNLOADED" } },
          ],
        },
        limit: 100,
      };

      do {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };

        const inventoryData = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );

        nextToken = inventoryData.data.searchInventorys.nextToken;

        const inventories = inventoryData.data.searchInventorys.items.filter(
          (itm) => itm.pcs || itm.product?.pcs,
        );

        console.log("inventories:", inventories.length);

        for (const inventory of inventories) {
          const promiseList = [];
          const ppu = inventory.pcs || inventory.product?.pcs;

          if (ppu != null) {
            inventory.pcsOnHand = (inventory.quantityOnHand ?? 0) * ppu;
            //            inventory.pcsCommitted = (inventory.quantityCommitted ?? 0) * ppu;
            inventory.pcsCommitted = 0;

            const inventoryToUpdate = {
              id: inventory.id,
              pcsOnHand: inventory.pcsOnHand,
              pcsCommitted: inventory.pcsCommitted,
            };
            console.log("inventoryToUpdate:", inventoryToUpdate);

            await API.graphql(
              graphqlOperation(mutations.updateInventorie, {
                input: inventoryToUpdate,
              }),
            );

            for (const item of inventory.inventoryItems.items) {
              item.pcsOnHand = (item.quantityOnHand ?? 0) * ppu;
              //              item.pcsCommitted = (item.quantityCommitted ?? 0) * ppu;
              item.pcsCommitted = 0;

              const itemToUpdate = {
                id: item.id,
                pcsOnHand: item.pcsOnHand,
                pcsCommitted: item.pcsCommitted,
              };
              console.log("itemToUpdate:", itemToUpdate);

              promiseList.push(
                API.graphql(
                  graphqlOperation(mutations.updateInventoryItem, {
                    input: itemToUpdate,
                  }),
                ),
              );
            }

            await Promise.all(promiseList);
          }
        }
      } while (nextToken);

      notify("All inventories updated successfully!");
    } catch (error) {
      notify("Error fixing pcs counts", "error");
      console.error("Error fixing pcs counts:", error);
    }
  };

  const deleteBulkErrorLogs = async (queryTerms, notifyInput) => {
    console.clear();
    const TIMEOUT_MS = 10000;

    // Helper to pause execution
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    try {
      let nextToken = true;
      const notifyFn = notifyInput || notify;
      const pathValue = (queryTerms?.path || "").trim();
      const exceptionValue = (queryTerms?.exception || "").trim();

      if (!pathValue && !exceptionValue) {
        notifyFn("Enter Path and/or Exception to search.", "warning");
        return;
      }

      let total = 0;

      const buildContainsFilter = (fieldName, value) => ({
        or: [
          { [fieldName]: { matchPhrasePrefix: value } },
          { [fieldName]: { wildcard: `*${value}*` } },
        ],
      });

      const clauses = [];
      if (pathValue) clauses.push(buildContainsFilter("message", pathValue));
      if (exceptionValue)
        clauses.push(buildContainsFilter("exception", exceptionValue));

      const filter = clauses.length === 1 ? clauses[0] : { and: clauses };

      const searchInput = {
        filter,
        limit: 500,
      };

      do {
        const input =
          nextToken === true ? searchInput : { ...searchInput, nextToken };

        const logData = await API.graphql(
          graphqlOperation(queries.searchLogs, input),
        );

        nextToken = logData.data.searchLogs.nextToken;
        const logs = logData.data.searchLogs.items;
        console.log("Logs to delete:", logs.length);
        total += logs.length;

        const promiseList = logs.map((log) =>
          API.graphql(
            graphqlOperation(mutations.deleteLog, {
              input: { id: log.id }, // assuming deleteLog requires { input: { id } }
            }),
          ),
        );

        if (nextToken) {
          await Promise.all(promiseList);
          console.log(
            `Deleted: ${total}. Waiting ${TIMEOUT_MS / 1000} seconds...`,
          );
          await sleep(TIMEOUT_MS);
        }
      } while (nextToken);

      console.log(`Total of ${total} logs deleted.`);
      notifyFn("Logs deleted successfully!");
    } catch (error) {
      notify("Error deleting logs", "error");
      console.error("Error deleting MISMATCH logs:", error);
    }
  };

  const runAddressBookBackfill = async () => {
    console.clear();
    console.log("Starting addressBook backfill...");

    try {
      let nextToken = null;
      const limit = 250;

      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listAccounts, {
            limit,
            nextToken,
          }),
        );

        const accounts = result.data.listAccounts.items;
        nextToken = result.data.listAccounts.nextToken;

        // console.log(`Processing accounts:`, accounts.length);
        console.log(`Processing accounts:`, accounts);

        for (const account of accounts) {
          debugger;
          const {
            id: accountId,
            groups,
            owner,
            reloadYardId,
            addressBook = [],
            savedAddresses,
          } = account;

          if (!addressBook?.length) continue;
          // if (savedAddresses?.items?.length >= addressBook.length) continue;

          const existing = new Set(
            (savedAddresses?.items || []).map((s) =>
              `${s.name.trim()}||${s.street.trim()}`.toLowerCase(),
            ),
          );

          for (const addr of addressBook) {
            const nameTrim = (addr.name || "").trim();
            const streetTrim = (addr.street || "").trim();
            const cityTrim = (addr.city || "").trim();
            const stateTrim = (addr.state || "").trim();
            const zipTrim = (addr.zipCode || "").trim();
            const countryTrim = (addr.country || "").trim();

            const key = `${nameTrim}||${streetTrim}`.toLowerCase();
            if (existing.has(key)) {
              console.log(
                `  Skipping existing address for account ${accountId}:`,
                streetTrim,
              );
              continue;
            }

            const input = {
              groups,
              owner,
              accountId,
              reloadYardId,

              name: nameTrim,
              street: streetTrim,
              city: cityTrim,
              state: stateTrim,
              zipCode: zipTrim,
              country: countryTrim,
            };

            await API.graphql(
              graphqlOperation(mutations.createSavedAddress, { input }),
            );
          }
        }
      } while (nextToken);

      console.log("AddressBook backfill complete.");
      notify("All addresses updated");
    } catch (error) {
      logError("AdminDashboard:runAddressBookBackfill", error, notify);
    }
  };

  const runAddressBookDelete = async () => {
    console.clear();
    console.log("Starting addressBook delete...");

    try {
      let nextToken = null;
      const limit = 250;

      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listAccounts, {
            limit,
            nextToken,
          }),
        );

        const accounts = result.data.listAccounts.items;
        nextToken = result.data.listAccounts.nextToken;

        // console.log(`Processing accounts:`, accounts.length);
        console.log(`Processing accounts:`, accounts);

        for (const [index, account] of accounts.entries()) {
          console.log("account:", index);
          debugger;
          const input = {
            id: account.id,
            addressBook: null,
          };
          await API.graphql(
            graphqlOperation(mutations.updateAccount, { input }),
          );
        }
      } while (nextToken);

      console.log("AddressBook delete complete.");
      notify("All addresses deleted");
    } catch (error) {
      logError("AdminDashboard:runAddressBookDelete", error, notify);
    }
  };

  // ab16ff44-1ad0-4812-8fb2-d2f39ddf058b
  // transloads1e8c0-dev
  // 2025-07-13T01:34:56.977Z
  // 63ca7eaa-ae93-41c6-8878-f56fe0fc5f1b/inbound/1751911976952/Reload Web 8.JPG
  // us-east-1

  const runInboundTempPhotosBackfill = async () => {
    console.clear();
    console.log("Starting inbound photo backfill…");

    const startTime = Date.now();
    let nextToken = null;
    const limit = 100;
    let total = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInboundShipmentsContainerDrop, {
            limit,
            nextToken,
          }),
        );

        const inbounds = result.data.listInboundShipments.items.filter(
          (itm) => itm.photos?.length > 0,
        );
        nextToken = result.data.listInboundShipments.nextToken;

        await Promise.all(
          inbounds.map(async (inbound) => {
            const {
              id: parentId,
              reloadYardId,
              owner,
              groups,
              photos = [],
              tempPhotos,
            } = inbound;
            const items = tempPhotos?.items || [];

            try {
              const existingKeys = new Set(items.map((p) => p.key));
              const newPhotos = photos.filter((p) => !existingKeys.has(p.key));

              if (newPhotos.length) {
                await Promise.all(
                  newPhotos.map((p) =>
                    API.graphql(
                      graphqlOperation(mutations.createPhoto, {
                        input: {
                          owner,
                          groups,
                          reloadYardId,
                          parentId,
                          parentType: "INBOUND",
                          bucket: p.bucket,
                          region: p.region,
                          key: p.key,
                          type: p.type,
                          name: p.key.split("/").pop(),
                          createdBy: p.createdBy,
                        },
                      }),
                    ),
                  ),
                );
              }

              // clear legacy fields now that copy succeeded
              await API.graphql(
                graphqlOperation(mutations.updateInboundShipment, {
                  input: {
                    id: parentId,
                    photos: null,
                  },
                }),
              );

              // console.log(
              //   `[${parentId}] migrated ${newPhotos.length} photo(s) & cleared legacy fields`
              // );
            } catch (recordErr) {
              console.error(`[${parentId}] failed:`, recordErr);
            }
          }),
        );
        // Only here do we update the shared counter, once per page
        total += inbounds.length;
        console.log(`processed: ${total}`);
      } while (nextToken);

      const endTime = Date.now();
      const durationMs = endTime - startTime;
      const durationSec = durationMs / 1000;

      const timePerRecord = total ? durationSec / total : 0;
      const projectedSec = timePerRecord * 10000;
      const projectedMin = projectedSec / 60;

      console.log(`Elapsed time: ${durationSec.toFixed(2)}s`);
      console.log(`Avg time/record: ${timePerRecord.toFixed(2)}s`);
      console.log(
        `Projected time for 10,000 records: ~${projectedMin.toFixed(2)} min`,
      );

      console.log("Inbound photo backfill complete.");
      notify("backfill complete");
    } catch (pageErr) {
      console.error("Fatal error in backfill loop:", pageErr);
      logError("AdminDashboard:runInboundTempPhotosBackfill", pageErr, notify);
    }
  };

  const resetParentType = async () => {
    console.log("Starting inbound photo reset…");

    let nextToken = null;
    const limit = 250;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(queries.listPhotos, {
            limit,
            nextToken,
          }),
        );

        const photos = result.data.listPhotos.items.filter(
          (itm) => itm.parentType === "INBOUND",
        );
        nextToken = result.data.listPhotos.nextToken;

        await Promise.all(
          photos.map(async (photo) => {
            try {
              await API.graphql(
                graphqlOperation(mutations.updatePhoto, {
                  input: {
                    id: photo.id,
                    parentType: ResourceType.INBOUND, // Assuming you want to re-apply this or correct the value
                  },
                }),
              );
              // console.log(`Updated photo: ${photo.id}`);
            } catch (recordErr) {
              console.error(`Photo [${photo.id}] update failed:`, recordErr);
            }
          }),
        );
      } while (nextToken);

      console.log("Inbound photo reset complete.");
      notify("Backfill complete");
    } catch (pageErr) {
      console.error("Fatal error in backfill loop:", pageErr);
      logError("AdminDashboard:resetParentType", pageErr, notify);
    }
  };

  const runOutboundTempPhotosBackfill = async () => {
    console.clear();
    console.log("Starting outbound photo backfill…");

    const startTime = Date.now();
    let nextToken = null;
    const limit = 100;
    let total = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listOutboundShipmentsContainerDrop, {
            limit,
            nextToken,
          }),
        );

        const outbounds = result.data.listOutboundShipments.items.filter(
          (itm) => itm.photos?.length > 0,
        );
        nextToken = result.data.listOutboundShipments.nextToken;

        await Promise.all(
          outbounds.map(async (outbound) => {
            const {
              id: parentId,
              reloadYardId,
              groups,
              owner,
              photos = [],
              tempPhotos,
            } = outbound;
            const items = tempPhotos?.items || [];

            try {
              const existingKeys = new Set(items.map((p) => p.key));
              const newPhotos = photos.filter((p) => !existingKeys.has(p.key));

              if (newPhotos.length) {
                await Promise.all(
                  newPhotos.map((p) =>
                    API.graphql(
                      graphqlOperation(mutations.createPhoto, {
                        input: {
                          reloadYardId,
                          parentId,
                          groups,
                          owner,
                          parentType: ResourceType.OUTBOUND,
                          bucket: p.bucket,
                          region: p.region,
                          key: p.key,
                          type: p.type,
                          name: p.key.split("/").pop(),
                          createdBy: p.createdBy,
                        },
                      }),
                    ),
                  ),
                );
              }

              // clear legacy fields now that copy succeeded
              await API.graphql(
                graphqlOperation(mutations.updateOutboundShipment, {
                  input: {
                    id: parentId,
                    photos: null,
                  },
                }),
              );

              // console.log(
              //   `[${parentId}] migrated ${newPhotos.length} photo(s) & cleared legacy fields`
              // );
            } catch (recordErr) {
              console.error(`[${parentId}] failed:`, recordErr);
            }
          }),
        );
        // Only here do we update the shared counter, once per page
        total += outbounds.length;
        console.log(`processed: ${total}`);
      } while (nextToken);

      const endTime = Date.now();
      const durationMs = endTime - startTime;
      const durationSec = durationMs / 1000;

      const timePerRecord = total ? durationSec / total : 0;
      const projectedSec = timePerRecord * 15000;
      const projectedMin = projectedSec / 60;

      console.log(`Elapsed time: ${durationSec.toFixed(2)}s`);
      console.log(`Avg time/record: ${timePerRecord.toFixed(2)}s`);
      console.log(
        `Projected time for 15,000 records: ~${projectedMin.toFixed(2)} min`,
      );

      console.log("Outbound photo backfill complete.");
      notify("backfill complete");
    } catch (pageErr) {
      console.error("Fatal error in backfill loop:", pageErr);
      logError("AdminDashboard:runOutboundTempPhotosBackfill", pageErr, notify);
    }
  };

  const runInventoryTempPhotosBackfill = async () => {
    console.clear();
    console.log("Starting inventory photo backfill…");

    const startTime = Date.now();
    let nextToken = null;
    const limit = 100;
    let total = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInventories, {
            limit,
            nextToken,
          }),
        );

        const inventories = result.data.listInventories.items.filter(
          (itm) => itm.photos?.length >= 0,
        );
        nextToken = result.data.listInventories.nextToken;

        await Promise.all(
          inventories.map(async (inventory) => {
            const {
              id: parentId,
              reloadYardId,
              groups,
              owner,
              photos = [],
              tempPhotos,
            } = inventory;
            const items = tempPhotos?.items || [];

            try {
              const existingKeys = new Set(items.map((p) => p.key));
              const newPhotos = photos.filter((p) => !existingKeys.has(p.key));

              if (newPhotos.length) {
                await Promise.all(
                  newPhotos.map((p) =>
                    API.graphql(
                      graphqlOperation(mutations.createPhoto, {
                        input: {
                          reloadYardId,
                          parentId,
                          groups,
                          owner,
                          parentType: ResourceType.INVENTORY,
                          bucket: p.bucket,
                          region: p.region,
                          key: p.key,
                          type: p.type,
                          name: p.key.split("/").pop(),
                          createdBy: p.createdBy,
                        },
                      }),
                    ),
                  ),
                );
              }

              // clear legacy fields now that copy succeeded
              await API.graphql(
                graphqlOperation(mutations.updateInventorie, {
                  input: {
                    id: parentId,
                    // photos: null,
                  },
                }),
              );
            } catch (recordErr) {
              console.error(`[${parentId}] failed:`, recordErr);
            }
          }),
        );
        // Only here do we update the shared counter, once per page
        total += inventories.length;
        console.log(`processed: ${total}`);
      } while (nextToken);

      const endTime = Date.now();
      const durationMs = endTime - startTime;
      const durationSec = durationMs / 1000;

      const timePerRecord = total ? durationSec / total : 0;
      const projectedSec = timePerRecord * 35000;
      const projectedMin = projectedSec / 60;

      console.log(`Elapsed time: ${durationSec.toFixed(2)}s`);
      console.log(`Avg time/record: ${timePerRecord.toFixed(2)}s`);
      console.log(
        `Projected time for 35,000 records: ~${projectedMin.toFixed(2)} min`,
      );

      console.log("Inventory photo backfill complete.");
      notify("backfill complete");
    } catch (pageErr) {
      console.error("Fatal error in backfill loop:", pageErr);
      logError(
        "AdminDashboard:runInventoryTempPhotosBackfill",
        pageErr,
        notify,
      );
    }
  };

  const runProductStatusBackfill = async () => {
    console.clear();
    console.log("Starting product status backfill...");

    try {
      let nextToken = null;
      const limit = 250;

      do {
        const result = await API.graphql(
          graphqlOperation(queries.listProducts, {
            limit,
            nextToken,
          }),
        );

        const products = result.data.listProducts.items;
        nextToken = result.data.listProducts.nextToken;

        console.log(`Processing products:`, products.length);

        for (const product of products) {
          const updatedProduct = {
            id: product.id,
            accountId: product.accountId,
            active: product.active,
            productCode: product.productCode,
            name: product.name,
            status: "STORED",
          };

          await dataProvider.update("products", {
            data: updatedProduct,
          });

          console.log(`Updated: ${product.name} (${product.id})`);
        }
      } while (nextToken);

      console.log("product status backfill complete.");
      notify("All products statuses updated to STORED");
    } catch (error) {
      logError("AdminDashboard:runProductStatusBackfill", error, notify);
    }
  };

  const runReloadYardStatusBackfill = async () => {
    console.clear();
    console.log("Starting reloadYard status backfill...");

    try {
      let nextToken = null;
      const limit = 250;

      do {
        const result = await API.graphql(
          graphqlOperation(queries.listReloadYards, {
            limit,
            nextToken,
          }),
        );

        const reloadYards = result.data.listReloadYards.items;
        nextToken = result.data.listReloadYards.nextToken;

        console.log(`Processing yards:`, reloadYards.length);

        for (const reloadYard of reloadYards) {
          const subscriptionStaus = reloadYard.subscription.status;
          let newStatus = reloadYard.status;
          if (subscriptionStaus === SubscriptionStatus.ACTIVE)
            newStatus = ReloadYardStatus.ACTIVE;
          else if (subscriptionStaus === SubscriptionStatus.CANCELED)
            newStatus = ReloadYardStatus.CANCELLED;
          else if (subscriptionStaus === SubscriptionStatus.TRIALING)
            newStatus = ReloadYardStatus.TRIALING;

          const updatedYard = {
            id: reloadYard.id,
            status: newStatus,
          };

          await dataProvider.update("reloadYards", {
            data: updatedYard,
          });

          console.log(`Updated: ${reloadYard.name} (${updatedYard.id})`);
        }
      } while (nextToken);

      console.log("Yard status backfill complete.");
      notify("All yards statuses updated");
    } catch (error) {
      logError("AdminDashboard:runReloadYardStatusBackfill", error, notify);
    }
  };

  const runBackFillUnFilledContainerDropValuesInbounds = () =>
    utils.backFillUnFilledContainerDropValuesInbounds(notify);
  const runBackFillUnFilledContainerPickupValuesOutbounds = () =>
    utils.backFillUnFilledContainerPickupValuesOutbounds(notify);

  const backfillYardPaymentTerms = async () => {
    console.clear();
    debugger;

    try {
      const promiseList = [];

      // 1. Get all reload yards
      const result = await API.graphql(
        graphqlOperation(queries.listReloadYards, { limit: 1000 }),
      );
      const reloadYardList = result.data.listReloadYards.items;
      console.log("reloadYardList", reloadYardList);

      // 2. Iterate over each reload yard and create payment terms
      for (const yard of reloadYardList) {
        const reloadYardId = yard.id;
        const groups = yard.groups;

        const termList = DefaultPaymentTerms.map((itm, idx) => {
          const input = {
            groups,
            reloadYardId,
            code: itm.code,
            name: itm.name,
            type: itm.type,
            dueDays: itm.dueDays,
            sortKey: SORTKEY_BUFFER + idx,
          };
          return API.graphql(
            graphqlOperation(mutations.createPaymentTerm, { input }),
          );
        });

        promiseList.push(...termList);
      }

      // 3. Await all mutations
      const response = await Promise.allSettled(promiseList);

      // 4. Log any rejections
      const reasonMap = {};
      response.forEach((resp) => {
        if (resp.status === "rejected") {
          const message = resp.reason?.errors?.[0]?.message;
          if (message && !reasonMap[message]) {
            reasonMap[message] = message;
            console.warn("REJECTED:", message, resp);
          }
        }
      });

      notify("Yard payment terms backfill complete");
    } catch (err) {
      logError("AdminDashboard:backfillYardPaymentTerms", err, notify);
    }
  };

  const hasVal = (v) => v !== undefined && v !== null;
  const needsArray = (v) => !Array.isArray(v);
  const looksLikeAWSDateTime = (s) =>
    typeof s === "string" && /\d{4}-\d{2}-\d{2}T/.test(s);
  // "M/d/yyyy" or "MM/dd/yyyy" -> AWSDateTime at UTC midnight (no TZ drift)
  function toAWSDateTime(mdy) {
    if (!mdy || typeof mdy !== "string") return null;
    const parts = mdy.split("/");
    if (parts.length !== 3) return null;
    const [mStr, dStr, yStr] = parts;
    const m = Number(mStr),
      d = Number(dStr),
      y = Number(yStr);
    if (!m || !d || !y) return null;
    const dt = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
    return dt.toISOString();
  }

  const backfillInvoiceEvents = async () => {
    console.clear();
    console.clear();
    console.log("Starting InvoiceService.event backfill...");

    let nextToken = null;
    const limit = 100;
    let count = 0;

    do {
      const result = await API.graphql(
        graphqlOperation(adminQueries.listInvoiceServices, {
          limit,
          nextToken,
        }),
      );

      const items = result.data.listInvoiceServices.items || [];
      nextToken = result.data.listInvoiceServices.nextToken;

      try {
        const promiseList = [];
        for (const item of items) {
          const id = item.id;
          const invoiceEvent = item.invoice?.event;

          if (invoiceEvent && !item.event) {
            promiseList.push(
              API.graphql(
                graphqlOperation(mutations.updateInvoiceService, {
                  input: {
                    id,
                    event: invoiceEvent,
                  },
                }),
              ),
            );
            count++;
          }
        }
        // 3. Await all mutations
        const response = await Promise.allSettled(promiseList);
        console.log(`${count} records updated.`);

        // 4. Log any rejections
        const reasonMap = {};
        response.forEach((resp) => {
          if (resp.status === "rejected") {
            const message = resp.reason?.errors?.[0]?.message;
            if (message && !reasonMap[message]) {
              reasonMap[message] = message;
              console.warn("REJECTED:", message, resp);
            }
          }
        });
      } catch (err) {
        console.error(`Failed to parse or update event:`, err);
      }
    } while (nextToken);

    console.log(`Backfill complete. ${count} records updated.`);
  };

  const backfillInvoiceServiceDate = async () => {
    console.clear();
    console.log("Starting InvoiceService serviceDate backfill...");

    let nextToken = null;
    const limit = 100;
    let count = 0;

    do {
      const result = await API.graphql(
        graphqlOperation(adminQueries.listInvoiceServices, {
          limit,
          nextToken,
        }),
      );

      const items = result.data.listInvoiceServices.items;
      nextToken = result.data.listInvoiceServices.nextToken;

      try {
        const promiseList = [];
        for (const item of items) {
          const { id, tempServiceDate } = item;

          if (tempServiceDate) {
            promiseList.push(
              API.graphql(
                graphqlOperation(mutations.updateInvoiceService, {
                  input: {
                    id,
                    serviceDate: tempServiceDate,
                    tempServiceDate: null,
                  },
                }),
              ),
            );
          }

          count++;
        }
        // 3. Await all mutations
        const response = await Promise.allSettled(promiseList);
        console.log(`${count} records updated.`);

        // 4. Log any rejections
        const reasonMap = {};
        response.forEach((resp) => {
          if (resp.status === "rejected") {
            const message = resp.reason?.errors?.[0]?.message;
            if (message && !reasonMap[message]) {
              reasonMap[message] = message;
              console.warn("REJECTED:", message, resp);
            }
          }
        });
      } catch (err) {
        console.error(`Failed to parse or update serviceDate:`, err);
      }
    } while (nextToken);

    console.log(`Backfill complete. ${count} records updated.`);
  };

  const backfillFreeDaysPolicy = async () => {
    console.clear();
    console.log("Starting backfill for ChargeRule.freeDaysPolicy …");

    let nextToken = null;
    const limit = 250;
    let scanned = 0;
    let updated = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(queries.listChargeRules, {
            limit,
            nextToken,
          }),
        );

        const rules = result?.data?.listChargeRules?.items || [];
        nextToken = result?.data?.listChargeRules?.nextToken || null;

        const promiseList = [];
        for (const rule of rules) {
          scanned++;
          const input = { id: rule.id, execution: "AUTO" };
          if (rule.event === "STORAGE") {
            input.execution = "MANUAL";
            if (!rule?.freeDaysPolicy) {
              input.freeDaysPolicy = "DAYS";
            }
            if (!rule?.freeDays) {
              input.freeDays = 0;
            }
          }
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateChargeRule, { input }),
            ),
          );
          updated++;
        }

        if (promiseList.length > 0) {
          const response = await Promise.allSettled(promiseList);
          response.forEach((resp) => {
            if (resp.status === "rejected") console.warn("REJECTED:", resp);
          });
        }

        console.log(
          `Processed page. Scanned: ${scanned}, Updated: ${updated}, nextToken: ${!!nextToken}`,
        );
      } while (nextToken);

      notify(`Backfill complete: scanned=${scanned}, updated=${updated}`);
    } catch (err) {
      logError("AdminDashboard:backfillFreeDaysPolicy", err, notify);
    }
  };

  const backfillAC = async () => {
    console.clear();
    console.log("Starting Accessorial Charges sentinel backfill...");

    try {
      let nextToken = null;
      const limit = 250;
      let scanned = 0;
      let created = 0;

      do {
        const yardResp = await API.graphql(
          graphqlOperation(queries.listReloadYards, { limit, nextToken }),
        );
        const yards = yardResp?.data?.listReloadYards?.items || [];
        nextToken = yardResp?.data?.listReloadYards?.nextToken || null;

        console.log(`Processing yards:`, yards.length);

        for (const yard of yards) {
          scanned++;
          const svcResp = await API.graphql(
            graphqlOperation(queries.serviceByAccountByActiveByName, {
              accountId: yard.id, // reloadYardId
              activeName: {
                eq: {
                  active: "true",
                  name: ServiceType.ACCESSORIAL_CHARGES,
                },
              },
              limit: 1,
            }),
          );
          const exists =
            (svcResp?.data?.serviceByAccountByActiveByName?.items || [])
              .length > 0;
          if (exists) continue;

          try {
            await createAccessorialCharges(yard);
            created++;
            console.log(
              `Created sentinel AC for yard ${yard.id} (${yard.name})`,
            );
          } catch (err) {
            console.warn(`Failed to create AC for yard ${yard.id}:`, err);
          }
        }
      } while (nextToken);

      console.log(`Backfill complete. scanned=${scanned}, created=${created}`);
      notify(`AC backfill done: scanned=${scanned}, created=${created}`);
    } catch (err) {
      logError("AdminDashboard:backfillAC", err, notify);
    }
  };

  const backfillInboundSkipInvoice = async () => {
    console.clear();
    console.log("Starting inbound skipInvoice backfill…");

    let nextToken = null;
    const limit = 250;
    let scanned = 0;
    let updated = 0;

    do {
      const result = await API.graphql(
        graphqlOperation(adminQueries.listInboundShipmentsContainerDrop, {
          limit,
          nextToken,
        }),
      );

      const items = result?.data?.listInboundShipments?.items || [];
      nextToken = result?.data?.listInboundShipments?.nextToken || null;

      try {
        const promiseList = [];
        for (const item of items) {
          scanned++;
          const skipInvoice = Boolean(!item.billUnits && !item.billQuantity);
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateInboundShipment, {
                input: {
                  id: item.id,
                  skipInvoice: skipInvoice,
                  stockImport: false,
                },
              }),
            ),
          );
          updated++;
        }

        // 3. Await all mutations
        const response = await Promise.allSettled(promiseList);

        // 4. Log any rejections
        const reasonMap = {};
        response.forEach((resp) => {
          if (resp.status === "rejected") {
            const message = resp.reason?.errors?.[0]?.message;
            if (message && !reasonMap[message]) {
              reasonMap[message] = message;
              console.warn("REJECTED:", message, resp);
            }
          }
        });
      } catch (err) {
        console.error(`[INBOUND] Failed`, err);
      }

      console.log(
        `Inbound page processed. Scanned: ${scanned}, Updated: ${updated}, nextToken: ${!!nextToken}`,
      );
    } while (nextToken);

    console.log(
      `Inbound backfill complete. Scanned=${scanned}, Updated=${updated}.`,
    );
  };
  const backfillOutboundSkipInvoice = async () => {
    console.log("Starting outbound skipInvoice backfill…");

    let nextToken = null;
    const limit = 250;
    let scanned = 0;
    let updated = 0;

    do {
      const result = await API.graphql(
        graphqlOperation(adminQueries.listOutboundShipmentsSkipInvoice, {
          limit,
          nextToken,
        }),
      );

      const items = result?.data?.listOutboundShipments?.items || [];
      nextToken = result?.data?.listOutboundShipments?.nextToken || null;

      try {
        const promiseList = [];
        for (const item of items) {
          scanned++;
          const skipInvoice = Boolean(!item.billUnits && !item.billQuantity);
          if (item.skipInvoice !== skipInvoice) {
            promiseList.push(
              API.graphql(
                graphqlOperation(mutations.updateOutboundShipment, {
                  input: { id: item.id, skipInvoice: skipInvoice },
                }),
              ),
            );
            updated++;
          }
        }

        // 3. Await all mutations
        const response = await Promise.allSettled(promiseList);

        // 4. Log any rejections
        const reasonMap = {};
        response.forEach((resp) => {
          if (resp.status === "rejected") {
            const message = resp.reason?.errors?.[0]?.message;
            if (message && !reasonMap[message]) {
              reasonMap[message] = message;
              console.warn("REJECTED:", message, resp);
            }
          }
        });
      } catch (err) {
        console.error(`[OUTBOUND] Failed`, err);
      }

      console.log(
        `Outbound page processed. Scanned: ${scanned}, Updated: ${updated}, nextToken: ${!!nextToken}`,
      );
    } while (nextToken);

    console.log(
      `Outbound backfill complete. Scanned=${scanned}, Updated=${updated}.`,
    );
  };
  const backfillAllSkipInvoice = async () => {
    await backfillInboundSkipInvoice();
    await backfillOutboundSkipInvoice();
    console.log("All skipInvoice backfills done.");
    notify("all done");
  };

  const validateAndFixServiceHistory = async () => {
    console.clear();
    console.log("Starting service history validation and fix...");

    const startTime = performance.now();
    let totalServices = 0;
    let servicesWithHistory = 0;
    let servicesFixed = 0;
    let totalHistoryNotesCreated = 0;
    let servicesToCleanup = [];

    try {
      let nextToken = true;
      const searchInput = { limit: 50 };

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(queries.listServices, input),
        );
        const services = result.data.listServices.items;
        nextToken = result.data.listServices.nextToken;

        console.log(`Processing ${services.length} services...`);

        for (const service of services) {
          totalServices++;

          // Check if service still has history array
          if (service.history && service.history.length > 0) {
            servicesWithHistory++;

            try {
              // Query existing history notes for this service
              const input = {
                parentId: service.id,
                limit: 1000,
              };
              const historyNotesResult = await API.graphql(
                graphqlOperation(queries.historyByParentByTime, input),
              );
              const existingHistoryNotes =
                historyNotesResult.data.historyByParentByTime.items || [];

              // Find history entries that haven't been migrated yet by comparing text, createdBy, and dateTime
              const missingHistoryEntries = service.history.filter((entry) => {
                return !existingHistoryNotes.some(
                  (note) =>
                    note.text === entry.text &&
                    note.createdBy === entry.by &&
                    note.dateTime === entry.date,
                );
              });

              if (missingHistoryEntries.length > 0) {
                console.log(
                  `Service ${service.id} missing ${missingHistoryEntries.length} history entries, creating them...`,
                );

                // Create missing history notes
                const historyNotePromises = missingHistoryEntries.map(
                  (historyEntry) => {
                    const input = {
                      groups: service.groups,
                      reloadYardId: service.reloadYardId,
                      parentId: service.id,
                      parentType: ResourceType.SERVICE,
                      text: historyEntry.text,
                      dateTime: historyEntry.date,
                      createdBy: historyEntry.by,
                    };

                    return API.graphql(
                      graphqlOperation(mutations.createHistoryNote, { input }),
                    );
                  },
                );

                try {
                  await Promise.allSettled(historyNotePromises);
                  totalHistoryNotesCreated += missingHistoryEntries.length;
                  console.log(
                    `Successfully created ${missingHistoryEntries.length} history notes for service ${service.id}`,
                  );

                  // Mark service for cleanup if all history entries are now migrated
                  servicesToCleanup.push(service.id);
                  servicesFixed++;
                } catch (error) {
                  console.error(
                    `Failed to create history notes for service ${service.id}:`,
                    error,
                  );
                }
              } else {
                console.log(
                  `Service ${service.id} already has all history entries migrated, marking for cleanup`,
                );
                servicesToCleanup.push(service.id);
                servicesFixed++;
              }
            } catch (error) {
              console.error(
                `Failed to query history notes for service ${service.id}:`,
                error,
              );
            }
          }
        }
      }

      // Clean up old history arrays for services that have been fully migrated
      console.log("Cleaning up old history arrays...");

      if (servicesToCleanup.length > 0) {
        const serviceUpdatePromises = servicesToCleanup.map((serviceId) => {
          const input = { id: serviceId, history: null };
          return API.graphql(
            graphqlOperation(mutations.updateService, { input }),
          );
        });

        try {
          await Promise.allSettled(serviceUpdatePromises);
          console.log(
            `Cleaned up history for ${servicesToCleanup.length} services`,
          );
        } catch (error) {
          console.error("Failed to clean up service history:", error);
        }
      }

      const endTime = performance.now();
      const durationMs = endTime - startTime;
      const durationSec = durationMs / 1000;

      console.log(`Service history validation and fix complete:`);
      console.log(`- Total services processed: ${totalServices}`);
      console.log(`- Services with history arrays: ${servicesWithHistory}`);
      console.log(`- Services fixed: ${servicesFixed}`);
      console.log(`- History notes created: ${totalHistoryNotesCreated}`);
      console.log(`- Services cleaned up: ${servicesToCleanup.length}`);
      console.log(`- Duration: ${durationSec.toFixed(2)}s`);

      utils.elapsedTime(startTime, endTime, totalHistoryNotesCreated, 10000);
      notify(
        `Service history validation complete: ${totalHistoryNotesCreated} notes created, ${servicesToCleanup.length} services cleaned up`,
      );
      console.log("All done");
    } catch (error) {
      logError("AdminDashboard:validateAndFixServiceHistory", error, notify);
    }
  };

  const backfillServiceUOM = async () => {
    console.clear();
    console.log("Starting Service UOM backfill...");

    const uomFields = UnitOptions.map(({ column }) => column).filter(Boolean);

    let nextToken = null;
    const limit = 50;
    let scanned = 0;
    let updated = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(queries.listServices, {
            limit,
            nextToken,
          }),
        );

        const services = result?.data?.listServices?.items || [];
        nextToken = result?.data?.listServices?.nextToken || null;

        console.log(`Processing ${services.length} services...`);

        const promiseList = [];

        for (const service of services) {
          scanned++;

          const updates = {};
          for (const field of uomFields) {
            if (service[field] === null || service[field] === undefined) {
              updates[field] = 0;
            }
          }

          if (Object.keys(updates).length === 0) continue;

          const input = { id: service.id, ...updates };

          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateService, {
                input,
              }),
            ),
          );
        }

        if (promiseList.length > 0) {
          const response = await Promise.allSettled(promiseList);
          let pageUpdated = 0;
          for (const resp of response) {
            if (resp.status === "fulfilled") {
              pageUpdated++;
            } else {
              console.warn("REJECTED:", resp);
            }
          }
          updated += pageUpdated;
        }

        console.log(
          `Processed page. Scanned: ${scanned}, Updated: ${updated}, nextToken: ${!!nextToken}`,
        );
      } while (nextToken);

      notify(`Backfill complete: scanned=${scanned}, updated=${updated}`);
      console.log(
        `Service UOM backfill complete. Scanned=${scanned}, Updated=${updated}`,
      );
    } catch (error) {
      logError("AdminDashboard:backfillServiceUOM", error, notify);
    }
  };

  const parseNumericValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const convertSfValueToMsf = (sfValue, multiplier) => {
    const numericSfValue = parseNumericValue(sfValue);
    if (numericSfValue === null) return null;

    return parseNumericValue(trimDecimal(numericSfValue * multiplier, 6));
  };

  const backfillSfToMsf = async () => {
    console.clear();
    console.log("Starting SF to MSF backfill...");

    const limit = 250;

    const processPagedBackfill = async ({
      label,
      query,
      rootKey,
      mutation,
      buildInput,
    }) => {
      let nextToken = null;
      let scanned = 0;
      let updated = 0;
      let skipped = 0;
      let failed = 0;

      do {
        const result = await API.graphql(
          graphqlOperation(query, {
            limit,
            nextToken,
          }),
        );
        const records = result?.data?.[rootKey]?.items || [];
        nextToken = result?.data?.[rootKey]?.nextToken || null;

        const promiseList = [];
        for (const record of records) {
          scanned++;
          const input = buildInput(record);
          if (!input) {
            skipped++;
            continue;
          }

          promiseList.push(
            API.graphql(
              graphqlOperation(mutation, {
                input,
              }),
            ),
          );
        }

        if (promiseList.length > 0) {
          const response = await Promise.allSettled(promiseList);
          for (const resp of response) {
            if (resp.status === "fulfilled") {
              updated++;
            } else {
              failed++;
              console.warn(`${label} update rejected:`, resp);
            }
          }
        }

        console.log(
          `${label} page processed. scanned=${scanned}, updated=${updated}, skipped=${skipped}, failed=${failed}, nextToken=${!!nextToken}`,
        );
      } while (nextToken);

      return { scanned, updated, skipped, failed };
    };

    try {
      const productResults = await processPagedBackfill({
        label: "Products",
        query: queries.listProducts,
        rootKey: "listProducts",
        mutation: mutations.updateProduct,
        buildInput: (product) => {
          const convertedMsf = convertSfValueToMsf(product?.sf, 0.001);
          if (convertedMsf === null) return null;

          const currentMsf = parseNumericValue(product?.msf);
          const shouldUpdateMsf =
            currentMsf === null ||
            Math.abs(currentMsf - convertedMsf) >= 0.000001;
          const shouldUpdateDefaultUnits = product?.defaultUOM === Unit.SF;

          if (!shouldUpdateMsf && !shouldUpdateDefaultUnits) return null;

          const input = { id: product.id };
          if (shouldUpdateMsf) input.msf = convertedMsf;
          if (shouldUpdateDefaultUnits) input.defaultUOM = Unit.MSF;

          return input;
        },
      });

      const serviceResults = await processPagedBackfill({
        label: "Services",
        query: queries.listServices,
        rootKey: "listServices",
        mutation: mutations.updateService,
        buildInput: (service) => {
          const convertedMsf = convertSfValueToMsf(service?.sf, 1000);
          if (convertedMsf === null) return null;

          const currentMsf = parseNumericValue(service?.msf);
          if (
            currentMsf !== null &&
            Math.abs(currentMsf - convertedMsf) < 0.000001
          ) {
            return null;
          }

          return {
            id: service.id,
            msf: convertedMsf,
          };
        },
      });

      console.log("SF to MSF backfill complete", {
        productResults,
        serviceResults,
      });
      notify(
        `SF to MSF backfill complete. Products updated=${productResults.updated}, Services updated=${serviceResults.updated}`,
      );
    } catch (error) {
      logError("AdminDashboard:backfillSfToMsf", error, notify);
    }
  };

  const backfillStlLfToFt = async () => {
    console.clear();
    console.log("Starting STL LF to FT backfill...");

    const RELOAD_YARD_ID = "a52a7658-0e2f-49b9-9f41-e3ebff901ea4";
    const COMMODITY_ID = "31aa7feb-67a1-4ec9-b77c-8f6ae60f6b63";
    const limit = 250;
    let nextToken = null;
    let scanned = 0;
    let updated = 0;
    let skipped = 0;
    let failed = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(queries.listProducts, {
            filter: {
              reloadYardId: { eq: RELOAD_YARD_ID },
              reloadYardCommodityId: { eq: COMMODITY_ID },
            },
            limit,
            nextToken,
          }),
        );
        const records = result?.data?.listProducts?.items || [];
        nextToken = result?.data?.listProducts?.nextToken || null;

        const promiseList = [];
        for (const product of records) {
          scanned++;
          const lfValue = parseNumericValue(product?.lf);
          if (lfValue === null) {
            skipped++;
            continue;
          }

          const currentFt = parseNumericValue(product?.ft);
          if (currentFt !== null && currentFt === lfValue) {
            skipped++;
            continue;
          }

          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateProduct, {
                input: { id: product.id, ft: lfValue },
              }),
            ),
          );
        }

        if (promiseList.length > 0) {
          const response = await Promise.allSettled(promiseList);
          for (const resp of response) {
            if (resp.status === "fulfilled") {
              updated++;
            } else {
              failed++;
              console.warn("STL LF→FT update rejected:", resp);
            }
          }
        }

        console.log(
          `STL LF→FT page processed. scanned=${scanned}, updated=${updated}, skipped=${skipped}, failed=${failed}, nextToken=${!!nextToken}`,
        );
      } while (nextToken);

      notify(
        `STL LF to FT complete: scanned=${scanned}, updated=${updated}, skipped=${skipped}, failed=${failed}`,
      );
      console.log("STL LF to FT backfill complete", {
        scanned,
        updated,
        skipped,
        failed,
      });
    } catch (error) {
      logError("AdminDashboard:backfillStlLfToFt", error, notify);
    }
  };

  const backfillServiceTaxRate = async () => {
    console.clear();
    console.log("Starting Service taxRate backfill...");

    // First: load ALL ReloadYards to determine each yard's default taxRate
    const yardDefaultTaxRateById = new Map();
    let yardNextToken = null;
    const yardLimit = 200;

    let nextToken = null;
    const limit = 50;
    let scanned = 0;
    let updated = 0;
    let skippedNoReloadYardId = 0;
    let skippedNoYardDefault = 0;

    try {
      do {
        const yardResult = await API.graphql(
          graphqlOperation(queries.listReloadYards, {
            limit: yardLimit,
            nextToken: yardNextToken,
          }),
        );
        const yards = yardResult?.data?.listReloadYards?.items || [];
        yardNextToken = yardResult?.data?.listReloadYards?.nextToken || null;

        for (const yard of yards) {
          const yardDefault = getDefaultTaxRate(yard);
          if (yard?.id && yardDefault) {
            yardDefaultTaxRateById.set(yard.id, yardDefault);
          }
        }
      } while (yardNextToken);

      console.log(
        `Loaded default taxRates for ${yardDefaultTaxRateById.size} reloadYards`,
      );

      do {
        const result = await API.graphql(
          graphqlOperation(queries.listServices, {
            limit,
            nextToken,
          }),
        );

        const services = result?.data?.listServices?.items || [];
        nextToken = result?.data?.listServices?.nextToken || null;

        console.log(`Processing ${services.length} services...`);

        const promiseList = [];

        for (const service of services) {
          scanned++;

          // Only backfill missing taxRate
          if (service?.taxRate) continue;

          const reloadYardId = service?.reloadYardId;
          if (!reloadYardId) {
            skippedNoReloadYardId++;
            continue;
          }

          const defaultTaxRate = yardDefaultTaxRateById.get(reloadYardId);
          if (!defaultTaxRate) {
            skippedNoYardDefault++;
            continue;
          }

          const input = { id: service.id, taxRate: defaultTaxRate };

          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateService, {
                input,
              }),
            ),
          );
        }

        if (promiseList.length > 0) {
          const response = await Promise.allSettled(promiseList);
          let pageUpdated = 0;
          for (const resp of response) {
            if (resp.status === "fulfilled") {
              pageUpdated++;
            } else {
              console.warn("REJECTED:", resp);
            }
          }
          updated += pageUpdated;
        }

        console.log(
          `Processed page. Scanned: ${scanned}, Updated: ${updated}, skippedNoReloadYardId: ${skippedNoReloadYardId}, skippedNoYardDefault: ${skippedNoYardDefault}, nextToken: ${!!nextToken}`,
        );
      } while (nextToken);

      notify(
        `Backfill complete: scanned=${scanned}, updated=${updated}, skippedNoReloadYardId=${skippedNoReloadYardId}, skippedNoYardDefault=${skippedNoYardDefault}`,
      );
      console.log(
        `Service taxRate backfill complete. Scanned=${scanned}, Updated=${updated}, skippedNoReloadYardId=${skippedNoReloadYardId}, skippedNoYardDefault=${skippedNoYardDefault}`,
      );
    } catch (error) {
      logError("AdminDashboard:backfillServiceTaxRate", error, notify);
    }
  };

  const validateAndFixProductHistory = async () => {
    console.clear();
    console.log("Starting product history validation and fix...");

    const startTime = performance.now();
    let totalProducts = 0;
    let productsWithHistory = 0;
    let productsFixed = 0;
    let totalHistoryNotesCreated = 0;
    let productsToCleanup = [];

    try {
      let nextToken = true;
      const searchInput = { limit: 50 };

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(queries.listProducts, input),
        );
        const products = result.data.listProducts.items;
        nextToken = result.data.listProducts.nextToken;

        console.log(`Processing ${products.length} products...`);

        for (const product of products) {
          totalProducts++;

          // Check if product still has history array
          if (product.history && product.history.length > 0) {
            productsWithHistory++;

            try {
              // Query existing history notes for this product
              const input = {
                parentId: product.id,
                limit: 1000,
              };
              const historyNotesResult = await API.graphql(
                graphqlOperation(queries.historyByParentByTime, input),
              );
              const existingHistoryNotes =
                historyNotesResult.data.historyByParentByTime.items || [];

              // Find history entries that haven't been migrated yet by comparing text, createdBy, and dateTime
              const missingHistoryEntries = product.history.filter((entry) => {
                return !existingHistoryNotes.some(
                  (note) =>
                    note.text === entry.text &&
                    note.createdBy === entry.by &&
                    note.dateTime === entry.date,
                );
              });

              if (missingHistoryEntries.length > 0) {
                console.log(
                  `Product ${product.id} missing ${missingHistoryEntries.length} history entries, creating them...`,
                );

                // Create missing history notes
                const historyNotePromises = missingHistoryEntries.map(
                  (historyEntry) => {
                    const input = {
                      groups: product.groups,
                      reloadYardId: product.reloadYardId,
                      parentId: product.id,
                      parentType: ResourceType.PRODUCT,
                      text: historyEntry.text,
                      dateTime: historyEntry.date,
                      createdBy: historyEntry.by,
                    };

                    return API.graphql(
                      graphqlOperation(mutations.createHistoryNote, { input }),
                    );
                  },
                );

                try {
                  await Promise.allSettled(historyNotePromises);
                  totalHistoryNotesCreated += missingHistoryEntries.length;
                  console.log(
                    `Successfully created ${missingHistoryEntries.length} history notes for product ${product.id}`,
                  );

                  // Mark product for cleanup if all history entries are now migrated
                  productsToCleanup.push(product.id);
                  productsFixed++;
                } catch (error) {
                  console.error(
                    `Failed to create history notes for product ${product.id}:`,
                    error,
                  );
                }
              } else {
                console.log(
                  `Product ${product.id} already has all history entries migrated, marking for cleanup`,
                );
                productsToCleanup.push(product.id);
                productsFixed++;
              }
            } catch (error) {
              console.error(
                `Failed to query history notes for product ${product.id}:`,
                error,
              );
            }
          }
        }
      }

      // Clean up old history arrays for products that have been fully migrated
      console.log("Cleaning up old history arrays...");

      if (productsToCleanup.length > 0) {
        const productUpdatePromises = productsToCleanup.map((productId) => {
          const input = { id: productId, history: null };
          return API.graphql(
            graphqlOperation(mutations.updateProduct, { input }),
          );
        });

        try {
          await Promise.allSettled(productUpdatePromises);
          console.log(
            `Cleaned up history for ${productsToCleanup.length} products`,
          );
        } catch (error) {
          console.error("Failed to clean up product history:", error);
        }
      }

      const endTime = performance.now();
      const durationMs = endTime - startTime;
      const durationSec = durationMs / 1000;

      console.log(`Product history validation and fix complete:`);
      console.log(`- Total products processed: ${totalProducts}`);
      console.log(`- Products with history arrays: ${productsWithHistory}`);
      console.log(`- Products fixed: ${productsFixed}`);
      console.log(`- History notes created: ${totalHistoryNotesCreated}`);
      console.log(`- Products cleaned up: ${productsToCleanup.length}`);
      console.log(`- Duration: ${durationSec.toFixed(2)}s`);

      utils.elapsedTime(startTime, endTime, totalHistoryNotesCreated, 10000);
      notify(
        `Product history validation complete: ${totalHistoryNotesCreated} notes created, ${productsToCleanup.length} products cleaned up`,
      );
      console.log("All done");
    } catch (error) {
      logError("AdminDashboard:validateAndFixProductHistory", error, notify);
    }
  };

  const validateAndFixInvoiceHistory = async () => {
    console.clear();
    console.log("Starting invoice history validation and fix...");

    const startTime = performance.now();
    let totalInvoices = 0;
    let invoicesWithHistory = 0;
    let invoicesFixed = 0;
    let totalHistoryNotesCreated = 0;
    const limit = pLimit(50); // Limit to 50 concurrent GraphQL requests

    // Map to collect all invoice updates (date migration + history cleanup)
    const invoiceUpdateMap = new Map();

    try {
      let nextToken = true;
      const searchInput = { limit: 50 };

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInvoices, input),
        );
        const invoices = result.data.listInvoices.items;
        nextToken = result.data.listInvoices.nextToken;

        console.log(`Processing ${invoices.length} invoices...`);

        for (const invoice of invoices) {
          totalInvoices++;

          // Initialize update input for this invoice
          if (!invoiceUpdateMap.has(invoice.id)) {
            invoiceUpdateMap.set(invoice.id, { id: invoice.id });
          }

          // Check if settledDate needs to be migrated to paidDate
          if (hasVal(invoice.settledDate) && !hasVal(invoice.paidDate)) {
            const updateInput = invoiceUpdateMap.get(invoice.id);
            updateInput.paidDate = invoice.settledDate;
            updateInput.settledDate = null;
            console.log(
              `Invoice ${invoice.id} will migrate settledDate to paidDate`,
            );
          }

          // Check if invoice still has history array
          if (invoice.history && invoice.history.length > 0) {
            invoicesWithHistory++;

            try {
              // Query existing history notes for this invoice
              const existingHistoryNotes = invoice.historyLog?.items || [];

              // Find history entries that haven't been migrated yet by comparing text, createdBy, and dateTime
              const missingHistoryEntries = invoice.history.filter((entry) => {
                return !existingHistoryNotes.some(
                  (note) =>
                    note.text === entry.text &&
                    note.createdBy === entry.by &&
                    note.dateTime === entry.date,
                );
              });

              if (missingHistoryEntries.length > 0) {
                console.log(
                  `Invoice ${invoice.id} missing ${missingHistoryEntries.length} history entries, creating them...`,
                );
                try {
                  // Create missing history notes
                  const historyNotePromises = missingHistoryEntries.map(
                    (historyEntry) =>
                      limit(() => {
                        const input = {
                          groups: invoice.groups,
                          reloadYardId: invoice.reloadYardId,
                          parentId: invoice.id,
                          parentType: ResourceType.INVOICE,
                          text: historyEntry.text,
                          dateTime: historyEntry.date,
                          createdBy: historyEntry.by,
                        };
                        return API.graphql(
                          graphqlOperation(mutations.createHistoryNote, {
                            input,
                          }),
                        );
                      }),
                  );

                  await Promise.allSettled(historyNotePromises);
                  totalHistoryNotesCreated += missingHistoryEntries.length;
                  console.log(
                    `Successfully created ${missingHistoryEntries.length} history notes for invoice ${invoice.id}`,
                  );

                  // Mark invoice for history cleanup
                  const updateInput = invoiceUpdateMap.get(invoice.id);
                  updateInput.history = null;
                  invoicesFixed++;
                } catch (error) {
                  console.error(
                    `Failed to create history notes for invoice ${invoice.id}:`,
                    error,
                  );
                }
              } else {
                console.log(
                  `Invoice ${invoice.id} already has all history entries migrated, marking for cleanup`,
                );
                // Mark invoice for history cleanup
                const updateInput = invoiceUpdateMap.get(invoice.id);
                updateInput.history = null;
                invoicesFixed++;
              }
            } catch (error) {
              console.error(
                `Failed to query history notes for invoice ${invoice.id}:`,
                error,
              );
            }
          }
        }
      }

      // Execute all invoice updates (date migration + history cleanup) in a single batch
      console.log(
        "Executing combined invoice updates (date migration + history cleanup)...",
      );

      if (invoiceUpdateMap.size > 0) {
        const updatePromises = Array.from(invoiceUpdateMap.values()).map(
          (updateInput) =>
            limit(() => {
              return API.graphql(
                graphqlOperation(mutations.updateInvoice, {
                  input: updateInput,
                }),
              );
            }),
        );

        try {
          await Promise.allSettled(updatePromises);
          console.log(
            `Successfully updated ${invoiceUpdateMap.size} invoices with combined changes`,
          );
        } catch (error) {
          console.error("Failed to execute invoice updates:", error);
        }
      }

      const endTime = performance.now();
      const durationMs = endTime - startTime;
      const durationSec = durationMs / 1000;

      console.log(`Invoice history validation and fix complete:`);
      console.log(`- Total invoices processed: ${totalInvoices}`);
      console.log(`- Invoices with history arrays: ${invoicesWithHistory}`);
      console.log(`- Invoices fixed: ${invoicesFixed}`);
      console.log(`- History notes created: ${totalHistoryNotesCreated}`);
      console.log(`- Invoices updated: ${invoiceUpdateMap.size}`);
      console.log(`- Duration: ${durationSec.toFixed(2)}s`);

      utils.elapsedTime(startTime, endTime, totalHistoryNotesCreated, 10000);
      notify(
        `Invoice history validation complete: ${totalHistoryNotesCreated} notes created, ${invoiceUpdateMap.size} invoices updated`,
      );
      console.log("All done");
    } catch (error) {
      logError("AdminDashboard:validateAndFixInvoiceHistory", error, notify);
    }
  };

  const fetchProducts = async (accountId, activeOnly = true) => {
    if (!accountId) throw new Error("Account ID is required");
    let allItems = [];
    let nextToken = null;

    do {
      const input = {
        accountId: accountId,
        ...(activeOnly && {
          activeName: { beginsWith: { active: "true" } },
        }),
        limit: 500,
        nextToken,
      };

      const resp = await API.graphql(
        graphqlOperation(queries.productByAccountByActiveByName, input),
      );
      const productData = resp.data.productByAccountByActiveByName;
      allItems = [...allItems, ...productData.items];
      nextToken = productData.nextToken;
    } while (nextToken);
    return allItems;
  };

  const backfillInventoryProducts = async () => {
    console.clear();
    console.log("Starting inventory backfill...");

    const accountId = "abx-123";
    const startTime = performance.now();
    const limit = pLimit(50);

    let totalInventories = 0;
    let inventoriesNeedingUpdate = 0;
    let inventoriesUpdated = 0;

    try {
      // Fetch products once
      const products = await fetchProducts(accountId);
      const normalize = (str) => str?.trim().toLowerCase() || "";
      console.log("fetched products", products?.length);

      const productMap = new Map();
      for (const product of products) {
        productMap.set(normalize(product.name), product);
      }

      let nextToken = true;
      let searchInput = {
        filter: {
          accountId: { eq: accountId },
        },
        limit: 100,
      };

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };

        const result = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );
        const inventories = result.data.searchInventorys.items;
        nextToken = result.data.searchInventorys.nextToken;

        console.log(`Processing ${inventories.length} inventories...`);

        const updateInputs = [];

        for (const inv of inventories) {
          totalInventories++;

          if (inv.accountId !== accountId) continue; // filter account

          // Skip if already linked
          if (inv.productId) continue;

          const match = productMap.get(normalize(inv.productName));
          if (match) {
            inventoriesNeedingUpdate++;

            // IMPORTANT: include all index fields!
            updateInputs.push({
              id: inv.id,
              productId: match.id,
              productCode: match.code,

              // required for composite keys
              reloadYardId: inv.reloadYardId,
              status: inv.status,
              accountId: inv.accountId,
              accountName: inv.accountName,
              productName: inv.productName,
              lotNumber: inv.lotNumber,
              areaId: inv.areaId,
            });
          }
        }

        if (updateInputs.length > 0) {
          const updatePromises = updateInputs.map((input) =>
            limit(() =>
              API.graphql(
                graphqlOperation(mutations.updateInventorie, { input }),
              ),
            ),
          );

          const results = await Promise.allSettled(updatePromises);
          const succeeded = results.filter(
            (r) => r.status === "fulfilled",
          ).length;
          inventoriesUpdated += succeeded;

          console.log(
            `Updated ${succeeded} inventories in this batch (out of ${updateInputs.length} attempted)`,
          );
        }
      }

      const endTime = performance.now();
      const durationSec = ((endTime - startTime) / 1000).toFixed(2);

      console.log("Inventory backfill complete:");
      console.log(`- Total inventories processed: ${totalInventories}`);
      console.log(`- Inventories needing update: ${inventoriesNeedingUpdate}`);
      console.log(`- Inventories updated: ${inventoriesUpdated}`);
      console.log(`- Duration: ${durationSec}s`);
    } catch (err) {
      console.error("Inventory backfill failed:", err);
    }
  };

  const backfillAccountFields = async () => {
    console.clear();
    debugger;

    try {
      let nextToken = true;
      let searchInput = {
        limit: 250,
      };

      let totalAccountsUpdated = 0;
      let failedAccounts = 0;

      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(queries.listAccounts, input),
        );

        const accountList = result.data.listAccounts.items;
        nextToken = result.data.listAccounts.nextToken;

        let promiseList = [];
        let accountIds = [];

        for (const account of accountList) {
          console.log("account", account);

          const updatedAccount = {
            id: account.id,
          };

          // if (account.corporateAccount) {
          //   if (account.skipInvoice == null) updatedAccount.skipInvoice = true;
          //   updatedAccount.accountType = AccountType.INTERNAL;
          // } else {
          //   updatedAccount.accountType = AccountType.CUSTOMER;
          // }
          // updatedAccount.corporateAccount = null;

          console.log("updatedAccount", updatedAccount);

          const promise = API.graphql(
            graphqlOperation(mutations.updateAccount, {
              input: updatedAccount,
            }),
          );
          promiseList.push(promise);
          accountIds.push(account.id);
        }

        const responses = await Promise.allSettled(promiseList);

        // **Use a regular for loop instead of responses.forEach**
        let successfulUpdates = 0;
        let failedUpdates = 0;

        for (let index = 0; index < responses.length; index++) {
          const resp = responses[index];
          if (resp.status === "fulfilled") {
            successfulUpdates++;
          } else {
            console.error(
              `Failed to update account ${accountIds[index]}: `,
              resp.reason,
            );
            failedUpdates++;
          }
        }

        totalAccountsUpdated += successfulUpdates;
        failedAccounts += failedUpdates;
      }

      console.log("Total Accounts Updated:", totalAccountsUpdated);
      console.log("Failed Accounts:", failedAccounts);
    } catch (err) {
      logError("AdminDashboard:backfillAccountFields", err, notify);
    }
    notify("All done");
  };

  const backfillInvoiceEvent = async () => {
    console.clear();
    let nextToken = null;
    const limit = 100;
    const limitConcurrency = pLimit(50);
    let page = 0;

    try {
      do {
        const result = await API.graphql(
          graphqlOperation(adminQueries.listInvoices, { limit, nextToken }),
        );
        const invoices = result?.data?.listInvoices?.items || [];
        nextToken = result?.data?.listInvoices?.nextToken;

        console.log(`Processing page ${++page}: ${invoices.length} invoices`);

        // Process per-invoice to keep phases atomic, then batch update this page
        const promiseList = [];

        for (const invoice of invoices) {
          const {
            id: invoiceId,
            lineItems,
            notes,
            inboundShipmentId,
            outboundShipmentId,
            attachments,
          } = invoice;

          // -------- Phase 1: Non-destructive updates --------
          const lineItemsArr = lineItems?.items || [];
          const attachmentsArr = attachments || [];
          const addInput = { id: invoiceId };
          let shouldAddUpdate = false;

          if (!invoice.event) {
            const isInbound =
              inboundShipmentId ||
              lineItemsArr.some((item) => item.inboundShipmentId);
            const isOutbound =
              outboundShipmentId ||
              lineItemsArr.some((item) => item.outboundShipmentId);

            const hasStorageInNotes = (notes || "")
              .toLowerCase()
              .includes("storage");
            const hasStorageInServiceNote = lineItemsArr.some((item) =>
              (item?.serviceNote || "").toLowerCase().includes("storage"),
            );
            const hasStorageInServiceName = lineItemsArr.some((item) =>
              (item?.service?.name || "").toLowerCase().includes("storage"),
            );
            const hasStorageInAttachments = attachmentsArr.some((att) =>
              (att?.key || "").toLowerCase().includes("storage"),
            );

            const isStorage =
              hasStorageInNotes ||
              hasStorageInServiceNote ||
              hasStorageInServiceName ||
              hasStorageInAttachments;

            if (isInbound) {
              addInput.event = ChargeEvent.INBOUND;
              shouldAddUpdate = true;
            } else if (isOutbound) {
              addInput.event = ChargeEvent.OUTBOUND;
              shouldAddUpdate = true;
            } else if (isStorage) {
              addInput.event = ChargeEvent.STORAGE;
              shouldAddUpdate = true;
            } else {
              shouldAddUpdate = false;
            }
          }

          if (shouldAddUpdate) {
            const promise = limitConcurrency(() =>
              API.graphql(
                graphqlOperation(mutations.updateInvoice, { input: addInput }),
              ),
            ).catch((e) => {
              console.warn(`Invoice event update failed: ${invoiceId}`, e);
              // Continue; nothing destructive yet
            });

            promiseList.push(promise);
          }
        }

        if (promiseList.length > 0) {
          const results = await Promise.allSettled(promiseList);
          const succeeded = results.filter(
            (r) => r.status === "fulfilled",
          ).length;
          const failed = results.length - succeeded;

          console.log(
            `backfillInvoiceEvent: page ${page} updates complete - ${succeeded} succeeded, ${failed} failed`,
          );
        }
      } while (nextToken);

      notify("All done");
      console.log("Backfill complete.");
    } catch (err) {
      console.error("Error during backfillInvoiceEvent", err);
    }
  };

  const deleteDuplicatePhotos = async () => {
    console.clear();
    console.log("Starting cleanPhotos...");
    const startedAt = performance.now();
    const concurrency = 25;
    const limit = pLimit(concurrency);

    const seenMap = new Map(); // signature -> { id, parentId, key, owner, createdAt }
    let page = 0;
    let scanned = 0;
    let duplicatesFound = 0;
    let deleted = 0;
    let deleteFailed = 0;

    try {
      let nextToken;
      do {
        page++;
        const pageStart = performance.now();
        const input = nextToken ? { limit: 200, nextToken } : { limit: 200 };

        const resp = await API.graphql(
          graphqlOperation(queries.listPhotos, input),
        );

        const items = resp?.data?.listPhotos?.items || [];
        nextToken = resp?.data?.listPhotos?.nextToken;
        scanned += items.length;

        const deleteTasks = [];
        let pageDuplicates = 0;

        for (const photo of items) {
          if (!photo?.id) continue;

          const signature = `${photo.parentId || ""}||${photo.key || ""}||${
            photo.owner || ""
          }`;

          if (!seenMap.has(signature)) {
            seenMap.set(signature, {
              id: photo.id,
              parentId: photo.parentId,
              key: photo.key,
              owner: photo.owner,
              createdAt: photo.createdAt,
            });
            continue;
          }

          // Duplicate found: delete THIS record, keep the first-seen record.
          duplicatesFound++;
          pageDuplicates++;

          deleteTasks.push(
            limit(() =>
              API.graphql(
                graphqlOperation(mutations.deletePhoto, {
                  input: { id: photo.id },
                }),
              ),
            ),
          );
        }

        let pageDeleted = 0;
        let pageFailed = 0;
        const results = await Promise.allSettled(deleteTasks);
        for (const r of results) {
          if (r.status === "fulfilled") pageDeleted++;
          else pageFailed++;
        }

        deleted += pageDeleted;
        deleteFailed += pageFailed;

        console.log(
          `[cleanPhotos] page ${page} photos=${
            items.length
          } duplicates=${pageDuplicates} deleted=${pageDeleted} failed=${pageFailed} nextToken=${
            nextToken ? "yes" : "no"
          } (${Math.round(performance.now() - pageStart)}ms)`,
        );
      } while (nextToken);

      console.log("[cleanPhotos] done", {
        pages: page,
        scanned,
        unique: seenMap.size,
        duplicatesFound,
        deleted,
        deleteFailed,
        durationMs: Math.round(performance.now() - startedAt),
      });

      notify("All done");
    } catch (err) {
      logError("AdminDashboard:cleanPhotos", err, notify);
    }
  };

  const resetStoredLastShipDates = async () => {
    console.clear();
    console.log("Starting resetStoredLastShipDates...");
    const startedAt = performance.now();

    try {
      const limit = 250;
      let nextToken = null;
      let scanned = 0;
      let updated = 0;
      let inventoryItemsUpdated = 0;

      const searchInput = {
        filter: {
          status: { eq: InventoryStatus.STORED },
          depletedAt: { gte: "2020-11-05T12:52:28.177Z" },
          quantityOnHand: { gt: 0 },
        },
        limit,
      };

      do {
        const input = nextToken ? { ...searchInput, nextToken } : searchInput;

        const result = await API.graphql(
          graphqlOperation(adminQueries.searchInventorysBackfill, input),
        );

        const page = result?.data?.searchInventorys;
        const items = page?.items || [];
        nextToken = page?.nextToken;
        scanned += items.length;
        console.log("items", items.length);

        for (const inv of items) {
          console.log(
            `[${updated + 1}] Resetting inv ${inv.id} | depletedAt: ${inv.depletedAt} | turnDays: ${inv.turnDays}`,
          );

          await utils.saveInventory({
            id: inv.id,
            depletedAt: null,
            turnDays: null,
          });
          updated += 1;

          const inventoryItems = inv?.inventoryItems?.items || [];
          for (const ii of inventoryItems) {
            if (ii?.depletedAt) {
              console.log(
                `  InventoryItem ${ii.id} | depletedAt: ${ii.depletedAt}`,
              );
              await utils.saveInventoryItem({
                id: ii.id,
                depletedAt: null,
              });
              inventoryItemsUpdated += 1;
            }
          }
        }

        console.log(`Scanned ${scanned} inventories so far...`);
      } while (nextToken);

      const durationMs = Math.round(performance.now() - startedAt);
      console.log("resetStoredLastShipDates complete", {
        scanned,
        updated,
        inventoryItemsUpdated,
        durationMs,
      });
      notify(
        `Reset ${updated} inventories, ${inventoryItemsUpdated} items in ${durationMs}ms`,
      );
    } catch (err) {
      logError("AdminDashboard:resetStoredLastShipDates", err, notify);
    }
  };

  return (
    <>
      <Card>
        <CardContent
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography
            variant="h3"
            color={getEnv() === "prod" ? "error" : "default"}
          >
            {getEnv()} -- {getVersion()}
          </Typography>
        </CardContent>
      </Card>
      {!appUser.email.includes("mikek") && (
        <Card className={classes.card}>
          <CardHeader title="PROD FUNCTIONS" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="default">
                      AUDIT DATA
                    </Typography>
                  </legend>
                  <Button
                    onClick={() =>
                      handleDialogOpen(utils.auditLocations, "auditLocations")
                    }
                  >
                    Audit Location
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(
                        utils.auditCapacityUsed,
                        "auditCapacityUsed",
                      )
                    }
                  >
                    Audit Capacity Used
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(
                        utils.auditInventoryQuantity,
                        "auditInventoryQuantity",
                      )
                    }
                  >
                    Audit Inventory Qty
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(utils.auditInbounds, "auditInbounds")
                    }
                  >
                    Audit Inbound
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(utils.auditOutbounds, "auditOutbounds")
                    }
                  >
                    Audit Outbound
                  </Button>
                  <Button
                    disabled={true}
                    onClick={() =>
                      handleDialogOpen(
                        utils.auditInvItemLocations,
                        "auditInvItemLocations",
                      )
                    }
                  >
                    Audit InvItem Location
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(auditProducts, "auditProducts")
                    }
                  >
                    Audit Product Qty
                  </Button>
                </fieldset>
              </Grid>

              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="default">
                      DATA INTEGRITY AUDITS
                    </Typography>
                  </legend>
                  <Button onClick={auditInventoriesMissingInbound}>
                    inventories missing inbound
                  </Button>
                  <Button onClick={auditInventoryItemsMissingInventory}>
                    inventoryItems missing inventory
                  </Button>
                  <Button onClick={auditShipmentLineItemsMissingInventory}>
                    shipmentLineItems missing inventory
                  </Button>
                  <Button onClick={auditShipmentLineItemsMissingOutbound}>
                    shipmentLineItems missing outbound
                  </Button>
                  <Button
                    onClick={auditShipmentLineItemInventoryItemsMissingRefs}
                  >
                    lineItemInventoryItems missing refs
                  </Button>
                  <Button onClick={auditChargeRulesMissingRefs}>
                    chargeRules missing account/service
                  </Button>
                  <Button onClick={auditInvoicesMissingAccount}>
                    invoices missing account
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(
                        auditInventoryQuantityDiscrepancies,
                        "auditInventoryQuantityDiscrepancies",
                      )
                    }
                  >
                    inventory qty discrepancies
                  </Button>
                  <Button onClick={fixShippedInventoryDepletedAt}>
                    fix shipped inv depletedAt
                  </Button>
                  <Button onClick={auditInvoiceServicesMissingInvoice}>
                    invoiceServices missing invoice
                  </Button>
                  <Button onClick={auditShipmentsWithInvoiceIdNoServices}>
                    shipments w/ invoiceId no services
                  </Button>
                  <Button onClick={unlinkInboundsWithInvoiceIdNoServices}>
                    unlink inbounds w/ invoiceId no services
                  </Button>
                  <Button onClick={unlinkOutboundsWithInvoiceIdNoServices}>
                    unlink outbounds w/ invoiceId no services
                  </Button>
                  <Button onClick={auditServicesMissingAccount}>
                    services missing account
                  </Button>
                </fieldset>
              </Grid>

              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="default">
                      CLEAN DATA
                    </Typography>
                  </legend>
                  <Button
                    onClick={() =>
                      handleDialogOpen(
                        utils.runAccountChange,
                        "runAccountChange",
                      )
                    }
                  >
                    Account Change
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(utils.fixCapacityUsed, "fixCapacityUsed")
                    }
                  >
                    fix capacity used
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(utils.fixInventoryQty, "fixInventoryQty")
                    }
                  >
                    fix Inventory Qty
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(
                        updateGroupNameHandler,
                        "updateGroupName",
                      )
                    }
                  >
                    Update Group Name
                  </Button>
                  <Button
                    onClick={() =>
                      handleDialogOpen(
                        deleteBulkErrorLogs,
                        "deleteBulkErrorLogs",
                      )
                    }
                  >
                    delete bulk error Logs
                  </Button>
                </fieldset>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {getEnv() !== "prod" && (
        <Card className={classes.card}>
          <CardHeader title="DEV FUNCTIONS" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  disabled={getEnv() !== "dev"}
                  onClick={() =>
                    handleDialogOpen(utils.deleteReloadYard, "deleteReloadYard")
                  }
                >
                  Delete ReloadYard
                </Button>
                <>&nbsp;&nbsp;</>
                <Button
                  variant="contained"
                  disabled={getEnv() !== "dev" && getEnv() !== "hotfix"}
                  onClick={() =>
                    handleDialogOpen(utils.resetTestData, "resetTestData")
                  }
                >
                  Reset ALL Test Data
                </Button>
                <>&nbsp;&nbsp;</>
                <Button
                  variant="contained"
                  disabled={getEnv() !== "dev"}
                  onClick={() =>
                    handleDialogOpen(
                      utils.deleteAccountById,
                      "deleteAccountById",
                    )
                  }
                >
                  Delete Account
                </Button>
                <>&nbsp;&nbsp;</>
                <Button
                  variant="contained"
                  disabled={getEnv() !== "dev"}
                  onClick={() =>
                    handleDialogOpen(
                      runScalabilityLoadTest,
                      "runScalabilityLoadTest",
                    )
                  }
                >
                  Execute Scalability Load Test
                </Button>
                <>&nbsp;&nbsp;</>
              </Grid>
              <Grid item xs={12}>
                <Button disabled={false} onClick={devInventoryItemCleanout}>
                  Delete InventoryItems with missing data
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(resetLineItems, "resetLineItems")
                  }
                >
                  Reset LineItems
                </Button>
                <Button
                  disabled={false}
                  onClick={() => handleDialogOpen(fixAisleList, "fixAisleList")}
                >
                  Fix Aisle List
                </Button>
                <br />
                <Button disabled={true} onClick={inventoryItemAddAccount}>
                  inventoryItem Add Account, Unloaded By
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(mergeInventories, "mergeInventories")
                  }
                >
                  merge Inventory
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(
                      mergeShipmentLineItems,
                      "mergeShipmentLineItems",
                    )
                  }
                >
                  merge ShipmentLineItems
                </Button>
                <br />
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(resetAisleSortKey, "resetAisleSortKey")
                  }
                >
                  RESET Aisles sort_key
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(
                      invoices_Backfill_Numbers,
                      "invoices_Backfill_Numbers",
                    )
                  }
                >
                  invoices Backfill Numbers
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(runSortKeyBackfill, "runSortKeyBackfill")
                  }
                >
                  Run SorkKey Backfill
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(
                      createInventoryItems,
                      "createInventoryItems",
                    )
                  }
                >
                  create Inventory Items
                </Button>
                <br />
                <Button
                  disabled={getEnv() === "prod"}
                  onClick={devFixLocations}
                >
                  Fix Locations
                </Button>
                <Button
                  disabled={getEnv() === "prod"}
                  onClick={() =>
                    handleDialogOpen(
                      devDeleteEmptyInbounds,
                      "devDeleteEmptyInbounds",
                    )
                  }
                >
                  Delete Empty Inbounds
                </Button>
                <Button
                  disabled={getEnv() === "prod"}
                  onClick={() =>
                    handleDialogOpen(
                      devDeleteEmptyOutbounds,
                      "devDeleteEmptyOutbounds",
                    )
                  }
                >
                  Delete Empty Outbounds
                </Button>
                <Button
                  disabled={getEnv() === "prod"}
                  onClick={() => deleteLogs()}
                >
                  Delete all Logs
                </Button>
                <Button
                  disabled={getEnv() === "prod"}
                  onClick={() =>
                    handleDialogOpen(
                      resetGateCheckInData,
                      "resetGateCheckInData",
                    )
                  }
                >
                  Reset Gate Check-In Data
                </Button>
                <Button
                  disabled={getEnv() !== "dev"}
                  onClick={() =>
                    handleDialogOpen(utils.resetCarrierData, "resetCarrierData")
                  }
                >
                  Reset Carrier App Data
                </Button>
                <Button
                  onClick={() =>
                    handleDialogOpen(backfillGateQueue, "backfillGateQueue")
                  }
                >
                  Backfill gate queue
                </Button>
                <Button
                  onClick={() => handleDialogOpen(backfillDba, "backfillDba")}
                >
                  Backfill DBA
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      <Card className={classes.card}>
        <CardHeader title="RELEASE FUNCTIONS" />
        <CardContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="default">
                    PERMANENT
                  </Typography>
                </legend>
                <Button
                  variant="contained"
                  onClick={() =>
                    handleDialogOpen(utils.runDBIndexRegen, "runDBIndexRegen")
                  }
                >
                  DB Index Regen
                </Button>
                <>&nbsp;&nbsp;</>
                <Button
                  variant="contained"
                  disabled={!helpPageButtonText}
                  onClick={impoertHelpPages}
                >
                  {helpPageButtonText
                    ? helpPageButtonText
                    : "Migrate Help Pages"}
                </Button>
                <>&nbsp;&nbsp;</>
                <Button
                  variant="contained"
                  disabled={!commodityButtonText}
                  onClick={importCommodities}
                >
                  {commodityButtonText
                    ? commodityButtonText
                    : "Migrate Commodities"}
                </Button>
              </fieldset>
            </Grid>
            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="default">
                    CURRENT SPRINT{" "}
                  </Typography>
                </legend>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(
                      backfillOutboundShipment,
                      "backfillOutboundShipment",
                    )
                  }
                >
                  Backfil Outbound Shipments
                </Button>
                <Button onClick={() => backfillTrackCarAisleZoneArea(notify)}>
                  Backfill TrackCar aisleId/zoneId/areaId
                </Button>
                {/* Touches every carrier entry that has no company yet and lets
                    handleCarrierChange place it. Safe to run twice, and it
                    reports what is still unplaced rather than assuming the
                    stream has caught up. */}
                <Button onClick={() => backfillCarrierCompanies(notify)}>
                  Backfill Carrier Companies
                </Button>
                <Button
                  onClick={() => migrateUnspottedTrackCarsActiveState(notify)}
                >
                  Migrate TrackCars (Unspotted + Collected cleanup)
                </Button>
                <Button
                  disabled={getEnv() === "prod"}
                  onClick={() => backfillLocationLength({}, notify)}
                >
                  Backfill Location Length (TRACK)
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(addDefaultScheduleToYard, "Add Schedule")
                  }
                >
                  Add Schedule
                </Button>
                <Button disabled={true} onClick={hdmFixInventoryItems}>
                  HDM fix Inventory Items
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(
                      fixInventoryDateTime,
                      "fixInventoryDateTime",
                    )
                  }
                >
                  fix Inventory DateTime
                </Button>
                <Button disabled={true} onClick={fixNegativeInvQty}>
                  fix Negative Inventory qty
                </Button>
                <Button disabled={true} onClick={addUserACL}>
                  Add OUTOUBND_SIGN ACL
                </Button>
                <Button disabled={false} onClick={addTrackManagementACL}>
                  Add TRACK_MANAGEMENT ACL
                </Button>
                <Button disabled={false} onClick={addGateManagementACL}>
                  Add GATE_MANAGEMENT ACL
                </Button>
                <Button disabled={true} onClick={addBillingBusinessName}>
                  addBillingBusinessName
                </Button>
                <Button disabled={true} onClick={fixProductCode}>
                  fix product code
                </Button>
                <Button disabled={true} onClick={backfillTimeslotIds}>
                  backfill TimeslotIds
                </Button>
                <Button disabled={false} onClick={fixProductCounts}>
                  fix Product Counts
                </Button>
                <Button disabled={true} onClick={fixProductStatus}>
                  fix Product Status
                </Button>
                <Button disabled={true} onClick={addPcsQuantity}>
                  add Pcs Quantity
                </Button>
                <Button
                  onClick={runBackFillUnFilledContainerDropValuesInbounds}
                  disabled={true}
                >
                  Back-fill Inbound containerDrop
                </Button>
                <Button
                  onClick={runBackFillUnFilledContainerPickupValuesOutbounds}
                  disabled={true}
                >
                  Back-fill Outbound containerPickup
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(
                      run_Aisle_Location_Backfill,
                      "run_Aisle_Location_Backfill",
                    )
                  }
                >
                  Location_Load Backfill
                </Button>
                <Button disabled={true} onClick={backfillLoadTypes}>
                  Back-fill Load Types
                </Button>
                <Button disabled={true} onClick={runReloadYardStatusBackfill}>
                  Back-fill ReloadYrd Status
                </Button>
                <Button disabled={true} onClick={runProductStatusBackfill}>
                  Back-fill Product Status
                </Button>
                <Button disabled={true} onClick={runAddressBookBackfill}>
                  Back-fill Address Book
                </Button>
                <Button disabled={true} onClick={runAddressBookDelete}>
                  Delete Address Book
                </Button>
                <Button disabled={true} onClick={backfillExtIdPaymentTerm}>
                  backfill Account Ext ID Pay Term
                </Button>
                <Button disabled={true} onClick={runInboundTempPhotosBackfill}>
                  Back-fill Inbound Photos
                </Button>
                <Button disabled={true} onClick={resetParentType}>
                  Reset Photos Inbound
                </Button>
                <Button disabled={true} onClick={runOutboundTempPhotosBackfill}>
                  Back-fill Outbound Photos
                </Button>
                <Button
                  disabled={true}
                  onClick={runInventoryTempPhotosBackfill}
                >
                  Back-fill Inventory Photos
                </Button>
                <Button disabled={true} onClick={backfillYardPaymentTerms}>
                  Back-fill ReloadYrd Terms
                </Button>
                <Button disabled={true} onClick={backfillInvoiceServiceDate}>
                  Back-fill InvoiceService Date
                </Button>
                <Button disabled={true} onClick={backfillAllSkipInvoice}>
                  Back-fill skip Invoice
                </Button>
                <Button disabled={true} onClick={validateAndFixServiceHistory}>
                  Validate & Fix Service History
                </Button>
                <Button disabled={true} onClick={validateAndFixProductHistory}>
                  Validate & Fix Product Histor
                </Button>
                <Button disabled={true} onClick={validateAndFixInvoiceHistory}>
                  Validate & Fix Invoice History
                </Button>
                <Button disabled={true} onClick={backfilPanelDims}>
                  Back-fill Panel Dims
                </Button>
                <Button disabled={false} onClick={backfillAC}>
                  Back-fill Accessorial Charges
                </Button>
                <Button disabled={true} onClick={backfillInvoiceEvent}>
                  Back-fill Invoice Event
                </Button>
                <Button disabled={true} onClick={backfillFreeDaysPolicy}>
                  Back-fill FreeDays Policy
                </Button>
                <Button disabled={true} onClick={backfillServiceUOM}>
                  Back-fill Service UOM Defaults
                </Button>
                <Button disabled={false} onClick={() => backfillProductStats()}>
                  Backfill Product Stats
                </Button>
                <Button disabled={true} onClick={backfillInvoiceEvent}>
                  Back-fill Invoice Event
                </Button>
                <Button disabled={true} onClick={backfillServiceTaxRate}>
                  Back-fill Service taxRate
                </Button>
                <Button
                  disabled={true}
                  onClick={() =>
                    handleDialogOpen(fixProductLink, "fixProductLink")
                  }
                >
                  fix Product Links
                </Button>
                <Button disabled={true} onClick={backfillInvoiceEvents}>
                  Backfill Invoice Events
                </Button>
                <Button disabled={true} onClick={resetStoredLastShipDates}>
                  Reset STORED lastShipDates
                </Button>
                <Button disabled={false} onClick={deleteDuplicatePhotos}>
                  Delete Duplicate Photos
                </Button>
                <Button disabled={true} onClick={backfillSfToMsf}>
                  Back-fill SF to MSF
                </Button>
                <Button disabled={true} onClick={backfillStlLfToFt}>
                  Set STL LF to FT
                </Button>
                <Button disabled={false} onClick={backfillProductFields}>
                  Backfil Product Fields
                </Button>
                <Button disabled={false} onClick={backfillAccountFields}>
                  Backfill Account Fields
                </Button>
                <Button disabled={false} onClick={backfillInventoryFields}>
                  Backfill Inventory Fields
                </Button>
                <Button disabled={false} onClick={backfillInventoryItemFields}>
                  Backfill Inventory Item Fields
                </Button>
                <Button disabled={false} onClick={backfillInboundFields}>
                  Backfill Inbound Fields
                </Button>
                <Button disabled={false} onClick={backfillOutboundFields}>
                  Backfill Outbound Fields
                </Button>
                <Button
                  onClick={() =>
                    handleDialogOpen(
                      backfillOutboundPlacementDateTime,
                      "backfillOutboundPlacementDateTime",
                    )
                  }
                >
                  Backfill Outbound placementDateTime
                </Button>
                <Button
                  disabled={orphanAuditRunning}
                  onClick={runOrphanInventoryItemAudit}
                >
                  {orphanAuditRunning
                    ? "auditing orphan inventoryItems..."
                    : "orphan inventoryItems (all yards)"}
                </Button>
                <Button
                  disabled={orphansToDelete === 0 || orphanAuditRunning}
                  onClick={() =>
                    handleDialogOpen(
                      runOrphanInventoryItemCleanup,
                      "cleanupOrphanInventoryItems",
                    )
                  }
                >
                  {`Clean up orphan inventoryItems (${orphansToDelete})`}
                </Button>
              </fieldset>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {orphanAudit?.summary?.length > 0 && (
        <CardContent>
          <Typography variant="h6">
            Orphan inventoryItems: {orphanAudit.orphans.length} in{" "}
            {orphanAudit.summary.length} inbounds ({orphansToDelete} to clean
            up, {orphanAudit.orphans.length - orphansToDelete} to review) —
            audited {orphanAudit.finishedAt}
          </Typography>
          <ArrayField source="summary" record={orphanAudit}>
            <Datagrid>
              <TextField source="yard" />
              <TextField source="account" />
              <TextField source="inboundShipmentId" />
              <TextField source="items" />
              <TextField source="toDelete" />
              <TextField source="toReview" />
              <TextField source="capacityTracked" />
              <TextField source="statuses" />
              <TextField source="onHand" />
              <TextField source="firstCreated" />
              <TextField source="lastUpdated" />
            </Datagrid>
          </ArrayField>
        </CardContent>
      )}
      {
        // inventory audit output
        results?.items?.length > 0 && (
          <CardContent>
            <ArrayField source="items" record={results}>
              <Datagrid>
                {Object.keys(results.items[0]).map((column) => (
                  <TextField source={`${column}`} />
                ))}
                <MismatchIcon />
              </Datagrid>
            </ArrayField>
          </CardContent>
        )
      }
      <utils.HandlerDialog
        dialogOpen={dialogOpen}
        handleDialogClose={handleDialogClose}
        onSubmit={handleSubmit}
        handlerName={handlerName}
        loading={loading}
        reloadYards={reloadYards}
        queryTerms={queryTerms}
        setQueryTerms={setQueryTerms}
        orphanAudit={orphanAudit}
      />
    </>
  );
};
